/* ═══════════════════════════════════════════════════════════════════════════
   INCIDENT MANAGEMENT SYSTEM - Frontend Application (TypeScript)
   Auth + SPA routing
   ═══════════════════════════════════════════════════════════════════════════ */

import type {
  User,
  Incident,
  Company,
  IncidentStats,
  IncidentComment,
  IncidentStatus,
  Portal,
  Criticality,
  UserRole,
  ToastType,
  StatusGroup,
  PortalConfig,
  CriticalityConfig,
  StatusConfig,
  IncidentLimits,
  ConfirmModalState,
  ModalOpenOptions,
  UserFormPayload,
  RoleGroup,
  DeveloperPerformance,
  ResolutionType,
} from './types';

(function (): void {
  'use strict';

  // ─── Constants ──────────────────────────────────────────────────────────
  const PORTALS: Record<string, PortalConfig> = {
    paciente: { label: 'Portal Paciente' },
    clinica: { label: 'Portal Clinica' },
    profissional: { label: 'Portal Profissional' },
    superadmin: { label: 'Super Admin' }
  };

  const CRITICALITIES: Record<string, CriticalityConfig> = {
    baixa: { label: 'Baixa', tag: 'P4' },
    media: { label: 'Media', tag: 'P3' },
    alta: { label: 'Alta', tag: 'P2' },
    critica: { label: 'Critica', tag: 'P1' }
  };

  const STATUSES: Record<string, StatusConfig> = {
    aberto: { label: 'Aberto' },
    em_analise: { label: 'Em Análise' },
    em_andamento: { label: 'Em Andamento' },
    aguardando_validacao: { label: 'Enviado para análise' },
    resolvido: { label: 'Resolvido' },
    fechado: { label: 'Fechado' }
  };

  const INCIDENT_LIMITS: IncidentLimits = {
    title: 150,
    description: 5000
  };

  const SUCCESS_REDIRECT_DELAY_SECONDS = 10;
  const MODAL_SKELETON_DELAY_MS = 140;

  // ─── State Variables ────────────────────────────────────────────────────
  let currentView = 'new';
  let selectedFiles: File[] = [];
  let currentPage = 1;
  let pollingInterval: ReturnType<typeof setInterval> | null = null;
  let currentUser: User | null = null;
  let users: User[] = [];
  let editingUserId: number | null = null;
  let managePollingInterval: ReturnType<typeof setInterval> | null = null;
  let dashboardPollingInterval: ReturnType<typeof setInterval> | null = null;
  let successRedirectTimeout: ReturnType<typeof setTimeout> | null = null;
  let successRedirectInterval: ReturnType<typeof setInterval> | null = null;
  let incidentStatusGroup: StatusGroup = 'active';
  let confirmModalState: ConfirmModalState | null = null;
  let incidentModalRequestId = 0;
  let manageModalRequestId = 0;
  const modalFocusState = new Map<string, HTMLElement | null>();
  let cachedProducts: Array<{ id: number; name: string; code: string; color: string | null }> = [];
  let cachedResolutionTypes: Array<{ id: number; code: string; label: string }> = [];
  let cachedDevelopers: Array<{ id: number; name: string; email: string }> = [];

  // Desempacota envelope `{success, incident}` ou retorna o objeto direto se já for o incidente.
  function unwrapIncident(data: { incident?: Incident } | Incident | null | undefined): Incident | null {
    if (!data) return null;
    const wrapped = (data as { incident?: Incident }).incident;
    return wrapped ?? (data as Incident);
  }

  async function loadResolutionTypes(): Promise<void> {
    try {
      const res = await authenticatedFetch('/api/resolution-types');
      if (!res || !res.ok) return;
      const data = await res.json() as { success?: boolean; types?: Array<{ id: number; code: string; label: string }> };
      cachedResolutionTypes = data.types || [];
    } catch { /* ignore */ }
  }

  async function loadDevelopers(): Promise<void> {
    try {
      const res = await authenticatedFetch('/api/dev/users');
      if (!res || !res.ok) return;
      const data = await res.json() as { developers?: Array<{ id: number; name?: string; first_name?: string; last_name?: string; email: string }> };
      cachedDevelopers = (data.developers || []).map(d => ({
        id: d.id,
        name: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.email,
        email: d.email,
      }));
    } catch { /* ignore */ }
  }

  async function loadResolutionTypesView(): Promise<void> {
    const tbody = document.getElementById('resolution-types-table-body');
    if (!tbody) return;
    const res = await authenticatedFetch('/api/admin/resolution-types');
    if (!res || !res.ok) {
      tbody.innerHTML = '<tr><td colspan="6" class="empty-row">Erro ao carregar</td></tr>';
      return;
    }
    const data = await res.json() as { types?: Array<{ id: number; code: string; label: string; sort_order: number; is_active: boolean | number }> };
    const types = data.types || [];
    if (types.length === 0) {
      tbody.innerHTML = '<tr><td colspan="6" class="empty-row">Nenhum tipo cadastrado</td></tr>';
    } else {
      tbody.innerHTML = types.map(t => {
        const active = t.is_active === true || t.is_active === 1;
        return `
          <tr draggable="true" data-rt-row-id="${t.id}">
            <td style="width:32px;text-align:center;cursor:grab;color:var(--text-muted)" class="rt-drag-handle" title="Arrastar para reordenar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><circle cx="9" cy="6" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="18" r="1"/></svg>
            </td>
            <td><strong>${escapeHtml(t.label)}</strong></td>
            <td><code style="font-size:0.75rem;color:var(--text-muted)">${escapeHtml(t.code)}</code></td>
            <td><span class="company-status-badge ${active ? 'is-active' : 'is-inactive'}"><span class="company-status-dot"></span>${active ? 'Ativo' : 'Inativo'}</span></td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-ghost" data-rt-action="edit" data-rt-id="${t.id}" title="Editar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                <button class="btn btn-sm btn-ghost" data-rt-action="toggle" data-rt-id="${t.id}" data-rt-activate="${!active}" title="${active ? 'Desativar' : 'Ativar'}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none">${active ? '<path d="M18 6L6 18M6 6l12 12"/>' : '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'}</svg></button>
                <button class="btn btn-sm btn-ghost" data-rt-action="delete" data-rt-id="${t.id}" title="Excluir"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg></button>
              </div>
            </td>
          </tr>
        `;
      }).join('');
      tbody.querySelectorAll<HTMLElement>('[data-rt-action]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = Number(btn.dataset.rtId);
          const act = btn.dataset.rtAction;
          if (act === 'edit') void promptResolutionTypeEdit(types.find(t => t.id === id));
          else if (act === 'toggle') void toggleResolutionType(id, btn.dataset.rtActivate === 'true');
          else if (act === 'delete') void deleteResolutionTypeUI(id);
        });
      });
      enableResolutionTypesDragSort(tbody);
    }

    const btnNew = document.getElementById('btn-new-resolution-type');
    if (btnNew && !btnNew.dataset.bound) {
      btnNew.dataset.bound = 'true';
      btnNew.addEventListener('click', () => void promptResolutionTypeEdit(null));
    }
    refreshIcons();
  }

  function enableResolutionTypesDragSort(tbody: HTMLElement): void {
    let dragged: HTMLTableRowElement | null = null;
    tbody.querySelectorAll<HTMLTableRowElement>('tr[draggable="true"]').forEach(row => {
      row.addEventListener('dragstart', (e) => {
        dragged = row;
        row.classList.add('rt-dragging');
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      });
      row.addEventListener('dragend', () => {
        row.classList.remove('rt-dragging');
        dragged = null;
        void persistResolutionTypesOrder(tbody);
      });
      row.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (!dragged || dragged === row) return;
        const rect = row.getBoundingClientRect();
        const after = (e.clientY - rect.top) > rect.height / 2;
        row.parentElement?.insertBefore(dragged, after ? row.nextSibling : row);
      });
    });
  }

  async function persistResolutionTypesOrder(tbody: HTMLElement): Promise<void> {
    const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr[draggable="true"]'));
    const updates = rows.map((row, idx) => ({ id: Number(row.dataset.rtRowId), sort_order: idx + 1 }));
    await Promise.all(updates.map(u => authenticatedFetch(`/api/admin/resolution-types/${u.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sort_order: u.sort_order }),
    })));
    cachedResolutionTypes = [];
    showToast('Ordem atualizada', 'success');
  }

  async function promptResolutionTypeEdit(type: { id: number; code: string; label: string; sort_order: number } | null | undefined): Promise<void> {
    const isEdit = !!type;
    const labelInitial = type?.label || '';
    const codeInitial = type?.code || '';
    const orderInitial = type?.sort_order ?? 0;
    const result = await new Promise<{ label: string; code: string; sort_order: number } | null>(resolve => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay show';
      overlay.innerHTML = `
        <div class="modal-content" style="max-width:440px">
          <div class="modal-header">
            <h2 class="modal-title">${isEdit ? 'Editar' : 'Novo'} Tipo de Resolução</h2>
            <button type="button" class="modal-close" data-close-rt><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="modal-body">
            <label style="display:block;font-size:0.75rem;font-weight:600;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px">Rótulo *</label>
            <input class="rt-label" type="text" value="${escapeHtml(labelInitial)}" placeholder="Ex: Bug do Sistema" style="width:100%;padding:9px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-size:0.875rem;margin-bottom:12px" />
            ${!isEdit ? `<label style="display:block;font-size:0.75rem;font-weight:600;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px">Código *</label>
            <input class="rt-code" type="text" value="${escapeHtml(codeInitial)}" placeholder="Ex: bug_sistema (apenas letras/_)" style="width:100%;padding:9px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-size:0.875rem;margin-bottom:12px" />` : ''}
            <label style="display:block;font-size:0.75rem;font-weight:600;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px">Ordem (menor aparece primeiro)</label>
            <input class="rt-order" type="number" value="${orderInitial}" style="width:100%;padding:9px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-size:0.875rem" />
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px">
              <button type="button" class="btn btn-ghost" data-close-rt>Cancelar</button>
              <button type="button" class="btn btn-primary" data-save-rt>${isEdit ? 'Salvar' : 'Criar'}</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      const cleanup = (v: { label: string; code: string; sort_order: number } | null): void => { overlay.remove(); resolve(v); };
      overlay.querySelectorAll<HTMLElement>('[data-close-rt]').forEach(el => el.addEventListener('click', () => cleanup(null)));
      overlay.querySelector<HTMLButtonElement>('[data-save-rt]')?.addEventListener('click', () => {
        const labelV = (overlay.querySelector<HTMLInputElement>('.rt-label')?.value || '').trim();
        const codeV = (overlay.querySelector<HTMLInputElement>('.rt-code')?.value || codeInitial).trim();
        const orderV = parseInt(overlay.querySelector<HTMLInputElement>('.rt-order')?.value || '0', 10) || 0;
        if (!labelV) { showToast('Informe o rótulo', 'error'); return; }
        if (!isEdit && !codeV) { showToast('Informe o código', 'error'); return; }
        cleanup({ label: labelV, code: codeV, sort_order: orderV });
      });
    });
    if (!result) return;
    const url = isEdit ? `/api/admin/resolution-types/${type!.id}` : '/api/admin/resolution-types';
    const method = isEdit ? 'PUT' : 'POST';
    const body = isEdit ? { label: result.label, sort_order: result.sort_order } : result;
    const res = await authenticatedFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res || !res.ok) {
      const err = res ? await res.json().catch(() => ({})) as { error?: string } : {};
      showToast(err.error || 'Erro ao salvar', 'error');
      return;
    }
    showToast(isEdit ? 'Tipo atualizado' : 'Tipo criado', 'success');
    cachedResolutionTypes = [];
    void loadResolutionTypesView();
  }

  async function toggleResolutionType(id: number, activate: boolean): Promise<void> {
    const res = await authenticatedFetch(`/api/admin/resolution-types/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: activate }),
    });
    if (!res || !res.ok) { showToast('Erro ao alterar status', 'error'); return; }
    showToast(activate ? 'Tipo ativado' : 'Tipo desativado', 'success');
    cachedResolutionTypes = [];
    void loadResolutionTypesView();
  }

  async function deleteResolutionTypeUI(id: number): Promise<void> {
    if (!window.confirm('Excluir este tipo? Só é possível se nenhum incidente o estiver usando.')) return;
    const res = await authenticatedFetch(`/api/admin/resolution-types/${id}`, { method: 'DELETE' });
    if (!res || !res.ok) {
      const err = res ? await res.json().catch(() => ({})) as { error?: string } : {};
      showToast(err.error || 'Erro ao excluir', 'error');
      return;
    }
    showToast('Tipo excluído', 'success');
    cachedResolutionTypes = [];
    void loadResolutionTypesView();
  }

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  // ─── API References ─────────────────────────────────────────────────────
  const roleApi = (window as Window).EveryIncRoles || {};
  const textApi = (window as Window).EveryIncText || {};
  const userHelpersApi = (window as Window).EveryIncUserHelpers || {};
  const userModalHelpersApi = (window as Window).EveryIncUserModalHelpers || {};
  const userManagementUiApi = (window as Window).EveryIncUserManagementUi || {};

  const getRoleLabelText = roleApi.getRoleLabel || ((role: string): string => role || 'Usuario');
  const canAccessManagementView = roleApi.canAccessManagement || ((): boolean => false);
  const canAccessUserManagementView = roleApi.canAccessUserManagement || ((): boolean => false);
  const canAccessTrelloView = roleApi.canAccessTrello || ((): boolean => false);
  const canAccessDashboardView = roleApi.canAccessDashboard || ((): boolean => false);
  const canAccessGovernanceView = roleApi.canAccessGovernance || ((): boolean => false);
  const requiresRoleToken = roleApi.requiresAuthToken || ((): boolean => false);
  const getUserFullName = userHelpersApi.getUserFullName || ((user: User | null): string => String(user?.name || '').trim());
  const buildUserInitials = userHelpersApi.buildUserInitials || ((name: string): string => String(name || '--').slice(0, 2).toUpperCase());
  const normalizeComparableText = userHelpersApi.normalizeComparableText || ((value: string): string => String(value || '').trim().toLowerCase());
  const formatDurationCompact = userHelpersApi.formatDurationCompact || ((): string => 'Sem base ainda');
  const buildUserRoleGroups = userHelpersApi.buildUserRoleGroups || ((u: User[]): RoleGroup[] => u as unknown as RoleGroup[]);
  const buildRoleCompositionStyle = userHelpersApi.buildRoleCompositionStyle || ((): string => 'background: conic-gradient(var(--chart-track) 0deg 360deg);');
  const buildDeveloperPerformance = userHelpersApi.buildDeveloperPerformance || ((): DeveloperPerformance[] => []);
  const applyUserModalState = userModalHelpersApi.applyUserModalState || ((): void => {});
  const resetUserModalState = userModalHelpersApi.resetUserModalState || ((): void => {});
  const syncOwnerOnlyOptions = userModalHelpersApi.syncOwnerOnlyOptions || ((): void => {});
  const populateUserForm = userModalHelpersApi.populateUserForm || ((): void => {});
  const buildUserFormPayload = userModalHelpersApi.buildUserFormPayload || ((doc: Document, options: { includePassword: boolean }): UserFormPayload => {
    const payload: UserFormPayload = {
      first_name: (doc.getElementById('user-first-name') as HTMLInputElement)?.value || '',
      last_name: (doc.getElementById('user-last-name') as HTMLInputElement)?.value || '',
      email: (doc.getElementById('user-email') as HTMLInputElement)?.value || '',
      role: ((doc.getElementById('user-role') as HTMLSelectElement)?.value as UserRole) || 'user',
      company_id: parseInt((doc.getElementById('user-company') as HTMLSelectElement)?.value || '', 10) || null,
      department: (doc.getElementById('user-department') as HTMLInputElement)?.value || null
    };

    if (options?.includePassword) {
      payload.password = (doc.getElementById('user-password') as HTMLInputElement)?.value || '';
    }

    return payload;
  });
  const computeUserStats = userManagementUiApi.computeUserStats || ((): { active: number; inactive: number; devs: number; activeDevs: number; neverLogged: number } => ({ active: 0, inactive: 0, devs: 0, activeDevs: 0, neverLogged: 0 }));
  const buildEmptyUserTableMarkup = userManagementUiApi.buildEmptyUserTableMarkup || ((): string => '');
  const buildUserTableMarkup = userManagementUiApi.buildUserTableMarkup || ((): string => '');

  // ─── Text Utilities ─────────────────────────────────────────────────────
  function looksLikeMojibake(text: string | null): boolean {
    if (typeof textApi.looksLikeMojibake === 'function') {
      return textApi.looksLikeMojibake(text);
    }
    return typeof text === 'string' && /\u00c3.|\u00c2.|\u00e2.|\u00f0\u0160\u0178|\ufffd/.test(text);
  }

  function repairMojibakeText(text: string): string {
    if (typeof textApi.repairMojibakeText === 'function') {
      return textApi.repairMojibakeText(text);
    }
    if (!looksLikeMojibake(text)) return text;

    let repaired = text;

    for (let i = 0; i < 3; i += 1) {
      try {
        const bytes = Uint8Array.from(repaired, char => char.charCodeAt(0) & 0xff);
        const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);

        if (!decoded || decoded === repaired) break;
        repaired = decoded;

        if (!looksLikeMojibake(repaired)) break;
      } catch {
        break;
      }
    }

    return repaired;
  }

  function normalizeCurrentUser(user: User | null): User | null {
    if (typeof textApi.normalizeCurrentUser === 'function') {
      return textApi.normalizeCurrentUser(user);
    }
    if (!user) return null;

    const displayName = [
      user.first_name,
      user.last_name
    ].filter(Boolean).join(' ').trim() || user.name || 'Usuario';

    return {
      ...user,
      name: displayName
    };
  }

  // ─── JWT Token Management ──────────────────────────────────────────────
  function getAuthToken(): string | null {
    return localStorage.getItem('inc_token');
  }

  function setAuthToken(token: string | null): void {
    if (token) {
      localStorage.setItem('inc_token', token);
    } else {
      localStorage.removeItem('inc_token');
    }
  }

  function getAuthHeaders(): Record<string, string> {
    const token = getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response | null> {
    const headers = getAuthHeaders();

    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 || response.status === 403) {
        const cloned = response.clone();
        try {
          const data = await cloned.json();
          if (data.code === 'INVALID_TOKEN' || data.code === 'NO_TOKEN') {
            handleTokenExpiration();
            return null;
          }
        } catch {
          // ignore parse errors
        }
      }

      return response;
    } catch (error) {
      console.error('Authenticated fetch error:', error);
      throw error;
    }
  }

  function handleTokenExpiration(): void {
    setAuthToken(null);
    currentUser = null;
    sessionStorage.removeItem('inc_user');
    showToast('Sessao expirada. Faca login novamente.', 'warning');
    showLogin();
  }

  // ─── Initialization ────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', (): void => {
    enhanceStaticAccessibility();
    enhanceFilterAccessibility();
    initAuth();
    initNavigation();
    initForm();
    initUpload();
    initFilters();
    initUserMenu();
    initPasswordToggle();
    initModalClickGuard();
    if (typeof (window as Window).refreshIcons === 'function') {
      (window as Window).refreshIcons();
    }
    window.addEventListener('hashchange', handleHashChange);
  });

  // Anti-spam: bloqueia cliques repetidos no mesmo botão dentro de qualquer modal.
  // O primeiro clique passa; cliques no mesmo botão dentro da janela são engolidos
  // antes de chegar a qualquer onclick/handler. Cobre todos os botões de todos os modais.
  function initModalClickGuard(): void {
    const WINDOW_MS = 700;
    document.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!btn || !btn.closest('.modal-overlay')) return;
      const now = Date.now();
      const last = Number(btn.dataset.lastClickTs || '0');
      if (now - last < WINDOW_MS) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      btn.dataset.lastClickTs = String(now);
    }, true);

    // Fecha o dropdown de atribuição ao clicar fora dele.
    document.addEventListener('click', (e) => {
      const inside = (e.target as HTMLElement).closest('.manage-assign');
      document.querySelectorAll<HTMLElement>('.manage-assign-menu:not([hidden])').forEach(menu => {
        if (menu.closest('.manage-assign') !== inside) {
          menu.hidden = true;
          menu.previousElementSibling?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  // ─── Auth ──────────────────────────────────────────────────────────────
  function initAuth(): void {
    const stored = sessionStorage.getItem('inc_user');
    const token = getAuthToken();

    if (stored) {
      try {
        currentUser = normalizeCurrentUser(JSON.parse(stored));
        if (currentUser) {
          sessionStorage.setItem('inc_user', JSON.stringify(currentUser));
        }

        if (requiresRoleToken(currentUser?.role) && !token) {
          handleTokenExpiration();
          return;
        }

        showApp();
        handleHashChange();
        return;
      } catch {
        sessionStorage.removeItem('inc_user');
        setAuthToken(null);
      }
    }

    showLogin();

    const loginForm = document.getElementById('login-form');
    if (loginForm) {
      loginForm.addEventListener('submit', handleLogin);
    } else {
      console.error('[Auth] Login form not found!');
    }

    const logoutBtn = document.getElementById('btn-logout');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', handleLogout);
    }

    void initLoginCaptcha();
    initPasswordRecovery();
  }

  // ─── Captcha (Cloudflare Turnstile) ──────────────────────────────────────
  interface TurnstileApi {
    render: (el: string | HTMLElement, opts: { sitekey: string; theme?: string; size?: string; appearance?: string }) => string;
    getResponse: (id?: string) => string | undefined;
    reset: (id?: string) => void;
  }
  function getTurnstile(): TurnstileApi | undefined {
    return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
  }
  let captchaSiteKey = '';
  let turnstileLoad: Promise<void> | null = null;
  const captchaWidgets: Record<string, string> = {};

  async function ensureTurnstile(): Promise<boolean> {
    if (!captchaSiteKey) return false;
    if (getTurnstile()) return true;
    if (!turnstileLoad) {
      turnstileLoad = new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = (): void => resolve();
        script.onerror = (): void => resolve();
        document.head.appendChild(script);
      });
    }
    await turnstileLoad;
    return !!getTurnstile();
  }

  async function renderCaptcha(containerId: string): Promise<void> {
    const el = document.getElementById(containerId);
    if (!el || captchaWidgets[containerId]) return;
    if (!(await ensureTurnstile())) return;
    const ts = getTurnstile();
    if (!ts) return;
    captchaWidgets[containerId] = ts.render(el, { sitekey: captchaSiteKey, theme: 'light', size: 'flexible', appearance: 'interaction-only' });
  }

  function captchaToken(containerId: string): string | undefined {
    const id = captchaWidgets[containerId];
    return id ? getTurnstile()?.getResponse(id) || undefined : undefined;
  }

  function resetCaptcha(containerId: string): void {
    const id = captchaWidgets[containerId];
    if (id) getTurnstile()?.reset(id);
  }

  async function initLoginCaptcha(): Promise<void> {
    try {
      const resp = await fetch('/api/public-config');
      if (resp.ok) captchaSiteKey = ((await resp.json()) as { turnstileSiteKey?: string }).turnstileSiteKey || '';
    } catch {
      return; // sem config, fluxos seguem sem captcha
    }
    if (!captchaSiteKey) return;
    void renderCaptcha('login-captcha');
  }

  // ─── Recuperação de senha ────────────────────────────────────────────────
  let resetToken = '';

  function showAuthPanel(panel: 'login' | 'forgot' | 'reset'): void {
    const map: Record<string, string> = { login: 'login-form', forgot: 'forgot-form', reset: 'reset-form' };
    for (const [key, id] of Object.entries(map)) {
      const el = document.getElementById(id);
      if (el) (el as HTMLElement).hidden = key !== panel;
    }
  }

  function clearResetParam(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('reset');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    resetToken = '';
  }

  function initPasswordRecovery(): void {
    document.getElementById('btn-forgot-link')?.addEventListener('click', () => {
      showAuthPanel('forgot');
      void renderCaptcha('forgot-captcha');
    });
    document.getElementById('btn-forgot-back')?.addEventListener('click', () => showAuthPanel('login'));
    document.getElementById('btn-reset-back')?.addEventListener('click', () => { clearResetParam(); showAuthPanel('login'); });
    document.getElementById('forgot-form')?.addEventListener('submit', handleForgotPassword);
    document.getElementById('reset-form')?.addEventListener('submit', handleResetPassword);

    const t = new URLSearchParams(window.location.search).get('reset');
    if (t) {
      resetToken = t;
      showAuthPanel('reset');
      void renderCaptcha('reset-captcha');
    }
  }

  async function handleForgotPassword(e: Event): Promise<void> {
    e.preventDefault();
    const email = (document.getElementById('forgot-email') as HTMLInputElement)?.value.trim() || '';
    const msg = document.getElementById('forgot-msg');
    const btn = document.getElementById('btn-forgot') as HTMLButtonElement | null;
    if (msg) msg.classList.add('hidden');
    if (btn) { btn.disabled = true; btn.textContent = 'Enviando...'; }
    try {
      const resp = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken: captchaToken('forgot-captcha') }),
      });
      const data = await resp.json().catch(() => ({})) as { message?: string; error?: string };
      if (resp.ok) {
        showToast(data.message || 'Se o email estiver cadastrado, enviamos as instruções.', 'success');
        showAuthPanel('login');
      } else if (msg) {
        msg.textContent = data.error || 'Não foi possível enviar. Tente novamente.';
        msg.classList.remove('hidden');
      }
    } catch {
      if (msg) { msg.textContent = 'Sem conexão com o servidor.'; msg.classList.remove('hidden'); }
    } finally {
      resetCaptcha('forgot-captcha');
      if (btn) { btn.disabled = false; btn.textContent = 'Enviar link de redefinição'; }
    }
  }

  async function handleResetPassword(e: Event): Promise<void> {
    e.preventDefault();
    const pass = (document.getElementById('reset-password') as HTMLInputElement)?.value || '';
    const confirm = (document.getElementById('reset-password-confirm') as HTMLInputElement)?.value || '';
    const msg = document.getElementById('reset-msg');
    const btn = document.getElementById('btn-reset') as HTMLButtonElement | null;
    if (msg) msg.classList.add('hidden');
    if (pass.length < 6) { if (msg) { msg.textContent = 'A senha precisa ter ao menos 6 caracteres.'; msg.classList.remove('hidden'); } return; }
    if (pass !== confirm) { if (msg) { msg.textContent = 'As senhas não conferem.'; msg.classList.remove('hidden'); } return; }
    if (btn) { btn.disabled = true; btn.textContent = 'Redefinindo...'; }
    try {
      const resp = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: pass, turnstileToken: captchaToken('reset-captcha') }),
      });
      const data = await resp.json().catch(() => ({})) as { message?: string; error?: string };
      if (resp.ok) {
        clearResetParam();
        showAuthPanel('login');
        showToast(data.message || 'Senha redefinida! Faça login.', 'success');
      } else {
        if (msg) { msg.textContent = data.error || 'Não foi possível redefinir.'; msg.classList.remove('hidden'); }
        resetCaptcha('reset-captcha');
      }
    } catch {
      if (msg) { msg.textContent = 'Sem conexão com o servidor.'; msg.classList.remove('hidden'); }
      resetCaptcha('reset-captcha');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Redefinir senha'; }
    }
  }

  async function handleLogin(e: Event): Promise<void> {
    e.preventDefault();

    const email = (document.getElementById('login-email') as HTMLInputElement)?.value.trim() || '';
    const password = (document.getElementById('login-password') as HTMLInputElement)?.value || '';
    const errorEl = document.getElementById('login-error');
    const btn = document.getElementById('btn-login') as HTMLButtonElement | null;

    if (errorEl) errorEl.classList.add('hidden');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Entrando...';
    }

    const turnstileToken = captchaToken('login-captcha');

    try {
      const response = await fetch('/api/dev/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, turnstileToken })
      });

      if (response.ok) {
        const data = await response.json();
        currentUser = normalizeCurrentUser(data.user);

        if (data.token) {
          localStorage.setItem('inc_token', data.token);
        }

        if (currentUser) {
          sessionStorage.setItem('inc_user', JSON.stringify(currentUser));
        }
        showApp();
        handleHashChange();

        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Entrar';
        }
        return;
      } else {
        let errorData = null;

        try {
          errorData = await response.json();
        } catch {
          errorData = null;
        }

        throw new Error(getLoginErrorMessage(response.status, errorData));
      }
    } catch (error) {
      // Token Turnstile é de uso único: reseta pra permitir nova tentativa.
      resetCaptcha('login-captcha');
      if (errorEl) {
        const msg = error instanceof TypeError
          ? 'Sem conexão com o servidor. Verifique sua internet.'
          : (error as Error).message || 'Não foi possível entrar. Tente novamente.';
        errorEl.textContent = msg;
        errorEl.classList.remove('hidden');
      }
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Entrar';
      }
    }
  }

  function getLoginErrorMessage(status: number, data: { error?: string } | null): string {
    const normalizedMessage = String(data?.error || '').toLowerCase();

    if (status === 401 || normalizedMessage.includes('credenciais') || normalizedMessage.includes('senha')) {
      return 'Email ou senha invalidos.';
    }

    if (status === 403 || normalizedMessage.includes('acesso')) {
      return 'Seu perfil nao tem permissao para acessar o sistema.';
    }

    if (status === 429) {
      return 'Muitas tentativas de login. Aguarde um momento e tente novamente.';
    }

    if (status >= 500) {
      return 'Nao foi possivel entrar agora. Tente novamente em instantes.';
    }

    return 'Nao foi possivel realizar o login. Verifique seus dados e tente novamente.';
  }

  function initPasswordToggle(): void {
    const passwordInput = document.getElementById('login-password') as HTMLInputElement | null;
    const toggleButton = document.getElementById('login-password-toggle');

    if (!passwordInput || !toggleButton) {
      return;
    }

    toggleButton.addEventListener('click', (): void => {
      const isVisible = passwordInput.type === 'text';

      passwordInput.type = isVisible ? 'password' : 'text';
      toggleButton.setAttribute('aria-label', isVisible ? 'Mostrar senha' : 'Ocultar senha');
      toggleButton.setAttribute('aria-pressed', String(!isVisible));
      toggleButton.innerHTML = `<i data-lucide="${isVisible ? 'eye' : 'eye-off'}" aria-hidden="true"></i>`;

      if (typeof (window as Window).refreshIcons === 'function') {
        (window as Window).refreshIcons();
      }
    });
  }

  function handleLogout(): void {
    currentUser = null;
    closeUserMenu();
    setAuthToken(null);
    sessionStorage.removeItem('inc_user');
    showLogin();
    showToast('Logout realizado com sucesso', 'success');
  }

  (window as Window).handleLogout = handleLogout;

  function showLogin(): void {
    const screenLogin = document.getElementById('screen-login');
    const appShell = document.getElementById('app-shell');

    if (screenLogin) screenLogin.style.display = '';
    if (appShell) appShell.style.display = 'none';

    stopPolling();
    closeUserMenu();

    const passwordInput = document.getElementById('login-password') as HTMLInputElement | null;
    const toggleButton = document.getElementById('login-password-toggle');
    if (passwordInput) {
      passwordInput.type = 'password';
    }
    if (toggleButton) {
      toggleButton.setAttribute('aria-label', 'Mostrar senha');
      toggleButton.setAttribute('aria-pressed', 'false');
      toggleButton.innerHTML = '<i data-lucide="eye" aria-hidden="true"></i>';
      if (typeof (window as Window).refreshIcons === 'function') {
        (window as Window).refreshIcons();
      }
    }
  }

  function showApp(): void {
    const screenLogin = document.getElementById('screen-login');
    const appShell = document.getElementById('app-shell');

    if (screenLogin) screenLogin.style.display = 'none';
    if (appShell) appShell.style.display = 'block';

    if (currentUser) {
      const roleText = getRoleLabelText(currentUser.role);
      const headerRole = document.getElementById('header-user-role');
      if (headerRole) {
        headerRole.textContent = roleText;
        headerRole.className = `user-role role-${currentUser.role}`;
      }

      const displayName = currentUser.name || 'Usuario';
      const companyName = currentUser.company_name || '';

      const headerName = document.getElementById('header-user-name');
      if (headerName) headerName.textContent = displayName;

      const triggerName = document.getElementById('header-trigger-name');
      if (triggerName) triggerName.textContent = displayName;

      // Nome da empresa abaixo da logo (ajuda a saber qual login está ativo).
      const headerCompany = document.getElementById('header-company-name');
      if (headerCompany) {
        if (companyName) {
          headerCompany.textContent = companyName;
          headerCompany.hidden = false;
        } else {
          headerCompany.hidden = true;
        }
      }

      const userAvatar = document.getElementById('user-avatar');
      if (userAvatar) userAvatar.textContent = displayName.charAt(0).toUpperCase();

      const nameField = document.getElementById('field-reporter-name') as HTMLInputElement | null;
      const emailField = document.getElementById('field-reporter-email') as HTMLInputElement | null;
      if (nameField) nameField.value = displayName;
      if (emailField) emailField.value = currentUser.email;

      // Show/hide dropdown menus based on role
      const navTrello = document.getElementById('nav-trello');
      if (navTrello) {
        navTrello.classList.add('hidden');
        navTrello.setAttribute('style', 'display: none !important;');
      }

      const navGestao = document.getElementById('nav-gestao');
      if (navGestao) {
        if (canAccessUserManagementView(currentUser.role)) {
          navGestao.classList.remove('hidden');
          navGestao.removeAttribute('style');
        } else {
          navGestao.classList.add('hidden');
          navGestao.setAttribute('style', 'display: none !important;');
        }
      }

      // "Gestão Incidentes" (dentro do dropdown "Incidentes") — só dev/owner
      const tabIncidentDashboard = document.getElementById('tab-incident-dashboard');
      const navIncidentes = document.getElementById('nav-incidentes');
      const allowedManageIncidents = currentUser.role === 'developer' || currentUser.role === 'owner';
      if (tabIncidentDashboard) {
        if (allowedManageIncidents) {
          tabIncidentDashboard.classList.remove('hidden');
          tabIncidentDashboard.removeAttribute('style');
        } else {
          tabIncidentDashboard.classList.add('hidden');
          tabIncidentDashboard.setAttribute('style', 'display: none !important;');
        }
      }
      // Para role user: dropdown só com uma opção é inútil. Transformo em tab direto.
      if (navIncidentes) {
        const trigger = navIncidentes.querySelector<HTMLButtonElement>('.nav-dropdown-trigger');
        const chevron = navIncidentes.querySelector<HTMLElement>('.dropdown-chevron');
        const menu = navIncidentes.querySelector<HTMLElement>('.nav-dropdown-menu');
        if (!allowedManageIncidents) {
          if (chevron) chevron.style.display = 'none';
          if (menu) menu.style.display = 'none';
          if (trigger && !trigger.dataset.userTabBound) {
            trigger.dataset.userTabBound = 'true';
            trigger.dataset.view = 'list';
            trigger.classList.remove('nav-dropdown-trigger');
            trigger.setAttribute('aria-haspopup', 'false');
            trigger.addEventListener('click', (ev) => {
              ev.stopPropagation();
              window.location.hash = 'list';
            }, true);
          }
        } else {
          if (chevron) chevron.style.display = '';
          if (menu) menu.style.display = '';
        }
      }

      // Dashboard button - only owner
      const btnDashboard = document.getElementById('btn-external-dashboard');
      if (btnDashboard) {
        if (canAccessDashboardView(currentUser.role)) {
          btnDashboard.classList.remove('hidden');
          btnDashboard.removeAttribute('style');
        } else {
          btnDashboard.classList.add('hidden');
          btnDashboard.setAttribute('style', 'display: none !important;');
        }
      }

      // Governance tab (inside Gestão dropdown) - only owner/admin
      const tabGovernance = document.getElementById('tab-governance');
      if (tabGovernance) {
        const parent = tabGovernance.closest('.nav-dropdown-item');
        if (parent) (parent as HTMLElement).style.display = canAccessGovernanceView(currentUser.role) ? '' : 'none';
        tabGovernance.style.display = canAccessGovernanceView(currentUser.role) ? '' : 'none';
      }

      // Load products for dropdowns
      void loadProductsForForm();
    }
  }

  // ─── Navigation ────────────────────────────────────────────────────────
  function closeAllDropdowns(): void {
    document.querySelectorAll<HTMLElement>('.nav-dropdown.open').forEach(dropdown => {
      // Se o foco está dentro do dropdown, tira ele antes de aplicar aria-hidden
      // pra evitar warning "Blocked aria-hidden on element because its descendant retained focus"
      if (dropdown.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
      dropdown.classList.remove('open');
      const menu = dropdown.querySelector<HTMLElement>('.nav-dropdown-menu');
      if (menu) {
        menu.setAttribute('aria-hidden', 'true');
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.minWidth = '';
      }
      const trigger = dropdown.querySelector('.nav-dropdown-trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function positionDropdownMenu(trigger: HTMLElement, menu: HTMLElement): void {
    const rect = trigger.getBoundingClientRect();
    menu.style.position = 'fixed';
    menu.style.top = `${rect.bottom}px`;
    menu.style.left = `${rect.left}px`;
    menu.style.minWidth = `${Math.max(rect.width, 180)}px`;
  }

  function initNavigation(): void {
    // Regular nav tabs (not in dropdowns)
    document.querySelectorAll<HTMLElement>('.nav-tab:not(.nav-dropdown-trigger)').forEach(tab => {
      tab.addEventListener('click', (): void => {
        const view = tab.dataset.view;
        if (view) {
          closeAllDropdowns();
          window.location.hash = view;
        }
      });
    });

    // Dropdown triggers
    document.querySelectorAll<HTMLElement>('.nav-dropdown-trigger').forEach(trigger => {
      trigger.addEventListener('click', (event: Event): void => {
        event.stopPropagation();
        const dropdown = trigger.closest('.nav-dropdown');
        if (!dropdown) return;

        const isOpen = dropdown.classList.contains('open');
        closeAllDropdowns();

        if (!isOpen) {
          dropdown.classList.add('open');
          trigger.setAttribute('aria-expanded', 'true');
          const menu = dropdown.querySelector<HTMLElement>('.nav-dropdown-menu');
          if (menu) {
            menu.setAttribute('aria-hidden', 'false');
            positionDropdownMenu(trigger, menu);
          }
        }
      });
    });

    // Dropdown items
    document.querySelectorAll<HTMLElement>('.nav-dropdown-item').forEach(item => {
      item.addEventListener('click', (): void => {
        const view = item.dataset.view;
        if (view) {
          closeAllDropdowns();
          window.location.hash = view;
        }
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (event: Event): void => {
      const target = event.target as HTMLElement;
      if (!target.closest('.nav-dropdown')) {
        closeAllDropdowns();
      }
    });

    const logoLink = document.getElementById('app-logo');
    if (logoLink) {
      logoLink.addEventListener('click', (event: Event): void => {
        event.preventDefault();
        closeAllDropdowns();

        if (!currentUser) {
          window.location.hash = 'new';
          return;
        }

        if (currentView === 'new') {
          switchView('new');
          return;
        }

        window.location.hash = 'new';
      });
    }
  }

  function handleHashChange(): void {
    if (!currentUser) return;
    const hash = window.location.hash.replace('#', '') || 'new';
    switchView(hash);
  }

  function switchView(viewName: string): void {
    currentView = viewName;

    // Update regular nav tabs
    document.querySelectorAll<HTMLElement>('.nav-tab:not(.nav-dropdown-trigger)').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.view === viewName);
    });

    // Update dropdown items and parent triggers
    document.querySelectorAll<HTMLElement>('.nav-dropdown-item').forEach(item => {
      const isActive = item.dataset.view === viewName;
      item.classList.toggle('active', isActive);

      // Also highlight parent dropdown trigger if any child is active
      if (isActive) {
        const dropdown = item.closest('.nav-dropdown');
        if (dropdown) {
          const trigger = dropdown.querySelector('.nav-dropdown-trigger');
          if (trigger) trigger.classList.add('active');
        }
      }
    });

    // Remove active from dropdown triggers if no child is active
    document.querySelectorAll<HTMLElement>('.nav-dropdown').forEach(dropdown => {
      const hasActiveChild = dropdown.querySelector('.nav-dropdown-item.active');
      const trigger = dropdown.querySelector('.nav-dropdown-trigger');
      if (trigger && !hasActiveChild) {
        trigger.classList.remove('active');
      }
    });

    document.querySelectorAll<HTMLElement>('.view').forEach(view => {
      view.classList.toggle('active', view.id === `view-${viewName}`);
    });

    runTransition(document.getElementById(`view-${viewName}`), 'context');

    if (viewName === 'list') {
      loadStats();
      loadIncidents();
      startPolling();
    } else if (viewName === 'manage') {
      if (canAccessManagementView(currentUser?.role)) {
        loadManageTeamOverview();
        loadManageStats();
        loadManageIncidents();
        startManagePolling();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'users') {
      if (canAccessUserManagementView(currentUser?.role)) {
        loadUserManagementView();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'companies') {
      if (canAccessUserManagementView(currentUser?.role)) {
        loadCompaniesView();
        initCompanyModal();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'resolution-types') {
      if (currentUser?.role === 'owner') {
        loadResolutionTypesView();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'trello') {
      if (canAccessTrelloView(currentUser?.role)) {
        loadTrelloDashboard();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'trello-integration') {
      if (canAccessTrelloView(currentUser?.role)) {
        loadTrelloIntegration();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'governance') {
      if (canAccessGovernanceView(currentUser?.role)) {
        loadGovernanceData();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'reports') {
      if (canAccessUserManagementView(currentUser?.role)) {
        loadReportsData();
      } else {
        window.location.hash = 'new';
        return;
      }
    } else if (viewName === 'incident-dashboard') {
      loadIncidentDashboard();
      startDashboardPolling();
    } else {
      stopPolling();
      stopManagePolling();
      stopDashboardPolling();
    }
  }

  // ─── Polling ────────────────────────────────────────────────────────────
  function startPolling(): void {
    stopPolling();
    pollingInterval = setInterval((): void => {
      if (currentView === 'list') {
        loadStats();
        loadIncidents(true);
      }
    }, 30000);
  }

  function stopPolling(): void {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  }

  // ─── Form ──────────────────────────────────────────────────────────────
  function initForm(): void {
    const form = document.getElementById('incident-form');
    initIncidentFieldConstraints();
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    }
    initCriticalityHint();
    initSimilarIncidents();
    initDraftPersistence();
  }

  // ─── Criticality hint (shows description of selected level) ──────────────
  const CRIT_DESCRIPTIONS: Record<string, string> = {
    baixa: 'Problema cosmético ou pontual — não impede uso.',
    media: 'Funcionalidade degradada — existe contorno.',
    alta: 'Afeta múltiplos usuários — contorno difícil.',
    critica: 'Sistema fora do ar ou impede uso essencial.',
  };

  function initCriticalityHint(): void {
    const hint = document.getElementById('crit-hint');
    if (!hint) return;
    document.querySelectorAll<HTMLInputElement>('input[name="criticality"]').forEach(input => {
      input.addEventListener('change', () => {
        hint.textContent = CRIT_DESCRIPTIONS[input.value] || '';
      });
    });
  }

  // ─── Similar incidents while typing title ────────────────────────────────
  let similarTimer: number | null = null;

  function initSimilarIncidents(): void {
    const titleField = document.getElementById('field-title') as HTMLInputElement | null;
    const container = document.getElementById('similar-incidents');
    if (!titleField || !container) return;

    titleField.addEventListener('input', () => {
      if (similarTimer) window.clearTimeout(similarTimer);
      const term = titleField.value.trim();
      if (term.length < 4) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }
      similarTimer = window.setTimeout(() => fetchSimilarIncidents(term), 400);
    });
  }

  async function fetchSimilarIncidents(term: string): Promise<void> {
    const container = document.getElementById('similar-incidents');
    if (!container) return;
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/incidents?search=${encodeURIComponent(term)}&limit=5`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;
      const data = await response.json() as { success: boolean; incidents?: Array<{ id: number; protocol: string; title: string }> };
      const items = data.incidents || [];
      if (items.length === 0) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }
      container.hidden = false;
      container.innerHTML = `
        <div class="similar-incidents-header">Incidentes parecidos já registrados</div>
        <div class="similar-incidents-list">
          ${items.map(i => `
            <button type="button" class="similar-incidents-item" data-id="${i.id}">
              <span class="protocol">${escapeHtml(i.protocol)}</span>
              <span>${escapeHtml(i.title)}</span>
            </button>
          `).join('')}
        </div>
      `;
      container.querySelectorAll<HTMLButtonElement>('.similar-incidents-item').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.id;
          if (id) window.location.hash = `list?id=${id}`;
        });
      });
    } catch {
      /* silencioso — sugestão é opcional */
    }
  }

  // ─── Draft persistence (localStorage) ────────────────────────────────────
  const DRAFT_KEY = 'inc_draft_v1';

  function initDraftPersistence(): void {
    const form = document.getElementById('incident-form') as HTMLFormElement | null;
    const banner = document.getElementById('draft-banner');
    const discardBtn = document.getElementById('draft-discard');
    if (!form) return;

    restoreDraft();

    form.addEventListener('input', saveDraft);

    if (discardBtn) {
      discardBtn.addEventListener('click', () => {
        clearDraft();
        form.reset();
        if (banner) banner.hidden = true;
        const hint = document.getElementById('crit-hint');
        if (hint) hint.textContent = '';
      });
    }
  }

  function saveDraft(): void {
    const title = (document.getElementById('field-title') as HTMLInputElement | null)?.value || '';
    const description = (document.getElementById('field-description') as HTMLTextAreaElement | null)?.value || '';
    const product = (document.getElementById('field-product') as HTMLSelectElement | null)?.value || '';
    const criticality = (document.querySelector('input[name="criticality"]:checked') as HTMLInputElement | null)?.value || '';
    if (!title && !description && !product && !criticality) {
      clearDraft();
      return;
    }
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, description, product, criticality }));
    } catch { /* quota cheia, ignora */ }
  }

  function restoreDraft(): void {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as { title?: string; description?: string; product?: string; criticality?: string };
      const titleEl = document.getElementById('field-title') as HTMLInputElement | null;
      const descEl = document.getElementById('field-description') as HTMLTextAreaElement | null;
      const prodEl = document.getElementById('field-product') as HTMLSelectElement | null;
      const banner = document.getElementById('draft-banner');
      let restored = false;
      if (titleEl && draft.title) { titleEl.value = draft.title; restored = true; }
      if (descEl && draft.description) { descEl.value = draft.description; restored = true; }
      if (prodEl && draft.product) { prodEl.value = draft.product; restored = true; }
      if (draft.criticality) {
        const radio = document.querySelector<HTMLInputElement>(`input[name="criticality"][value="${draft.criticality}"]`);
        if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change')); restored = true; }
      }
      if (restored && banner) banner.hidden = false;
      updateCharacterCounter(titleEl, 'field-title-counter', INCIDENT_LIMITS.title);
      updateCharacterCounter(descEl, 'field-description-counter', INCIDENT_LIMITS.description);
    } catch { /* draft corrompido, ignora */ }
  }

  function clearDraft(): void {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* */ }
    const banner = document.getElementById('draft-banner');
    if (banner) banner.hidden = true;
  }

  async function loadProductsForForm(): Promise<void> {
    try {
      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) {
        console.error('[Products] Erro ao carregar produtos:', response.status);
        return;
      }

      const data = await response.json() as { success: boolean; products: Array<{ id: number; name: string; code: string; color: string | null }> };

      if (!data.success || !data.products) {
        console.error('[Products] Resposta inválida');
        return;
      }

      cachedProducts = data.products;

      const productSelects = [
        document.getElementById('field-product') as HTMLSelectElement | null,
        document.getElementById('filter-product') as HTMLSelectElement | null,
        document.getElementById('dash-filter-product') as HTMLSelectElement | null,
        document.getElementById('manage-filter-product') as HTMLSelectElement | null
      ];

      productSelects.forEach(select => {
        if (!select) return;

        const currentValue = select.value;
        const isFormField = select.id === 'field-product';

        select.innerHTML = isFormField
          ? '<option value="">Selecione...</option>'
          : '<option value="">Todos os produtos</option>';

        cachedProducts.forEach(product => {
          const option = document.createElement('option');
          option.value = String(product.id);
          option.textContent = product.name;
          if (product.color) {
            option.dataset.color = product.color;
          }
          select.appendChild(option);
        });

        if (currentValue) {
          select.value = currentValue;
        }
      });

      // Re-aplica produto do rascunho caso restoreDraft tenha rodado antes do load
      const formProduct = document.getElementById('field-product') as HTMLSelectElement | null;
      if (formProduct && !formProduct.value) {
        try {
          const raw = localStorage.getItem(DRAFT_KEY);
          if (raw) {
            const draft = JSON.parse(raw) as { product?: string };
            if (draft.product) formProduct.value = draft.product;
          }
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('[Products] Erro ao carregar produtos:', error);
    }
  }

  function initUserMenu(): void {
    const trigger = document.getElementById('user-menu-trigger');
    const menu = document.getElementById('user-menu');

    if (!trigger || !menu) {
      return;
    }

    trigger.addEventListener('click', (event: Event): void => {
      event.stopPropagation();
      const shouldOpen = !menu.classList.contains('open');
      setUserMenuOpen(shouldOpen);
    });

    document.addEventListener('click', (event: Event): void => {
      if (!menu.contains(event.target as Node)) {
        closeUserMenu();
      }
    });

    document.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeUserMenu();
      }
    });

    const profileButton = document.getElementById('btn-user-profile');
    if (profileButton) {
      profileButton.addEventListener('click', (): void => {
        closeUserMenu();
        openUserProfileModal();
      });
    }
  }

  function openUserProfileModal(): void {
    const modal = document.getElementById('user-profile-modal');
    if (!modal || !currentUser) return;

    const nameEl = document.getElementById('profile-info-name');
    const emailEl = document.getElementById('profile-info-email');
    const roleEl = document.getElementById('profile-info-role');
    const companyRow = document.getElementById('profile-row-company');
    const companyEl = document.getElementById('profile-info-company');

    if (nameEl) nameEl.textContent = currentUser.name || '—';
    if (emailEl) emailEl.textContent = currentUser.email || '—';
    if (roleEl) roleEl.textContent = getRoleLabelText(currentUser.role);
    if (companyRow && companyEl) {
      if (currentUser.company_name) {
        companyEl.textContent = currentUser.company_name;
        companyRow.hidden = false;
      } else {
        companyRow.hidden = true;
      }
    }

    openDialogModal(modal);
  }

  function setUserMenuOpen(isOpen: boolean): void {
    const menu = document.getElementById('user-menu');
    const trigger = document.getElementById('user-menu-trigger');
    const dropdown = document.getElementById('user-menu-dropdown');

    if (!menu || !trigger || !dropdown) {
      return;
    }

    menu.classList.toggle('open', isOpen);
    trigger.setAttribute('aria-expanded', String(isOpen));
    dropdown.setAttribute('aria-hidden', String(!isOpen));
  }

  function closeUserMenu(): void {
    setUserMenuOpen(false);
  }

  // ─── Modal Helpers ─────────────────────────────────────────────────────
  function getDialogElement(modal: HTMLElement | null): HTMLElement | null {
    return modal?.querySelector('.modal-content') || modal;
  }

  function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
    if (!container) return [];

    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  function focusModal(modal: HTMLElement | null): void {
    const dialog = getDialogElement(modal);
    const focusable = getFocusableElements(dialog);
    const focusTarget = focusable[0] || dialog;

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  function openDialogModal(modal: HTMLElement | null, options: ModalOpenOptions = {}): void {
    if (!modal) return;

    const dialog = getDialogElement(modal);
    const activeElement = options.sourceElement || document.activeElement;

    if (!modalFocusState.has(modal.id)) {
      modalFocusState.set(modal.id, activeElement instanceof HTMLElement ? activeElement : null);
    }

    modal.style.display = 'flex';
    modal.classList.add(options.stateClass || 'show');
    modal.setAttribute('aria-hidden', 'false');

    if (dialog && !dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }

    updateModalScrollState();
    requestAnimationFrame((): void => focusModal(modal));
  }

  function closeDialogModal(modal: HTMLElement | null): void {
    if (!modal) return;

    blurWithin(modal);
    modal.classList.remove('show', 'active');
    modal.style.display = '';
    modal.setAttribute('aria-hidden', 'true');

    const previousFocus = modalFocusState.get(modal.id);
    modalFocusState.delete(modal.id);

    updateModalScrollState();

    if (previousFocus && document.contains(previousFocus) && typeof previousFocus.focus === 'function') {
      requestAnimationFrame((): void => previousFocus.focus());
    }
  }

  function trapFocusInModal(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    const openModals = getOpenModals();
    const currentModal = openModals[openModals.length - 1];
    if (!currentModal) return;

    const dialog = getDialogElement(currentModal);
    const focusable = getFocusableElements(dialog);

    if (focusable.length === 0) {
      event.preventDefault();
      if (dialog) dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === first || activeElement === dialog)) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ─── Accessibility Enhancements ────────────────────────────────────────
  function enhanceStaticAccessibility(): void {
    const toastContainer = document.getElementById('toast-container');
    if (toastContainer) {
      toastContainer.setAttribute('aria-live', 'polite');
      toastContainer.setAttribute('aria-atomic', 'false');
    }

    const loginError = document.getElementById('login-error');
    if (loginError) {
      loginError.setAttribute('role', 'alert');
      loginError.setAttribute('aria-live', 'assertive');
    }

    const modalConfigs: [string, string, string][] = [
      ['incident-modal', 'incident-modal-title', 'Detalhes do Incidente'],
      ['manage-modal', 'manage-modal-title', 'Gerenciar Incidente'],
      ['image-preview-modal', 'image-preview-modal-title', 'Anexo'],
      ['user-modal', 'user-modal-title', 'Novo Usuario'],
      ['company-modal', 'company-modal-title', 'Nova Empresa']
    ];

    modalConfigs.forEach(([modalId, titleId, fallbackTitle]) => {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('aria-labelledby', titleId);

      const title = modal.querySelector('.modal-title');
      if (title && !title.id) {
        title.id = titleId;
      } else if (!title && fallbackTitle) {
        modal.setAttribute('aria-label', fallbackTitle);
      }

      const dialog = getDialogElement(modal);
      if (dialog && !dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      const closeButton = modal.querySelector('.modal-close');
      if (closeButton && !closeButton.getAttribute('aria-label')) {
        closeButton.setAttribute('aria-label', `Fechar ${fallbackTitle.toLowerCase()}`);
      }
    });
  }

  function enhanceFilterAccessibility(): void {
    const fieldConfigs: [string, string][] = [
      ['filter-search', 'Buscar incidentes'],
      ['filter-portal', 'Portal'],
      ['filter-criticality', 'Criticidade'],
      ['manage-filter-search', 'Buscar incidentes'],
      ['manage-filter-portal', 'Portal'],
      ['manage-filter-criticality', 'Criticidade'],
      ['manage-filter-status', 'Status'],
      ['manage-filter-assigned', 'Responsavel'],
      ['user-filter-search', 'Buscar usuario'],
      ['user-filter-role', 'Perfil'],
      ['user-filter-company', 'Empresa'],
      ['user-filter-status', 'Status']
    ];

    fieldConfigs.forEach(([fieldId, labelText]) => {
      const field = document.getElementById(fieldId) as HTMLElement & { dataset: { a11yEnhanced?: string } } | null;
      if (!field || field.dataset.a11yEnhanced === 'true') return;

      const wrapper = field.closest('.search-wrapper') || field.closest('.filter-group') || field.parentElement;
      if (!wrapper) return;

      const fieldContainer = wrapper as HTMLElement;

      if (!fieldContainer) return;

      fieldContainer.classList.add('filter-field');
      if (fieldId.includes('search')) {
        fieldContainer.classList.add('filter-field-search');
      }

      let label = document.querySelector(`label[for="${fieldId}"]`);
      if (!label) {
        label = document.createElement('label');
        label.className = 'filter-label';
        (label as HTMLLabelElement).htmlFor = fieldId;
        label.textContent = labelText;
        fieldContainer.insertBefore(label, fieldContainer.firstChild);
      }

      field.dataset.a11yEnhanced = 'true';
    });
  }

  function initIncidentFieldConstraints(): void {
    const titleField = document.getElementById('field-title') as HTMLInputElement | null;
    const descriptionField = document.getElementById('field-description') as HTMLTextAreaElement | null;

    if (!titleField || !descriptionField) {
      return;
    }

    titleField.maxLength = INCIDENT_LIMITS.title;
    descriptionField.maxLength = INCIDENT_LIMITS.description;

    updateCharacterCounter(titleField, 'field-title-counter', INCIDENT_LIMITS.title);
    updateCharacterCounter(descriptionField, 'field-description-counter', INCIDENT_LIMITS.description);

    titleField.addEventListener('input', (): void => {
      updateCharacterCounter(titleField, 'field-title-counter', INCIDENT_LIMITS.title);
    });

    descriptionField.addEventListener('input', (): void => {
      updateCharacterCounter(descriptionField, 'field-description-counter', INCIDENT_LIMITS.description);
    });
  }

  function updateCharacterCounter(field: HTMLInputElement | HTMLTextAreaElement | null, counterId: string, maxLength: number): void {
    const counter = document.getElementById(counterId);
    if (!field || !counter) {
      return;
    }

    const currentLength = field.value.length;
    counter.textContent = `${currentLength}/${maxLength}`;
    counter.classList.toggle('limit-near', currentLength >= Math.floor(maxLength * 0.9));
  }

  function normalizeIncidentFieldValue(value: string, options: { multiline?: boolean } = {}): string {
    if (typeof value !== 'string') {
      return '';
    }

    const normalized = value
      .normalize('NFC')
      .replace(/\r\n?/g, '\n')
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');

    if (!options.multiline) {
      return normalized.replace(/\s+/g, ' ').trim();
    }

    return normalized
      .split('\n')
      .map((line) => line.replace(/[ \t]+$/g, ''))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function clearFieldErrors(form: HTMLFormElement): void {
    form.querySelectorAll('.field-error').forEach(el => el.remove());
    form.querySelectorAll('.field-invalid').forEach(el => el.classList.remove('field-invalid'));
  }

  function flagField(fieldEl: HTMLElement | null, message: string): void {
    if (!fieldEl) return;
    const fieldContainer = fieldEl.closest('.field') || fieldEl.parentElement;
    fieldEl.classList.add('field-invalid');
    if (fieldContainer && !fieldContainer.querySelector('.field-error')) {
      const err = document.createElement('p');
      err.className = 'field-error';
      err.textContent = message;
      fieldContainer.appendChild(err);
    }
  }

  function failValidation(form: HTMLFormElement, fieldEl: HTMLElement | null, message: string): void {
    flagField(fieldEl, message);
    showToast(message, 'error');
    if (fieldEl) {
      fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (fieldEl as HTMLInputElement).focus?.();
    }
  }

  async function handleFormSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const submitBtn = form.querySelector('.btn-submit') as HTMLButtonElement | null;

    const titleField = form.querySelector('#field-title') as HTMLInputElement;
    const descriptionField = form.querySelector('#field-description') as HTMLTextAreaElement;
    const productField = form.querySelector('#field-product') as HTMLSelectElement | null;
    const criticalityContainer = form.querySelector('.criticality-options') as HTMLElement | null;
    const title = normalizeIncidentFieldValue(titleField.value);
    const description = normalizeIncidentFieldValue(descriptionField.value, { multiline: true });
    const productId = productField?.value;
    const criticality = (form.querySelector('input[name="criticality"]:checked') as HTMLInputElement)?.value;

    titleField.value = title;
    descriptionField.value = description;
    updateCharacterCounter(titleField, 'field-title-counter', INCIDENT_LIMITS.title);
    updateCharacterCounter(descriptionField, 'field-description-counter', INCIDENT_LIMITS.description);

    clearFieldErrors(form);

    if (!title) return failValidation(form, titleField, 'Informe o título do incidente.');
    if (title.length > INCIDENT_LIMITS.title) return failValidation(form, titleField, `O título deve ter no máximo ${INCIDENT_LIMITS.title} caracteres.`);
    if (!productId) return failValidation(form, productField, 'Selecione o produto.');
    if (!criticality) return failValidation(form, criticalityContainer, 'Selecione a criticidade.');
    if (!description) return failValidation(form, descriptionField, 'Descreva o incidente.');
    if (description.length > INCIDENT_LIMITS.description) return failValidation(form, descriptionField, `A descrição deve ter no máximo ${INCIDENT_LIMITS.description} caracteres.`);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('product_id', productId);
    formData.append('criticality', criticality);
    formData.append('reporter_name', currentUser?.name || '');
    formData.append('reporter_email', currentUser?.email || '');
    selectedFiles.forEach(f => formData.append('images', f));

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="spinner"></span> Enviando...';
    }

    try {
      const response = await fetch('/api/incidents', {
        method: 'POST',
        body: formData
      });

      // 413 / 401 / 5xx muitas vezes não retornam JSON — tratar antes
      if (response.status === 413) {
        throw new Error('Arquivo muito grande. Reduza o tamanho ou envie menos arquivos.');
      }
      if (response.status === 401) {
        throw new Error('Sessão expirou. Recarregue a página e faça login novamente.');
      }
      if (response.status === 429) {
        throw new Error('Muitos envios em pouco tempo. Aguarde e tente novamente.');
      }

      const data = await response.json().catch(() => ({} as { error?: string; incident?: Incident }));

      if (!response.ok) {
        throw new Error(data.error || `Erro ao criar incidente (HTTP ${response.status})`);
      }

      if (!data.incident) {
        throw new Error('Resposta inválida do servidor.');
      }
      showSuccess(data.incident);
    } catch (error) {
      const msg = error instanceof TypeError
        ? 'Sem conexão com o servidor. Verifique sua internet.'
        : (error as Error).message;
      showToast(msg, 'error');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'Enviar Incidente';
      }
    }
  }

  function showSuccess(incident: Incident): void {
    const formContainer = document.getElementById('form-container');
    const successContainer = document.getElementById('success-container');
    const redirectCountdown = document.getElementById('success-redirect-countdown');

    clearSuccessRedirect();
    if (formContainer) formContainer.classList.add('hidden');
    const protocolEl = successContainer?.querySelector('.success-protocol');
    if (protocolEl) protocolEl.textContent = incident.protocol;
    if (successContainer) successContainer.classList.add('visible');
    if (redirectCountdown) {
      redirectCountdown.textContent = String(SUCCESS_REDIRECT_DELAY_SECONDS);
    }

    let secondsRemaining = SUCCESS_REDIRECT_DELAY_SECONDS;
    successRedirectInterval = window.setInterval((): void => {
      secondsRemaining -= 1;
      if (redirectCountdown) {
        redirectCountdown.textContent = String(Math.max(secondsRemaining, 0));
      }
    }, 1000);

    successRedirectTimeout = window.setTimeout((): void => {
      clearSuccessRedirect();
      resetForm();
    }, SUCCESS_REDIRECT_DELAY_SECONDS * 1000);

    clearDraft();
    showToast(`Incidente ${incident.protocol} registrado com sucesso.`, 'success');
  }

  function clearSuccessRedirect(): void {
    if (successRedirectTimeout) {
      window.clearTimeout(successRedirectTimeout);
      successRedirectTimeout = null;
    }

    if (successRedirectInterval) {
      window.clearInterval(successRedirectInterval);
      successRedirectInterval = null;
    }
  }

  function resetForm(): void {
    const form = document.getElementById('incident-form') as HTMLFormElement | null;
    const formContainer = document.getElementById('form-container');
    const successContainer = document.getElementById('success-container');

    clearSuccessRedirect();
    if (form) form.reset();
    clearUpload();
    updateCharacterCounter(document.getElementById('field-title') as HTMLInputElement | null, 'field-title-counter', INCIDENT_LIMITS.title);
    updateCharacterCounter(document.getElementById('field-description') as HTMLTextAreaElement | null, 'field-description-counter', INCIDENT_LIMITS.description);
    if (successContainer) successContainer.classList.remove('visible');
    if (formContainer) formContainer.classList.remove('hidden');
  }

  (window as Window).resetForm = resetForm;

  // ─── Image Upload ──────────────────────────────────────────────────────
  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024;
  const ALLOWED_IMAGES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
  const ALLOWED_VIDEOS = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];

  function initUpload(): void {
    const zone = document.getElementById('upload-zone');
    const input = document.getElementById('upload-input') as HTMLInputElement | null;

    if (!zone || !input) return;

    zone.addEventListener('click', (e: MouseEvent): void => {
      if ((e.target as HTMLElement).closest('.upload-file-remove')) return;
      input.click();
    });

    zone.addEventListener('dragover', (e: DragEvent): void => {
      e.preventDefault();
      zone.classList.add('dragover');
    });

    zone.addEventListener('dragleave', (): void => {
      zone.classList.remove('dragover');
    });

    zone.addEventListener('drop', (e: DragEvent): void => {
      e.preventDefault();
      zone.classList.remove('dragover');
      if (e.dataTransfer?.files) {
        addFiles(Array.from(e.dataTransfer.files));
      }
    });

    input.addEventListener('change', (): void => {
      if (input.files) {
        addFiles(Array.from(input.files));
      }
      input.value = '';
    });
  }

  function addFiles(newFiles: File[]): void {
    for (const file of newFiles) {
      const isImage = ALLOWED_IMAGES.includes(file.type);
      const isVideo = ALLOWED_VIDEOS.includes(file.type);

      if (!isImage && !isVideo) {
        showToast(`"${file.name}" nao e um tipo permitido.`, 'error');
        continue;
      }

      if (selectedFiles.length >= MAX_FILES) {
        showToast(`Maximo de ${MAX_FILES} arquivos.`, 'warning');
        break;
      }

      const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0) + file.size;
      if (totalSize > MAX_TOTAL_SIZE) {
        showToast('Tamanho total excede 100MB.', 'warning');
        break;
      }

      selectedFiles.push(file);
    }

    renderFileList();
  }

  function removeFile(index: number): void {
    selectedFiles.splice(index, 1);
    renderFileList();
  }

  function renderFileList(): void {
    const zone = document.getElementById('upload-zone');
    const container = document.getElementById('upload-file-list');

    if (!zone || !container) return;

    zone.classList.toggle('has-file', selectedFiles.length > 0);

    if (selectedFiles.length === 0) {
      container.innerHTML = '';
      return;
    }

    const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    container.innerHTML = selectedFiles.map((file, i) => {
      const isVideo = ALLOWED_VIDEOS.includes(file.type);
      const thumb = isVideo
        ? `<span class="upload-file-thumb-video">\uD83C\uDFAC</span>`
        : `<img class="upload-file-thumb" src="${URL.createObjectURL(file)}" alt="" />`;
      return `
        <div class="upload-file-chip">
          ${thumb}
          <span class="upload-file-name">${escapeHtml(file.name)}</span>
          <span class="upload-file-size">${formatFileSize(file.size)}</span>
          <button type="button" class="upload-file-remove" onclick="removeUploadFile(${i})" title="Remover">\u00D7</button>
        </div>
      `;
    }).join('') + `
      <div class="upload-file-summary">${selectedFiles.length}/${MAX_FILES} arquivos \u2014 ${formatFileSize(totalSize)} de 100MB</div>
    `;
  }

  function clearUpload(): void {
    selectedFiles = [];
    const input = document.getElementById('upload-input') as HTMLInputElement | null;
    if (input) input.value = '';
    renderFileList();
  }

  (window as Window).clearUpload = clearUpload;
  (window as Window).removeUploadFile = removeFile;

  function parseImagePaths(raw: string | null | undefined): string[] {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return [raw]; }
  }

  function isVideoPath(filePath: string): boolean {
    return /\.(mp4|webm|mov|avi)$/i.test(filePath);
  }

  function resolveAttachmentUrl(filePath: string): string {
    if (/^https?:\/\//.test(filePath)) return filePath;
    if (filePath.startsWith('/uploads/') || filePath.startsWith('uploads/')) {
      return `/${filePath.replace(/^\/+/, '')}`;
    }
    return `/uploads/${filePath.replace(/^\/+/, '')}`;
  }

  function renderAttachment(filePath: string): string {
    const url = resolveAttachmentUrl(filePath);
    if (isVideoPath(filePath)) {
      return `<video src="${url}" controls class="attachment-tile-video"></video>`;
    }
    return `<button type="button" class="attachment-tile" data-preview-url="${url}"><img src="${url}" alt="Anexo" loading="lazy" /></button>`;
  }

  function openImagePreview(url: string): void {
    const modal = document.getElementById('image-preview-modal');
    const img = document.getElementById('image-preview-element') as HTMLImageElement | null;
    if (!modal || !img) return;
    img.src = url;
    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
      if (m !== modal) m.setAttribute('inert', '');
    });
  }

  function closeImagePreview(): void {
    const modal = document.getElementById('image-preview-modal');
    if (modal) {
      blurWithin(modal);
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
    }
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => m.removeAttribute('inert'));
  }

  (window as Window).closeImagePreview = closeImagePreview;

  // Delegation pra abrir preview ao clicar em qualquer attachment-tile
  document.addEventListener('click', (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLElement>('.attachment-tile[data-preview-url]');
    if (tile) {
      const url = tile.dataset.previewUrl;
      if (url) openImagePreview(url);
    }
  });

  function renderAttachmentsGallery(imagePath: string | null | undefined, options: { title?: string } = {}): string {
    const paths = parseImagePaths(imagePath);
    if (paths.length === 0) return '';
    const { title = 'Evidências' } = options;
    return `
      <div class="detail-section">
        <div class="detail-section-title">${title}</div>
        <div class="attachments-gallery">
          ${paths.map(p => renderAttachment(p)).join('')}
        </div>
      </div>
    `;
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ─── Filters ───────────────────────────────────────────────────────────
  function initFilters(): void {
    const statusTabs = document.getElementById('incident-status-tabs');
    if (statusTabs) {
      statusTabs.setAttribute('role', 'tablist');
      statusTabs.setAttribute('aria-label', 'Agrupamento de incidentes');
    }

    document.querySelectorAll<HTMLSelectElement>('#view-list .filter-select').forEach(select => {
      select.addEventListener('change', (): void => {
        currentPage = 1;
        loadIncidents();
      });
    });

    document.querySelectorAll<HTMLElement>('#incident-status-tabs .segment-tab').forEach((tab) => {
      const isActive = tab.dataset.statusGroup === incidentStatusGroup;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      tab.removeAttribute('aria-pressed');

      tab.addEventListener('click', (): void => {
        incidentStatusGroup = (tab.dataset.statusGroup as StatusGroup) || 'active';

        document.querySelectorAll<HTMLElement>('#incident-status-tabs .segment-tab').forEach((button) => {
          const isActive = button === tab;
          button.classList.toggle('active', isActive);
          button.setAttribute('aria-selected', String(isActive));
          button.tabIndex = isActive ? 0 : -1;
        });

        currentPage = 1;
        loadIncidents();
      });
    });

    const searchInput = document.getElementById('filter-search') as HTMLInputElement | null;
    let searchTimeout: ReturnType<typeof setTimeout>;
    if (searchInput) {
      searchInput.addEventListener('input', (): void => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout((): void => {
          currentPage = 1;
          loadIncidents();
        }, 400);
      });
    }
  }

  function getFilters(): { portal: string; criticality: string; status_group: string; search: string } {
    return {
      portal: (document.getElementById('filter-portal') as HTMLSelectElement)?.value || '',
      criticality: (document.getElementById('filter-criticality') as HTMLSelectElement)?.value || '',
      status_group: incidentStatusGroup,
      search: (document.getElementById('filter-search') as HTMLInputElement)?.value || ''
    };
  }

  // ─── Stats ─────────────────────────────────────────────────────────────
  async function loadStats(): Promise<void> {
    const view = document.getElementById('view-list');
    const currentStats = view?.querySelector<HTMLElement & { dataset: { loaded?: string } }>('.stats-grid');
    if (currentStats && !currentStats.dataset.loaded) {
      currentStats.innerHTML = renderStatsSkeleton(5);
    }

    try {
      const response = await fetch('/api/stats');
      const stats = await response.json() as IncidentStats;

      if (currentStats && !currentStats.dataset.loaded) {
        currentStats.outerHTML = `
          <div class="stats-grid" data-loaded="true">
            <div class="stat-card">
              <div class="stat-value" id="stat-total">0</div>
              <div class="stat-label">Total</div>
            </div>
            <div class="stat-card stat-blue">
              <div class="stat-value" id="stat-abertos">0</div>
              <div class="stat-label">Abertos</div>
            </div>
            <div class="stat-card stat-amber">
              <div class="stat-value" id="stat-andamento">0</div>
              <div class="stat-label">Em Andamento</div>
            </div>
            <div class="stat-card stat-green">
              <div class="stat-value" id="stat-resolvidos">0</div>
              <div class="stat-label">Resolvidos</div>
            </div>
            <div class="stat-card stat-red">
              <div class="stat-value" id="stat-criticos">0</div>
              <div class="stat-label">Criticos</div>
            </div>
          </div>
        `;
      }

      animateValue('stat-total', Number(stats.total) || 0);
      animateValue('stat-abertos', Number(stats.abertos) || 0);
      animateValue('stat-andamento', Number(stats.em_andamento) || 0);
      animateValue('stat-resolvidos', Number(stats.resolvidos) || 0);
      animateValue('stat-criticos', Number(stats.criticos_ativos) || 0);
    } catch (error) {
      console.error('Erro ao carregar stats:', error);
    }
  }

  function animateValue(elementId: string, newValue: number): void {
    const el = document.getElementById(elementId);
    if (!el) return;

    const current = parseInt(el.textContent || '0') || 0;
    if (current === newValue) return;

    const statCard = el.closest('.stat-card');
    if (statCard) {
      runTransition(statCard as HTMLElement, 'context');
    }

    const duration = 350;
    const startTime = performance.now();

    function update(currentTime: number): void {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      if (el) el.textContent = String(Math.round(current + (newValue - current) * eased));
      if (progress < 1) requestAnimationFrame(update);
    }

    requestAnimationFrame(update);
  }

  function runTransition(element: HTMLElement | null, variant: string = 'context'): void {
    if (!element) return;

    const className = `transition-${variant}`;
    element.classList.remove('transition-context', 'transition-drill', 'transition-continuity');
    void element.offsetWidth;
    element.classList.add(className);

    window.setTimeout((): void => {
      element.classList.remove(className);
    }, 320);
  }

  function markContinuitySource(element: HTMLElement | null): void {
    if (!element) return;

    element.classList.add('continuity-source');
    window.setTimeout((): void => {
      element.classList.remove('continuity-source');
    }, 320);
  }

  function renderStatsSkeleton(count: number = 5): string {
    return `
      <div class="skeleton-grid stats">
        ${Array.from({ length: count }, () => `
          <div class="skeleton-stat">
            <div class="skeleton-stack">
              <div class="skeleton-line lg w-50"></div>
              <div class="skeleton-line sm w-70"></div>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  function renderIncidentListSkeleton(count: number = 5): string {
    return Array.from({ length: count }, () => `
      <div class="skeleton-card">
        <div class="skeleton-row">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-stack flex-1">
            <div class="skeleton-row">
              <div class="skeleton-line sm w-20"></div>
              <div class="skeleton-line w-50"></div>
            </div>
            <div class="skeleton-line w-100"></div>
            <div class="skeleton-row">
              <div class="skeleton-line sm w-20"></div>
              <div class="skeleton-line sm w-20"></div>
              <div class="skeleton-line sm w-20"></div>
            </div>
          </div>
          <div class="skeleton-stack items-end min-w-22">
            <div class="skeleton-chip"></div>
            <div class="skeleton-line sm w-80"></div>
          </div>
        </div>
      </div>
    `).join('');
  }

  function renderDetailSkeleton(): string {
    return `
      <div class="skeleton-stack">
        <div class="detail-grid">
          ${Array.from({ length: 6 }, () => `
            <div class="detail-item">
              <div class="skeleton-stack">
                <div class="skeleton-line sm w-40"></div>
                <div class="skeleton-line w-80"></div>
              </div>
            </div>
          `).join('')}
        </div>
        <div class="detail-section">
          <div class="skeleton-stack">
            <div class="skeleton-line sm w-30"></div>
            <div class="skeleton-line w-100"></div>
            <div class="skeleton-line w-100"></div>
            <div class="skeleton-line w-70"></div>
          </div>
        </div>
      </div>
    `;
  }

  interface ModalLoader {
    finish: () => boolean;
    fail: (html: string) => void;
  }

  function beginModalLoad(modal: HTMLElement | null, body: HTMLElement | null, stateClass: string, sourceElement: HTMLElement | null = null): ModalLoader {
    markContinuitySource(sourceElement);
    if (body) body.classList.add('modal-body-pending');

    let skeletonVisible = false;
    let modalOpened = false;
    const openModal = (): void => {
      if (modalOpened) return;
      openDialogModal(modal, { stateClass, sourceElement });
      modalOpened = true;
    };
    const timer = window.setTimeout((): void => {
      openModal();
      if (body) body.innerHTML = renderDetailSkeleton();
      skeletonVisible = true;
    }, MODAL_SKELETON_DELAY_MS);

    return {
      finish(): boolean {
        window.clearTimeout(timer);
        if (!skeletonVisible && body) {
          body.innerHTML = '';
        }
        openModal();
        if (body) body.classList.remove('modal-body-pending');
        return skeletonVisible;
      },
      fail(html: string): void {
        window.clearTimeout(timer);
        if (body) body.innerHTML = html;
        openModal();
        if (body) body.classList.remove('modal-body-pending');
      }
    };
  }

  function renderTableSkeleton(columns: number = 7, rows: number = 5): string {
    return Array.from({ length: rows }, () => `
      <tr>
        ${Array.from({ length: columns }, (_, index) => `
          <td>${index === 0 ? `
            <div class="flex items-center gap-2.5">
              <div class="skeleton-avatar"></div>
              <div class="skeleton-cell w-30"></div>
            </div>
          ` : '<div class="skeleton-cell"></div>'}</td>
        `).join('')}
      </tr>
    `).join('');
  }

  // ─── Incident List ─────────────────────────────────────────────────────
  async function loadIncidents(silent: boolean = false): Promise<void> {
    const container = document.getElementById('incident-list');
    const pagination = document.getElementById('pagination');

    if (!silent && container) {
      container.innerHTML = renderIncidentListSkeleton(5);
      if (pagination) pagination.innerHTML = '';
      runTransition(container, 'context');
    }

    try {
      const filters = getFilters();
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: '20',
        ...(filters.portal && { portal: filters.portal }),
        ...(filters.criticality && { criticality: filters.criticality }),
        ...(filters.status_group && { status_group: filters.status_group }),
        ...(filters.search && { search: filters.search })
      });

      params.append('_t', String(Date.now()));

      const response = await authenticatedFetch(`/api/incidents?${params}`);
      if (!response) return;
      const result = await response.json() as { data: Incident[]; total: number; totalPages: number };

      renderIncidentList(result.data, result.total, result.totalPages);
    } catch (error) {
      console.error('Erro ao carregar incidentes:', error);
      if (container) {
        container.innerHTML = `
          <div class="empty-state">
            <div class="empty-title">Erro ao carregar</div>
            <div class="empty-desc">Nao foi possivel carregar os incidentes.</div>
          </div>
        `;
      }
    }
  }

  function renderIncidentList(incidents: Incident[], total: number, totalPages: number): void {
    const container = document.getElementById('incident-list');
    const paginationContainer = document.getElementById('pagination');

    if (!incidents || incidents.length === 0) {
      if (container) {
        container.innerHTML = `
          <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div class="empty-title">Nenhum incidente encontrado</div>
            <div class="empty-desc">Nao ha incidentes com os filtros selecionados.</div>
          </div>
        `;
      }
      if (paginationContainer) paginationContainer.innerHTML = '';
      return;
    }

    if (container) {
      const staleBanner = renderStaleBanner(incidents);
      container.innerHTML = staleBanner + incidents.map(inc => renderIncidentCard(inc)).join('');
      runTransition(container, 'context');

      container.querySelectorAll<HTMLElement>('.incident-card').forEach(card => {
        card.addEventListener('click', (): void => {
          openIncidentDetail(card.dataset.id || '', card);
        });
      });
    }

    renderPagination(totalPages);
  }

  function renderStaleBanner(incidents: Incident[]): string {
    const now = Date.now();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    const stale = incidents.filter(inc => {
      if (inc.status === 'resolvido' || inc.status === 'fechado') return false;
      const ref = inc.updated_at || inc.created_at;
      if (!ref) return false;
      return now - new Date(ref).getTime() >= SEVEN_DAYS;
    });
    if (stale.length === 0) return '';
    const protocols = stale.slice(0, 5).map(s => s.protocol).join(', ');
    const extra = stale.length > 5 ? ` e mais ${stale.length - 5}` : '';
    return `
      <div class="stale-banner">
        <strong>${stale.length} incidente(s) sem movimento há 7+ dias:</strong>
        <span class="stale-banner-list">${escapeHtml(protocols)}${extra}</span>
      </div>
    `;
  }

  function renderIncidentCard(inc: Incident, options: { mode?: string } = {}): string {
    const { mode = 'list' } = options;
    const critInfo = CRITICALITIES[inc.criticality] || { label: '?', tag: '?' };
    const statusInfo = STATUSES[inc.status] || { label: inc.status };
    const createdAt = formatDate(inc.created_at);
    const normalizedTitle = escapeHtml(inc.title);
    const normalizedDescription = escapeHtml(inc.description || '');
    const shouldShowDescription = normalizedDescription && normalizedDescription !== normalizedTitle;
    const cardClassName = mode === 'manage' ? 'incident-card incident-card-manage' : 'incident-card';
    const interactionAttr = mode === 'manage'
      ? `onclick="openManageModal('${inc.id}', this)"`
      : '';
    const productName = (inc as Incident & { product_name?: string }).product_name;
    const canManage = mode === 'manage';

    const actionsMenu = canManage ? `
      <div class="incident-actions">
        <button type="button" class="incident-actions-trigger" aria-label="Ações" data-incident-actions="${inc.id}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><circle cx="12" cy="6" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="18" r="1.5"/></svg>
        </button>
        <div class="incident-actions-menu" data-incident-actions-menu="${inc.id}" role="menu">
          <button type="button" class="incident-actions-item" data-action="edit" data-incident-id="${inc.id}">Editar</button>
          <button type="button" class="incident-actions-item" data-action="archive" data-incident-id="${inc.id}">Arquivar</button>
          <button type="button" class="incident-actions-item incident-actions-item-danger" data-action="close" data-incident-id="${inc.id}">Encerrar</button>
        </div>
      </div>
    ` : '';

    return `
      <div class="${cardClassName}" data-id="${inc.id}" data-criticality="${inc.criticality}" ${interactionAttr}>
        <div class="incident-topline">
          <div class="incident-header">
            <span class="incident-protocol">${inc.protocol}</span>
            <span class="incident-date">${createdAt}</span>
          </div>
          <div class="incident-topline-right">
            <span class="status-badge status-${inc.status}">
              <span class="status-dot"></span>
              ${statusInfo.label}
            </span>
            ${actionsMenu}
          </div>
        </div>
        <div class="incident-body">
          <div class="incident-title-row">
            <h3 class="incident-title">${normalizedTitle}</h3>
          </div>
          ${shouldShowDescription ? `<div class="incident-desc">${normalizedDescription}</div>` : ''}
          <div class="incident-meta">
            <span class="incident-meta-pill incident-meta-criticality">${critInfo.tag} ${critInfo.label}</span>
            ${productName ? `<span class="incident-meta-pill">${escapeHtml(productName)}</span>` : ''}
            <span class="incident-meta-pill">Por ${escapeHtml(inc.reporter_name)}</span>
            ${inc.assigned_dev ? `<span class="incident-meta-pill">Responsavel: ${escapeHtml(inc.assigned_dev)}</span>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // ─── Pagination ────────────────────────────────────────────────────────
  function renderPagination(totalPages: number): void {
    const container = document.getElementById('pagination');

    if (!container) return;

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '';
    html += `<button class="pagination-btn" onclick="goToPage(${currentPage - 1})" ${currentPage <= 1 ? 'disabled' : ''}>&#8592;</button>`;

    for (let i = 1; i <= totalPages; i++) {
      if (totalPages > 7 && i > 2 && i < totalPages - 1 && Math.abs(i - currentPage) > 1) {
        if (i === 3 || i === totalPages - 2) html += '<span class="pagination-info">&#8230;</span>';
        continue;
      }
      html += `<button class="pagination-btn ${i === currentPage ? 'active' : ''}" onclick="goToPage(${i})">${i}</button>`;
    }

    html += `<button class="pagination-btn" onclick="goToPage(${currentPage + 1})" ${currentPage >= totalPages ? 'disabled' : ''}>&#8594;</button>`;

    container.innerHTML = html;
  }

  (window as any).goToPage = function (page: number): void {
    currentPage = page;
    loadIncidents();
    document.getElementById('view-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Incident action dropdown + arquivar/encerrar ───────────────────────
  // Capture phase: intercepta antes do onclick do card (que abre manage modal)
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const trigger = target.closest<HTMLElement>('[data-incident-actions]');
    if (trigger) {
      e.stopPropagation();
      e.preventDefault();
      const id = trigger.dataset.incidentActions;
      document.querySelectorAll<HTMLElement>('.incident-actions-menu.open').forEach(m => {
        if (m.dataset.incidentActionsMenu !== id) m.classList.remove('open');
      });
      const menu = document.querySelector<HTMLElement>(`[data-incident-actions-menu="${id}"]`);
      menu?.classList.toggle('open');
      return;
    }
    const item = target.closest<HTMLElement>('.incident-actions-item');
    if (item) {
      e.stopPropagation();
      e.preventDefault();
      const id = Number(item.dataset.incidentId);
      const action = item.dataset.action;
      document.querySelectorAll<HTMLElement>('.incident-actions-menu.open').forEach(m => m.classList.remove('open'));
      if (!id) return;
      if (action === 'edit') void (window as Window).openManageModal(id);
      else if (action === 'archive') void promptArchiveIncident(id);
      else if (action === 'close') void promptCloseIncident(id);
      return;
    }
    // Click fora de qualquer menu/trigger — fecha menus abertos
    if (!target.closest('.incident-actions')) {
      document.querySelectorAll<HTMLElement>('.incident-actions-menu.open').forEach(m => m.classList.remove('open'));
    }
  }, true);

  let incidentActionInFlight = false;

  // Recarrega a lista/stats da view atualmente visível (lista, gestão ou dashboard).
  // Sem isso, encerrar/arquivar a partir do dashboard ou gestão não some o card.
  function refreshIncidentsView(): void {
    if (currentView === 'incident-dashboard') {
      void loadDashboardStats();
      void loadDashboardIncidents();
    } else if (currentView === 'manage') {
      void loadManageStats();
      void loadManageIncidents(true);
    } else {
      void loadStats();
      void loadIncidents(true);
    }
  }

  async function promptArchiveIncident(id: number): Promise<void> {
    const res = await authenticatedFetch(`/api/incidents/${id}`);
    const data = res && res.ok ? await res.json() as { incident?: Incident } : null;
    const inc = data?.incident;
    const isResolved = inc && (inc.status === 'resolvido' || inc.status === 'fechado');
    const reason = await promptReasonModal({
      title: 'Arquivar incidente',
      message: isResolved
        ? `O incidente ${inc?.protocol || ''} já está resolvido/fechado. Confirmar arquivamento?`
        : `O incidente ${inc?.protocol || ''} ainda não foi resolvido. Para arquivá-lo, informe o motivo:`,
      requireReason: !isResolved,
      confirmLabel: 'Arquivar',
    });
    if (reason === null) return;
    if (incidentActionInFlight) return;
    incidentActionInFlight = true;
    try {
      const post = await authenticatedFetch(`/api/dev/incidents/${id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!post || !post.ok) {
        const err = post ? await post.json().catch(() => ({})) as { error?: string } : {};
        showToast(err.error || 'Erro ao arquivar', 'error');
        return;
      }
      showToast('Incidente arquivado', 'success');
      refreshIncidentsView();
    } finally {
      incidentActionInFlight = false;
    }
  }

  async function promptCloseIncident(id: number): Promise<void> {
    const res = await authenticatedFetch(`/api/incidents/${id}`);
    const data = res && res.ok ? await res.json() as { incident?: Incident } : null;
    const inc = data?.incident;
    const isResolved = inc && inc.status === 'resolvido';
    const reason = await promptReasonModal({
      title: 'Encerrar incidente',
      message: isResolved
        ? `O incidente ${inc?.protocol || ''} foi resolvido. Confirmar encerramento?`
        : `O incidente ${inc?.protocol || ''} ainda não foi resolvido. Para encerrá-lo, informe o motivo:`,
      requireReason: !isResolved,
      confirmLabel: 'Encerrar',
      danger: true,
    });
    if (reason === null) return;
    if (incidentActionInFlight) return;
    incidentActionInFlight = true;
    try {
      const post = await authenticatedFetch(`/api/dev/incidents/${id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!post || !post.ok) {
        const err = post ? await post.json().catch(() => ({})) as { error?: string } : {};
        showToast(err.error || 'Erro ao encerrar', 'error');
        return;
      }
      showToast('Incidente encerrado', 'success');
      refreshIncidentsView();
    } finally {
      incidentActionInFlight = false;
    }
  }

  async function promptAwaitValidation(id: number): Promise<void> {
    const result = await promptValidationModal();
    if (!result) return;
    const formData = new FormData();
    formData.append('note', result.note);
    result.files.forEach(f => formData.append('files', f));
    const token = getAuthToken();
    const res = await fetch(`/api/dev/incidents/${id}/await-validation`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      showToast(err.error || 'Erro ao enviar para validação', 'error');
      return;
    }
    showToast('Incidente enviado para validação do cliente', 'success');
    loadIncidents();
  }

  function promptValidationModal(): Promise<{ note: string; files: File[] } | null> {
    return new Promise(resolve => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay show';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML = `
        <div class="modal-content" style="max-width:560px">
          <div class="modal-header">
            <h2 class="modal-title">Enviar para validação do cliente</h2>
            <button type="button" class="modal-close" data-close-validation>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="modal-body">
            <p style="margin-bottom:14px;font-size:0.875rem;color:var(--text-muted)">Descreva a correção feita e anexe evidências (imagens ou vídeos) que o cliente possa usar pra validar.</p>
            <label class="field-label" style="font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-muted);display:block;margin-bottom:4px">Descrição da correção <span style="color:var(--crit-critica)">*</span></label>
            <textarea class="validation-note" rows="4" placeholder="O que foi corrigido e como o cliente pode validar..." style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-family:inherit;font-size:0.875rem;resize:vertical;margin-bottom:14px"></textarea>
            <label class="field-label" style="font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-muted);display:block;margin-bottom:4px">Evidências <span style="color:var(--crit-critica)">*</span></label>
            <input type="file" class="validation-files" accept="image/*,video/mp4,video/webm,video/quicktime" multiple style="width:100%;padding:10px;border:1px dashed var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-size:0.875rem" />
            <div class="validation-files-list" style="margin-top:8px;display:flex;flex-direction:column;gap:4px;font-size:0.8125rem;color:var(--text-muted)"></div>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px">
              <button type="button" class="btn btn-ghost" data-close-validation>Cancelar</button>
              <button type="button" class="btn btn-primary" data-confirm-validation>Enviar para validação</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
        if (m !== overlay) m.setAttribute('inert', '');
      });
      const cleanup = (value: { note: string; files: File[] } | null): void => {
        document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => m.removeAttribute('inert'));
        overlay.remove();
        resolve(value);
      };
      overlay.querySelectorAll<HTMLElement>('[data-close-validation]').forEach(el => {
        el.addEventListener('click', () => cleanup(null));
      });
      const fileInput = overlay.querySelector<HTMLInputElement>('.validation-files');
      const filesList = overlay.querySelector<HTMLDivElement>('.validation-files-list');
      let selected: File[] = [];
      fileInput?.addEventListener('change', () => {
        selected = Array.from(fileInput.files || []).slice(0, 5);
        if (filesList) {
          filesList.innerHTML = selected.length
            ? selected.map(f => `<span>• ${escapeHtml(f.name)} (${formatFileSize(f.size)})</span>`).join('')
            : '';
        }
      });
      overlay.querySelector<HTMLButtonElement>('[data-confirm-validation]')?.addEventListener('click', () => {
        const note = (overlay.querySelector<HTMLTextAreaElement>('.validation-note')?.value || '').trim();
        if (!note) { showToast('Informe a descrição da correção', 'error'); return; }
        if (selected.length === 0) { showToast('Anexe ao menos uma evidência', 'error'); return; }
        cleanup({ note, files: selected });
      });
    });
  }

  function promptReasonModal(opts: { title: string; message: string; requireReason: boolean; confirmLabel: string; danger?: boolean }): Promise<string | null> {
    return new Promise(resolve => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay show';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML = `
        <div class="modal-content" style="max-width:480px">
          <div class="modal-header">
            <h2 class="modal-title">${escapeHtml(opts.title)}</h2>
            <button type="button" class="modal-close" data-close-reason>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="modal-body">
            <p style="margin-bottom:14px">${escapeHtml(opts.message)}</p>
            <textarea class="reason-input" rows="3" placeholder="${opts.requireReason ? 'Motivo (obrigatório)' : 'Motivo (opcional)'}" style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--bg-input);color:var(--text-primary);font-family:inherit;font-size:0.875rem;resize:vertical"></textarea>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px">
              <button type="button" class="btn btn-ghost" data-close-reason>Cancelar</button>
              <button type="button" class="btn ${opts.danger ? 'btn-danger' : 'btn-primary'}" data-confirm-reason>${escapeHtml(opts.confirmLabel)}</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
        if (m !== overlay) m.setAttribute('inert', '');
      });
      const cleanup = (value: string | null): void => {
        document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => m.removeAttribute('inert'));
        overlay.remove();
        resolve(value);
      };
      overlay.querySelectorAll<HTMLElement>('[data-close-reason]').forEach(el => {
        el.addEventListener('click', () => cleanup(null));
      });
      overlay.querySelector<HTMLButtonElement>('[data-confirm-reason]')?.addEventListener('click', () => {
        const textarea = overlay.querySelector<HTMLTextAreaElement>('.reason-input');
        const value = textarea?.value.trim() || '';
        if (opts.requireReason && !value) {
          showToast('Informe o motivo', 'error');
          textarea?.focus();
          return;
        }
        cleanup(value);
      });
      const textarea = overlay.querySelector<HTMLTextAreaElement>('.reason-input');
      setTimeout(() => textarea?.focus(), 50);
    });
  }

  // ─── Incident Detail Modal ─────────────────────────────────────────────
  async function openIncidentDetail(id: string, sourceElement: HTMLElement | null = null): Promise<void> {
    const modal = document.getElementById('incident-modal');
    const body = document.getElementById('modal-body');
    const requestId = ++incidentModalRequestId;
    const loader = beginModalLoad(modal, body, 'active', sourceElement);

    try {
      const response = await authenticatedFetch(`/api/incidents/${id}`);
      if (requestId !== incidentModalRequestId) return;
      if (!response || !response.ok) {
        throw new Error(`HTTP ${response?.status || 'auth failed'}`);
      }
      const data = await response.json() as { success?: boolean; incident?: Incident } | Incident;
      const inc = unwrapIncident(data);
      if (!inc) throw new Error('Resposta vazia');
      const skeletonVisible = loader.finish();
      if (body) body.innerHTML = renderIncidentDetail(inc);
      if (skeletonVisible && body) {
        runTransition(body, 'continuity');
      }
    } catch (error) {
      if (requestId !== incidentModalRequestId) return;
      loader.fail('<div class="empty-state"><div class="empty-title">Erro ao carregar detalhes</div></div>');
    }
  }

  function renderIncidentDetail(inc: Incident): string {
    const productName = (inc as Incident & { product_name?: string }).product_name;
    const critInfo = CRITICALITIES[inc.criticality] || { label: inc.criticality };
    const statusInfo = STATUSES[inc.status] || { label: inc.status };

    const trackingLine = inc.assigned_dev
      ? `<div class="incident-detail-row"><span class="incident-detail-row-label">Atribu\u00eddo a</span><span class="incident-detail-row-value">${escapeHtml(inc.assigned_dev)} \u2014 desde ${inc.assigned_at ? formatDate(inc.assigned_at) : '\u2014'}</span></div>
         <div class="incident-detail-row"><span class="incident-detail-row-label">Previs\u00e3o</span><span class="incident-detail-row-value">${inc.estimated_delivery ? formatDate(inc.estimated_delivery) : 'Aguardando defini\u00e7\u00e3o'}</span></div>`
      : inc.status === 'aberto'
        ? `<div class="incident-detail-row incident-detail-row-empty"><span>Aguardando atribui\u00e7\u00e3o de um desenvolvedor</span></div>`
        : '';

    const imageSection = renderAttachmentsGallery(inc.image_path, { title: 'Evid\u00eancias' });

    return `
      <div class="incident-detail-stack">
        <div class="incident-detail-head">
          <div class="incident-detail-head-main">
            <span class="incident-detail-protocol">${inc.protocol}</span>
            <h2 class="incident-detail-title">${escapeHtml(inc.title)}</h2>
          </div>
          <div class="incident-detail-head-side">
            <span class="status-badge status-${inc.status}"><span class="status-dot"></span>${statusInfo.label}</span>
            <span class="incident-criticality incident-crit-${inc.criticality}">${critInfo.label}</span>
          </div>
        </div>

        <dl class="incident-detail-grid">
          ${productName ? `<div><dt>Produto</dt><dd>${escapeHtml(productName)}</dd></div>` : ''}
          <div><dt>Reportado por</dt><dd>${escapeHtml(inc.reporter_name)}</dd></div>
          <div><dt>Data</dt><dd>${formatDate(inc.created_at)}</dd></div>
        </dl>

        ${trackingLine ? `<div class="incident-detail-block">${trackingLine}</div>` : ''}

        <div class="incident-detail-block">
          <h3 class="incident-detail-block-title">Descri\u00e7\u00e3o</h3>
          <p class="incident-detail-description">${escapeHtml(inc.description)}</p>
        </div>

        ${inc.close_reason ? `<div class="incident-detail-block"><h3 class="incident-detail-block-title">Motivo do encerramento</h3><p>${escapeHtml(inc.close_reason)}</p></div>` : ''}
        ${inc.archive_reason ? `<div class="incident-detail-block"><h3 class="incident-detail-block-title">Motivo do arquivamento</h3><p>${escapeHtml(inc.archive_reason)}</p></div>` : ''}
        ${inc.reopen_reason ? `<div class="incident-detail-block"><h3 class="incident-detail-block-title">Motivo da reabertura</h3><p>${escapeHtml(inc.reopen_reason)}</p></div>` : ''}

        ${renderValidationBlock(inc)}

        ${renderClientTimeline(inc)}

        ${imageSection ? `<div class="incident-detail-block">${imageSection}</div>` : ''}
      </div>
    `;
  }

  function renderClientTimeline(inc: Incident): string {
    const events: Array<{ when: string; label: string }> = [];
    events.push({ when: inc.created_at, label: 'Incidente aberto' });
    if (inc.assigned_at && inc.assigned_dev) {
      events.push({ when: inc.assigned_at, label: `Atribuído a ${inc.assigned_dev}` });
    }
    if (inc.awaiting_validation_at) {
      events.push({ when: inc.awaiting_validation_at, label: 'Enviado para sua análise' });
    }
    if (inc.reopen_reason && inc.status === 'em_andamento') {
      events.push({ when: inc.updated_at, label: 'Reaberto após validação' });
    }
    if (inc.resolved_at) {
      events.push({ when: inc.resolved_at, label: 'Resolvido' });
    }
    if (inc.closed_at) {
      events.push({ when: inc.closed_at, label: 'Encerrado' });
    }
    if (inc.archived_at) {
      events.push({ when: inc.archived_at, label: 'Arquivado' });
    }
    events.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
    if (events.length <= 1) return '';
    return `
      <div class="incident-detail-block">
        <h3 class="incident-detail-block-title">Linha do tempo</h3>
        <ul class="client-timeline">
          ${events.map(e => `
            <li class="client-timeline-item">
              <span class="client-timeline-dot"></span>
              <div class="client-timeline-body">
                <span class="client-timeline-label">${escapeHtml(e.label)}</span>
                <time class="client-timeline-time">${formatDate(e.when)}</time>
              </div>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  function renderValidationBlock(inc: Incident): string {
    if (inc.status !== 'aguardando_validacao' || !inc.validation_note) return '';
    const files = parseImagePaths(inc.validation_files);
    const isReporter = currentUser?.email === inc.reporter_email;
    const canRespond = isReporter || currentUser?.role === 'owner';
    return `
      <div class="incident-detail-block validation-block">
        <h3 class="incident-detail-block-title">Aguardando sua validação</h3>
        <p class="incident-detail-description">${escapeHtml(inc.validation_note)}</p>
        ${files.length > 0 ? `<div class="attachments-gallery" style="margin-top:10px">${files.map(p => renderAttachment(p)).join('')}</div>` : ''}
        ${canRespond ? `
          <div class="validation-actions">
            <button type="button" class="btn btn-primary" data-validation-action="confirm" data-incident-id="${inc.id}">Confirmar resolução</button>
            <button type="button" class="btn btn-outline" data-validation-action="reopen" data-incident-id="${inc.id}">Não resolveu / Reabrir</button>
          </div>
        ` : ''}
      </div>
    `;
  }

  document.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-validation-action]');
    if (!btn) return;
    const id = Number(btn.dataset.incidentId);
    const action = btn.dataset.validationAction;
    if (!id) return;
    if (action === 'confirm') void clientConfirmResolution(id);
    else if (action === 'reopen') void clientReopenIncident(id);
  });

  async function clientConfirmResolution(id: number): Promise<void> {
    if (!window.confirm('Confirmar que o incidente foi resolvido?')) return;
    const res = await authenticatedFetch(`/api/incidents/${id}/confirm-resolution`, { method: 'POST' });
    if (!res || !res.ok) {
      const err = res ? await res.json().catch(() => ({})) as { error?: string } : {};
      showToast(err.error || 'Erro ao confirmar', 'error');
      return;
    }
    showToast('Resolução confirmada', 'success');
    closeAllModals();
    loadIncidents();
  }

  async function clientReopenIncident(id: number): Promise<void> {
    const reason = await promptReasonModal({
      title: 'Reabrir incidente',
      message: 'Descreva o que ainda não está funcionando ou o que o cliente espera:',
      requireReason: true,
      confirmLabel: 'Reabrir',
      danger: true,
    });
    if (!reason) return;
    const res = await authenticatedFetch(`/api/incidents/${id}/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res || !res.ok) {
      const err = res ? await res.json().catch(() => ({})) as { error?: string } : {};
      showToast(err.error || 'Erro ao reabrir', 'error');
      return;
    }
    showToast('Incidente reaberto', 'success');
    closeAllModals();
    loadIncidents();
  }

  function closeModal(): void {
    incidentModalRequestId += 1;
    const modal = document.getElementById('incident-modal');
    const body = document.getElementById('modal-body');
    closeDialogModal(modal);
    if (body) body.classList.remove('modal-body-pending');
  }

  (window as Window).closeModal = closeAllModals;

  // ─── Toast ─────────────────────────────────────────────────────────────
  function showToast(message: string, type: ToastType = 'info'): void {
    const container = document.getElementById('toast-container');

    if (!container) return;

    const icons: Record<ToastType, string> = {
      success: '\u2713',
      error: '\u2715',
      warning: '!',
      info: 'i'
    };

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.setAttribute('role', 'status');
    toast.innerHTML = `
      <span class="toast-icon">${icons[type] || 'i'}</span>
      <span class="toast-message">${escapeHtml(message)}</span>
      <button type="button" class="toast-close" aria-label="Fechar">&times;</button>
    `;

    const closeBtn = toast.querySelector('.toast-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (): void => {
        toast.classList.add('removing');
        setTimeout((): void => toast.remove(), 300);
      });
    }

    container.appendChild(toast);

    const autoCloseTimeout = setTimeout((): void => {
      toast.classList.add('removing');
      setTimeout((): void => toast.remove(), 300);
    }, 4000);

    // Clear timeout if manually closed
    closeBtn?.addEventListener('click', (): void => {
      clearTimeout(autoCloseTimeout);
    });
  }

  // ─── Utilities ─────────────────────────────────────────────────────────
  function escapeHtml(text: string | null | undefined): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '\u2014';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  }

  function formatExecutionTime(createdAt: string | null | undefined, endAt: string | null | undefined): string {
    if (!createdAt) return '—';
    const start = new Date(createdAt).getTime();
    const end = endAt ? new Date(endAt).getTime() : Date.now();
    if (isNaN(start) || isNaN(end)) return '—';

    const diffMs = end - start;
    if (diffMs < 0) return '—';

    const totalMinutes = Math.floor(diffMs / 60000);
    const totalHours = Math.floor(diffMs / 3600000);
    const totalDays = Math.floor(diffMs / 86400000);

    if (totalDays > 0) {
      const remainingHours = totalHours % 24;
      return remainingHours > 0 ? `${totalDays}d ${remainingHours}h` : `${totalDays}d`;
    }
    if (totalHours > 0) {
      const remainingMinutes = totalMinutes % 60;
      return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}min` : `${totalHours}h`;
    }
    return `${totalMinutes}min`;
  }

  // Stub functions for manage, users, trello, governance views
  // These would be fully implemented similarly to the above patterns

  let manageCurrentPage = 1;
  let manageFilters: Record<string, string> = {};
  let companies: Company[] = [];
  let userCurrentPage = 1;
  let userFilters: Record<string, string> = {};
  let activeManagePanel: string | null = null;

  async function loadManageStats(): Promise<void> {
    try {
      const response = await fetch('/api/stats');
      if (!response.ok) return;

      const stats = await response.json() as IncidentStats;

      const elTotal = document.getElementById('manage-stat-total');
      const elAbertos = document.getElementById('manage-stat-abertos');
      const elAndamento = document.getElementById('manage-stat-andamento');
      const elResolvidos = document.getElementById('manage-stat-resolvidos');
      const elCriticos = document.getElementById('manage-stat-criticos');

      if (elTotal) elTotal.textContent = String(stats.total || 0);
      if (elAbertos) elAbertos.textContent = String(stats.abertos || 0);
      if (elAndamento) elAndamento.textContent = String(stats.em_andamento || 0);
      if (elResolvidos) elResolvidos.textContent = String(stats.resolvidos || 0);
      if (elCriticos) elCriticos.textContent = String(stats.criticos_ativos || 0);
    } catch {
      console.error('Failed to load manage stats');
    }
  }

  async function loadManageTeamOverview(): Promise<void> {
    // TODO: Implement manage team overview
  }

  async function loadManageIncidents(silent: boolean = false): Promise<void> {
    const container = document.getElementById('manage-incident-list');
    if (!container) return;

    if (!silent) {
      container.innerHTML = renderIncidentListSkeleton(5);
    }

    try {
      const response = await authenticatedFetch(`/api/incidents?status_group=active&limit=50`);
      if (!response || !response.ok) {
        container.innerHTML = '<div class="empty-state">Erro ao carregar incidentes</div>';
        return;
      }

      const result = await response.json() as { data: Incident[]; total: number };
      const incidents = result.data || [];

      if (!incidents.length) {
        container.innerHTML = `
          <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div class="empty-title">Nenhum incidente ativo</div>
            <div class="empty-desc">Todos os incidentes foram resolvidos.</div>
          </div>
        `;
        return;
      }

      container.innerHTML = incidents.map(inc => renderIncidentCard(inc, { mode: 'manage' })).join('');
      refreshIcons();
    } catch (error) {
      console.error('Erro ao carregar incidentes:', error);
      container.innerHTML = '<div class="empty-state">Erro ao carregar incidentes</div>';
    }
  }

  function startManagePolling(): void {
    stopManagePolling();
    managePollingInterval = setInterval((): void => {
      loadManageStats();
      loadManageIncidents(true);
    }, 30000);
  }

  function stopManagePolling(): void {
    if (managePollingInterval) {
      clearInterval(managePollingInterval);
      managePollingInterval = null;
    }
  }

  // ─── User Management ────────────────────────────────────────────────────
  let usersCache: User[] = [];

  async function loadUserManagementView(): Promise<void> {
    await loadUsers();
    await loadCompaniesForSelect();
    initUserFilters();
    initUserModal();
    initDeletedUsersToggle();
  }

  function initDeletedUsersToggle(): void {
    const toggle = document.getElementById('deleted-users-toggle');
    if (toggle && !toggle.dataset.initialized) {
      toggle.dataset.initialized = 'true';
      toggle.addEventListener('click', toggleDeletedUsersSection);
    }
  }

  async function loadUsers(): Promise<void> {
    const tableBody = document.getElementById('user-table-body');
    if (!tableBody) return;

    try {
      const token = getAuthToken();
      const response = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar usuários');

      const data = await response.json();
      usersCache = data.users || [];

      updateUserStats(usersCache);
      renderUserTable(usersCache);

      // Load deleted users in parallel
      void loadDeletedUsers();
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      showToast('Erro ao carregar usuários', 'error');
    }
  }

  interface DeletedUser {
    id: number;
    name: string;
    email: string;
    role: string;
    deleted_at: string;
    company_name?: string;
  }

  async function loadDeletedUsers(): Promise<void> {
    try {
      const token = getAuthToken();
      const response = await fetch('/api/admin/users/deleted', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json() as { success: boolean; users: DeletedUser[] };
      renderDeletedUsers(data.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários deletados:', error);
    }
  }

  function renderDeletedUsers(users: DeletedUser[]): void {
    const section = document.getElementById('deleted-users-section');
    const list = document.getElementById('deleted-users-list');
    const countEl = document.getElementById('deleted-users-count');

    if (!section || !list || !countEl) return;

    if (users.length === 0) {
      section.style.display = 'none';
      return;
    }

    section.style.display = 'block';
    countEl.textContent = String(users.length);

    const roleLabels: Record<string, string> = {
      user: 'Usuário', developer: 'Dev', admin: 'Admin', owner: 'Owner'
    };

    list.innerHTML = users.map(user => {
      const initials = (user.name || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
      const deletedDate = new Date(user.deleted_at);
      const purgeDate = new Date(deletedDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      const daysLeft = Math.ceil((purgeDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      const isUrgent = daysLeft <= 7;
      const countdownText = daysLeft <= 0 ? 'Será removido em breve' : `Restam ${daysLeft} dia${daysLeft === 1 ? '' : 's'}`;

      return `
        <div class="deleted-user-card">
          <div class="deleted-user-avatar">${initials || '?'}</div>
          <div class="deleted-user-info">
            <div class="deleted-user-name">${escapeHtml(user.name || '')}</div>
            <div class="deleted-user-email">${escapeHtml(user.email)}</div>
          </div>
          <div class="deleted-user-meta">
            <span class="deleted-user-role">${roleLabels[user.role] || user.role}</span>
            <span class="deleted-user-countdown ${isUrgent ? 'urgent' : ''}">${countdownText}</span>
          </div>
          <button type="button" class="btn-restore" onclick="window.restoreUser(${user.id}, '${escapeHtml(user.name || user.email)}')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            Restaurar
          </button>
        </div>
      `;
    }).join('');
  }

  async function restoreUser(userId: number, userName: string): Promise<void> {
    const confirmed = await showConfirm({
      title: 'Restaurar usuário',
      message: `Deseja restaurar o usuário "${userName}"? Ele voltará a ter acesso ao sistema.`,
      confirmText: 'Restaurar',
      variant: 'default'
    });

    if (!confirmed) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/admin/users/${userId}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        showToast(data.error || 'Erro ao restaurar usuário', 'error');
        return;
      }

      showToast('Usuário restaurado com sucesso!', 'success');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao restaurar usuário:', error);
      showToast('Erro ao restaurar usuário', 'error');
    }
  }

  function toggleDeletedUsersSection(): void {
    const section = document.getElementById('deleted-users-section');
    if (section) section.classList.toggle('expanded');
  }

  (window as Window).restoreUser = restoreUser;

  function updateUserStats(users: User[]): void {
    const stats = {
      active: users.filter(u => u.is_active).length,
      devs: users.filter(u => u.role === 'developer' && u.is_active).length,
      neverLogged: users.filter(u => !u.last_login).length,
      inactive: users.filter(u => !u.is_active).length
    };

    const elActive = document.getElementById('user-stat-active');
    const elDevs = document.getElementById('user-stat-devs');
    const elNeverLogged = document.getElementById('user-stat-neverlogged');
    const elInactive = document.getElementById('user-stat-inactive');

    if (elActive) elActive.textContent = String(stats.active);
    if (elDevs) elDevs.textContent = String(stats.devs);
    if (elNeverLogged) elNeverLogged.textContent = String(stats.neverLogged);
    if (elInactive) elInactive.textContent = String(stats.inactive);
  }

  function renderUserTable(users: User[]): void {
    const tableBody = document.getElementById('user-table-body');
    if (!tableBody) return;

    if (!users.length) {
      tableBody.innerHTML = `
        <tr class="empty-state">
          <td colspan="7">
            <div class="empty-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <div class="empty-title">Nenhum usuário encontrado</div>
              <div class="empty-desc">Os usuários cadastrados aparecerão aqui.</div>
            </div>
          </td>
        </tr>
      `;
      return;
    }

    const roleLabels: Record<string, string> = {
      admin: 'Administrador',
      owner: 'Owner',
      developer: 'Desenvolvedor',
      user: 'Usuário'
    };

    tableBody.innerHTML = users.map(user => {
      const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const lastLogin = user.last_login
        ? new Date(user.last_login).toLocaleDateString('pt-BR')
        : 'Nunca';

      return `
        <tr>
          <td>
            <div style="display: flex; align-items: center; gap: 10px;">
              <div class="user-avatar-table">${initials}</div>
              <span>${escapeHtml(fullName)}</span>
            </div>
          </td>
          <td>${escapeHtml(user.email)}</td>
          <td>
            <span class="role-badge role-${user.role}">${roleLabels[user.role] || user.role}</span>
          </td>
          <td>${escapeHtml(user.company_name || '—')}</td>
          <td>
            <span class="user-status ${user.is_active ? 'active' : 'inactive'}">
              <span class="user-status-dot"></span>
              ${user.is_active ? 'Ativo' : 'Inativo'}
            </span>
          </td>
          <td>${lastLogin}</td>
          <td>
            <div class="table-actions">
              <button class="action-btn" onclick="window.openUserModal(${user.id})" title="Editar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
              </button>
              ${user.id === currentUser?.id ? '' : `
              <button class="action-btn ${user.is_active ? 'warning' : ''}" onclick="window.toggleUserStatus(${user.id}, ${!user.is_active})" title="${user.is_active ? 'Desativar' : 'Ativar'}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  ${user.is_active
                    ? '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/>'
                    : '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/>'
                  }
                </svg>
              </button>
              <button class="action-btn danger" onclick="window.deleteUser(${user.id}, '${escapeHtml(user.name)}')" title="Deletar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/>
                  <path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
              `}
            </div>
          </td>
        </tr>
      `;
    }).join('');
  }

  async function loadCompaniesForSelect(): Promise<void> {
    const select = document.getElementById('user-company') as HTMLSelectElement;
    if (!select) return;

    try {
      const token = getAuthToken();
      const response = await fetch('/api/companies', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar empresas');

      const data = await response.json();
      const companiesList = data.companies || data || [];

      select.innerHTML = '<option value="">Selecione...</option>' +
        companiesList.map((c: { id: number; name: string }) =>
          `<option value="${c.id}">${escapeHtml(c.name)}</option>`
        ).join('');
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      select.innerHTML = '<option value="">Erro ao carregar</option>';
    }
  }

  function initUserFilters(): void {
    const searchInput = document.getElementById('user-filter-search') as HTMLInputElement;
    const roleSelect = document.getElementById('user-filter-role') as HTMLSelectElement;
    const statusSelect = document.getElementById('user-filter-status') as HTMLSelectElement;

    const applyFilters = (): void => {
      const search = searchInput?.value?.toLowerCase() || '';
      const role = roleSelect?.value || '';
      const status = statusSelect?.value || '';

      const filtered = usersCache.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const matchesSearch = !search || fullName.includes(search) || user.email.toLowerCase().includes(search);
        const matchesRole = !role || user.role === role;
        const matchesStatus = !status ||
          (status === 'active' && user.is_active) ||
          (status === 'inactive' && !user.is_active);
        return matchesSearch && matchesRole && matchesStatus;
      });

      renderUserTable(filtered);
    };

    if (searchInput) {
      let timeout: ReturnType<typeof setTimeout>;
      searchInput.addEventListener('input', () => {
        clearTimeout(timeout);
        timeout = setTimeout(applyFilters, 300);
      });
    }

    if (roleSelect) roleSelect.addEventListener('change', applyFilters);
    if (statusSelect) statusSelect.addEventListener('change', applyFilters);
  }

  function initUserModal(): void {
    const btnNewUser = document.getElementById('btn-new-user');
    const userForm = document.getElementById('user-form') as HTMLFormElement;

    if (btnNewUser) {
      btnNewUser.onclick = () => openUserModal();
    }

    if (userForm && !userForm.dataset.initialized) {
      userForm.dataset.initialized = 'true';
      userForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await saveUser();
      });
    }
  }

  function openUserModal(userId?: number): void {
    const modal = document.getElementById('user-modal');
    const title = document.getElementById('user-modal-title');
    const submitText = document.getElementById('user-submit-text');
    const passwordField = document.getElementById('user-password') as HTMLInputElement;
    const passwordLabel = passwordField?.previousElementSibling;

    if (!modal) return;

    editingUserId = userId || null;

    // Reset form
    const form = document.getElementById('user-form') as HTMLFormElement;
    if (form) form.reset();

    // Restore company field visibility/required (may be hidden by btn-add-company-user flow)
    const companySelectReset = document.getElementById('user-company') as HTMLSelectElement | null;
    const companyFieldReset = companySelectReset?.closest('.field') as HTMLElement | null;
    if (companyFieldReset) companyFieldReset.hidden = false;
    if (companySelectReset) companySelectReset.required = true;

    if (userId) {
      // Edit mode
      if (title) title.textContent = 'Editar Usuário';
      if (submitText) submitText.textContent = 'Salvar Alterações';
      if (passwordField) passwordField.required = false;
      if (passwordLabel) passwordLabel.innerHTML = 'Nova Senha <span class="opt">(opcional)</span>';

      const user = usersCache.find(u => u.id === userId);
      if (user) {
        (document.getElementById('user-first-name') as HTMLInputElement).value = user.first_name || '';
        (document.getElementById('user-last-name') as HTMLInputElement).value = user.last_name || '';
        (document.getElementById('user-email') as HTMLInputElement).value = user.email;
        (document.getElementById('user-role') as HTMLSelectElement).value = user.role;
        (document.getElementById('user-company') as HTMLSelectElement).value = String(user.company_id || '');
        (document.getElementById('user-department') as HTMLInputElement).value = user.department || '';
      }
    } else {
      // Create mode
      if (title) title.textContent = 'Novo Usuário';
      if (submitText) submitText.textContent = 'Criar Usuário';
      if (passwordField) passwordField.required = true;
      if (passwordLabel) passwordLabel.innerHTML = 'Senha <span class="req">*</span>';
    }

    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');
    // Marca outros modais visíveis como inert pra não disparar warning de focus em aria-hidden
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
      if (m !== modal) m.setAttribute('inert', '');
    });
  }

  function closeUserModal(): void {
    const modal = document.getElementById('user-modal');
    if (modal) {
      blurWithin(modal);
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
    }
    editingUserId = null;
    // Restaura interatividade do modal abaixo (ex: company-modal)
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
      m.removeAttribute('inert');
    });
  }

  async function saveUser(): Promise<void> {
    const submitText = document.getElementById('user-submit-text');
    const submitLoading = document.getElementById('user-submit-loading');

    if (submitText) submitText.classList.add('hidden');
    if (submitLoading) submitLoading.classList.remove('hidden');

    try {
      const token = getAuthToken();
      const payload: Record<string, unknown> = {
        first_name: (document.getElementById('user-first-name') as HTMLInputElement).value,
        last_name: (document.getElementById('user-last-name') as HTMLInputElement).value,
        email: (document.getElementById('user-email') as HTMLInputElement).value,
        role: (document.getElementById('user-role') as HTMLSelectElement).value,
        company_id: parseInt((document.getElementById('user-company') as HTMLSelectElement).value) || null,
        department: (document.getElementById('user-department') as HTMLInputElement).value || null
      };

      const password = (document.getElementById('user-password') as HTMLInputElement).value;
      if (password) payload.password = password;

      const url = editingUserId ? `/api/admin/users/${editingUserId}` : '/api/admin/users';
      const method = editingUserId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Erro ao salvar usuário', 'error');
        return;
      }

      showToast(editingUserId ? 'Usuário atualizado!' : 'Usuário criado!', 'success');
      closeUserModal();
      await loadUsers();
      // Se o modal de empresa estiver aberto, recarrega a lista de usuários daquela empresa
      if (editingCompanyId) {
        await loadCompanyUsers(editingCompanyId);
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      showToast('Erro ao salvar usuário', 'error');
    } finally {
      if (submitText) submitText.classList.remove('hidden');
      if (submitLoading) submitLoading.classList.add('hidden');
    }
  }

  interface ConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'default' | 'danger' | 'warning';
  }

  function showConfirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = document.getElementById('confirm-modal');
      const icon = document.getElementById('confirm-modal-icon');
      const title = document.getElementById('confirm-modal-title');
      const message = document.getElementById('confirm-modal-message');
      const btnOk = document.getElementById('confirm-modal-ok') as HTMLButtonElement;
      const btnCancel = document.getElementById('confirm-modal-cancel') as HTMLButtonElement;

      if (!modal || !icon || !title || !message || !btnOk || !btnCancel) {
        resolve(confirm(options.message));
        return;
      }

      title.textContent = options.title;
      message.textContent = options.message;
      btnOk.textContent = options.confirmText || 'Confirmar';
      btnCancel.textContent = options.cancelText || 'Cancelar';

      // Set variant
      icon.className = 'confirm-modal-icon';
      btnOk.className = 'btn';
      if (options.variant === 'danger') {
        icon.classList.add('danger');
        btnOk.classList.add('btn-danger');
        icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';
      } else if (options.variant === 'warning') {
        icon.classList.add('warning');
        btnOk.classList.add('btn-primary');
        icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
      } else {
        btnOk.classList.add('btn-primary');
        icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
      }

      showModalSafely(modal);

      const cleanup = (): void => {
        hideModalSafely(modal);
        btnOk.onclick = null;
        btnCancel.onclick = null;
      };

      btnOk.onclick = (): void => {
        cleanup();
        resolve(true);
      };

      btnCancel.onclick = (): void => {
        cleanup();
        resolve(false);
      };
    });
  }

  (window as Window).showConfirm = showConfirm;

  async function toggleUserStatus(userId: number, activate: boolean): Promise<void> {
    const user = usersCache.find(u => u.id === userId);
    const userName = user?.name || 'este usuário';

    const confirmed = await showConfirm({
      title: activate ? 'Ativar usuário' : 'Desativar usuário',
      message: activate
        ? `Deseja ativar o usuário "${userName}"? Ele poderá fazer login novamente.`
        : `Deseja desativar o usuário "${userName}"? Ele não conseguirá mais fazer login.`,
      confirmText: activate ? 'Ativar' : 'Desativar',
      variant: activate ? 'default' : 'warning'
    });

    if (!confirmed) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: activate })
      });

      if (!response.ok) {
        const data = await response.json();
        showToast(data.error || 'Erro ao alterar status', 'error');
        return;
      }

      showToast(activate ? 'Usuário ativado!' : 'Usuário desativado!', 'success');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showToast('Erro ao alterar status', 'error');
    }
  }

  async function deleteUser(userId: number, userName: string): Promise<void> {
    const confirmed = await showConfirm({
      title: 'Deletar usuário',
      message: `Deseja deletar o usuário "${userName}"? Ele ficará na lixeira por 30 dias antes de ser removido permanentemente.`,
      confirmText: 'Deletar',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        showToast(data.error || 'Erro ao deletar usuário', 'error');
        return;
      }

      showToast('Usuário deletado com sucesso!', 'success');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      showToast('Erro ao deletar usuário', 'error');
    }
  }

  // Expose user functions globally
  (window as Window).openUserModal = openUserModal;
  (window as Window).closeUserModal = closeUserModal;
  (window as Window).toggleUserStatus = toggleUserStatus;
  (window as Window).deleteUser = deleteUser;

  // ─── Company Management ───────────────────────────────────────────────────
  interface CompanyData {
    id?: number;
    name: string;
    slug?: string;
    domain: string | null;
    plan: string;
    max_users: number | null;
    allowed_features: {
      incidents: boolean;
      users: boolean;
      developers: boolean;
      trello_integration: boolean;
    };
    allowed_roles?: {
      user: boolean;
      developer: boolean;
      admin: boolean;
    };
    is_active?: boolean | number;
  }

  interface ProductData {
    id?: number;
    company_id?: number;
    name: string;
    code: string;
    color: string | null;
    is_active?: boolean | number;
  }

  let companiesCache: CompanyData[] = [];
  let editingCompanyId: number | null = null;
  let tempProducts: ProductData[] = [];

  async function loadCompaniesView(): Promise<void> {
    const tableBody = document.getElementById('company-table-body');
    if (!tableBody) return;

    tableBody.innerHTML = '<tr><td colspan="7" class="loading-row">Carregando empresas...</td></tr>';

    try {
      const response = await authenticatedFetch('/api/admin/companies');
      if (!response) return;

      const data = await response.json() as { success: boolean; companies: CompanyData[] };
      if (!data.success) {
        tableBody.innerHTML = '<tr><td colspan="7" class="empty-row">Erro ao carregar empresas</td></tr>';
        return;
      }

      companiesCache = data.companies || [];

      if (companiesCache.length === 0) {
        tableBody.innerHTML = '<tr><td colspan="6" class="empty-row">Nenhuma empresa cadastrada</td></tr>';
        return;
      }

      tableBody.innerHTML = companiesCache.map(company => {
        const isActive = company.is_active === 1 || company.is_active === true;
        return `
          <tr>
            <td>
              <div class="company-name-cell">
                <strong>${escapeHtml(company.name)}</strong>
                ${company.slug ? `<span class="company-slug">${escapeHtml(company.slug)}</span>` : ''}
              </div>
            </td>
            <td>${company.domain || '—'}</td>
            <td>${(company as CompanyData & { products_count?: number }).products_count ?? 0}</td>
            <td>${(company as CompanyData & { users_count?: number }).users_count ?? 0}${company.max_users ? ` / ${company.max_users}` : ''}</td>
            <td>
              <span class="company-status-badge ${isActive ? 'is-active' : 'is-inactive'}">
                <span class="company-status-dot"></span>
                ${isActive ? 'Ativa' : 'Inativa'}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-ghost" data-company-action="edit" data-company-id="${company.id}" title="Editar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button class="btn btn-sm btn-ghost" data-company-action="toggle" data-company-id="${company.id}" data-company-activate="${!isActive}" title="${isActive ? 'Desativar' : 'Ativar'}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none">${isActive ? '<path d="M18 6L6 18M6 6l12 12"/>' : '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'}</svg>
                </button>
              </div>
            </td>
          </tr>
        `;
      }).join('');
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      tableBody.innerHTML = '<tr><td colspan="6" class="empty-row">Erro ao carregar empresas</td></tr>';
    }
  }

  let tempCompanyUsers: Array<{ id: number; name: string; email: string; role: string }> = [];

  function toggleExpandableCard(cardId: string): void {
    const card = document.getElementById(cardId);
    if (card) {
      card.classList.toggle('expanded');
    }
  }

  function openCompanyModal(companyId?: number): void {
    const modal = document.getElementById('company-modal');
    const title = document.getElementById('company-modal-title');
    const form = document.getElementById('company-form') as HTMLFormElement;
    const companyUsersSection = document.getElementById('company-users-section');

    if (!modal) return;

    editingCompanyId = companyId || null;
    tempProducts = [];
    tempCompanyUsers = [];

    if (form) form.reset();

    // Reset role checkboxes
    (document.getElementById('role-user') as HTMLInputElement).checked = true;
    (document.getElementById('role-developer') as HTMLInputElement).checked = true;

    // Collapse all cards initially
    document.querySelectorAll('.expandable-card').forEach(card => card.classList.remove('expanded'));

    // Update counts
    updateProductsCount();
    updateUsersCount();

    if (companyId) {
      if (title) title.textContent = 'Editar Empresa';
      if (companyUsersSection) companyUsersSection.style.display = 'block';

      const company = companiesCache.find(c => c.id === companyId);
      if (company) {
        (document.getElementById('company-id') as HTMLInputElement).value = String(company.id);
        (document.getElementById('company-name') as HTMLInputElement).value = company.name;
        (document.getElementById('company-domain') as HTMLInputElement).value = company.domain || '';
        (document.getElementById('company-max-users') as HTMLInputElement).value = company.max_users ? String(company.max_users) : '';

        if (company.allowed_roles) {
          (document.getElementById('role-user') as HTMLInputElement).checked = company.allowed_roles.user !== false;
          (document.getElementById('role-developer') as HTMLInputElement).checked = company.allowed_roles.developer !== false;
        } else if (company.allowed_features) {
          (document.getElementById('role-developer') as HTMLInputElement).checked = company.allowed_features.developers;
        }

        // Load products and users
        void loadCompanyProducts(companyId);
        void loadCompanyUsers(companyId);
      }
    } else {
      if (title) title.textContent = 'Nova Empresa';
      if (companyUsersSection) companyUsersSection.style.display = 'none';
      renderProductsList();
      renderCompanyUsersList();
    }

    showModalSafely(modal);
  }

  function updateProductsCount(): void {
    const countEl = document.getElementById('products-count');
    if (countEl) countEl.textContent = String(tempProducts.length);
  }

  function updateUsersCount(): void {
    const countEl = document.getElementById('users-count');
    if (countEl) countEl.textContent = String(tempCompanyUsers.length);
  }

  async function loadCompanyUsers(companyId: number): Promise<void> {
    try {
      const response = await authenticatedFetch(`/api/admin/companies/${companyId}/users`);
      if (!response) return;

      const data = await response.json() as { success: boolean; users: Array<{ id: number; first_name: string; last_name: string; email: string; role: string }> };
      if (data.success) {
        tempCompanyUsers = (data.users || []).map(u => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`,
          email: u.email,
          role: u.role
        }));
        renderCompanyUsersList();
        updateUsersCount();
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  }

  function renderCompanyUsersList(): void {
    const container = document.getElementById('company-users-list');
    if (!container) return;

    if (tempCompanyUsers.length === 0) {
      container.innerHTML = '<div class="empty-users">Nenhum usuário cadastrado nesta empresa.</div>';
      return;
    }

    const roleLabels: Record<string, string> = {
      user: 'Usuário',
      developer: 'Desenvolvedor',
      owner: 'Owner'
    };

    container.innerHTML = tempCompanyUsers.map(user => {
      const initials = user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
      const isActive = (user as typeof user & { is_active?: boolean | number }).is_active !== false && (user as typeof user & { is_active?: boolean | number }).is_active !== 0;
      return `
        <div class="company-user-item">
          <div class="company-user-avatar">${initials}</div>
          <div class="company-user-info">
            <div class="company-user-name">${escapeHtml(user.name)}</div>
            <div class="company-user-email">${escapeHtml(user.email)}</div>
          </div>
          <span class="company-user-role">${roleLabels[user.role] || user.role}</span>
          <button type="button" class="btn btn-sm btn-ghost" data-cuser-action="edit" data-cuser-id="${user.id}" title="Editar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button type="button" class="btn btn-sm btn-ghost" data-cuser-action="toggle" data-cuser-id="${user.id}" data-cuser-activate="${!isActive}" title="${isActive ? 'Inativar' : 'Ativar'}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none">${isActive ? '<path d="M18 6L6 18M6 6l12 12"/>' : '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'}</svg>
          </button>
          <button type="button" class="btn btn-sm btn-ghost" data-cuser-action="delete" data-cuser-id="${user.id}" title="Excluir">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
          </button>
        </div>
      `;
    }).join('');

    container.querySelectorAll<HTMLElement>('[data-cuser-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.cuserAction;
        const id = Number(btn.dataset.cuserId);
        if (!id) return;
        if (action === 'edit') {
          initUserModal();
          void openCompanyUserEdit(id);
        } else if (action === 'toggle') {
          const activate = btn.dataset.cuserActivate === 'true';
          void toggleUserStatus(id, activate);
        } else if (action === 'delete') {
          void deleteCompanyUser(id);
        }
      });
    });
  }

  async function openCompanyUserEdit(userId: number): Promise<void> {
    const res = await authenticatedFetch(`/api/admin/users/${userId}`);
    if (!res || !res.ok) {
      showToast('Erro ao carregar usuário', 'error');
      return;
    }
    const data = await res.json() as { success?: boolean; user?: { id: number; first_name: string; last_name: string; email: string; role: string; company_id: number | null; department: string | null } };
    if (!data.user) {
      showToast('Usuário não encontrado', 'error');
      return;
    }
    // Garante que está no cache pra openUserModal popular os campos
    usersCache = usersCache.filter(u => u.id !== data.user!.id);
    usersCache.push(data.user as User);
    openUserModal(userId);

    // Esconde Empresa porque já estamos no contexto da empresa
    const companySelect = document.getElementById('user-company') as HTMLSelectElement | null;
    const companyField = companySelect?.closest('.field') as HTMLElement | null;
    if (companySelect && data.user.company_id) {
      const idStr = String(data.user.company_id);
      if (!companySelect.querySelector(`option[value="${idStr}"]`)) {
        const opt = document.createElement('option');
        opt.value = idStr;
        opt.textContent = 'Empresa atual';
        companySelect.appendChild(opt);
      }
      companySelect.value = idStr;
      companySelect.required = false;
      if (companyField) companyField.hidden = true;
    }
  }

  async function toggleUserStatus(id: number, activate: boolean): Promise<void> {
    const res = await authenticatedFetch(`/api/admin/users/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: activate }),
    });
    if (!res) return;
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      showToast(err.error || 'Erro ao alterar status', 'error');
      return;
    }
    showToast(activate ? 'Usuário ativado' : 'Usuário inativado', 'success');
    if (editingCompanyId) await loadCompanyUsers(editingCompanyId);
  }

  async function deleteCompanyUser(id: number): Promise<void> {
    if (!window.confirm('Excluir este usuário? Ele poderá ser restaurado depois.')) return;
    const res = await authenticatedFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
    if (!res) return;
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      showToast(err.error || 'Erro ao excluir usuário', 'error');
      return;
    }
    showToast('Usuário excluído', 'success');
    if (editingCompanyId) await loadCompanyUsers(editingCompanyId);
  }

  function closeCompanyModal(): void {
    const modal = document.getElementById('company-modal');
    if (modal) {
      blurWithin(modal);
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
    }
    editingCompanyId = null;
    tempProducts = [];
  }

  async function loadCompanyProducts(companyId: number): Promise<void> {
    try {
      const response = await authenticatedFetch(`/api/admin/companies/${companyId}/products`);
      if (!response) return;

      const data = await response.json() as { success: boolean; products: ProductData[] };
      if (data.success) {
        tempProducts = data.products || [];
        renderProductsList();
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  }

  function renderProductsList(): void {
    const container = document.getElementById('products-list');
    if (!container) return;

    updateProductsCount();

    if (tempProducts.length === 0) {
      container.innerHTML = '<div class="empty-products">Nenhum produto cadastrado. Adicione produtos para categorizar os incidentes.</div>';
      return;
    }

    container.innerHTML = tempProducts.map((product, index) => {
      const p = product as ProductData & { id?: number };
      const isEditing = editingProductIndex === index;
      if (isEditing) {
        return `
          <div class="product-item product-item-editing" data-index="${index}">
            <div class="product-color" style="background-color: ${p.color || '#6b7280'}"></div>
            <input type="text" class="product-edit-name" value="${escapeHtml(p.name)}" placeholder="Nome" />
            <input type="text" class="product-edit-code" value="${escapeHtml(p.code)}" placeholder="Código" maxlength="10" style="width:90px" />
            <button type="button" class="btn btn-sm btn-primary" data-product-action="save" data-index="${index}">Salvar</button>
            <button type="button" class="btn btn-sm btn-ghost" data-product-action="cancel-edit" data-index="${index}">Cancelar</button>
          </div>
        `;
      }
      return `
        <div class="product-item" data-index="${index}">
          <div class="product-color" style="background-color: ${p.color || '#6b7280'}"></div>
          <div class="product-info">
            <span class="product-name">${escapeHtml(p.name)}</span>
            <span class="product-code">${escapeHtml(p.code)}</span>
          </div>
          <button type="button" class="btn btn-sm btn-ghost" data-product-action="edit" data-index="${index}" title="Editar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button type="button" class="btn btn-sm btn-ghost product-remove" data-product-action="delete" data-index="${index}" title="Remover">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      `;
    }).join('');

    container.querySelectorAll<HTMLElement>('[data-product-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.productAction;
        const idx = Number(btn.dataset.index);
        if (action === 'edit') startEditProduct(idx);
        else if (action === 'cancel-edit') cancelEditProduct();
        else if (action === 'save') saveEditProduct(idx);
        else if (action === 'delete') void deleteProductAt(idx);
      });
    });
  }

  let editingProductIndex: number | null = null;

  function startEditProduct(index: number): void {
    editingProductIndex = index;
    renderProductsList();
  }

  function cancelEditProduct(): void {
    editingProductIndex = null;
    renderProductsList();
  }

  async function saveEditProduct(index: number): Promise<void> {
    const container = document.getElementById('products-list');
    if (!container) return;
    const item = container.querySelector(`[data-index="${index}"]`);
    if (!item) return;
    const nameInput = item.querySelector<HTMLInputElement>('.product-edit-name');
    const codeInput = item.querySelector<HTMLInputElement>('.product-edit-code');
    const name = nameInput?.value.trim() || '';
    const code = codeInput?.value.trim().toUpperCase() || '';
    if (!name) { showToast('Nome obrigatório', 'error'); return; }
    if (code.length < 2 || code.length > 10) { showToast('Código entre 2 e 10 caracteres', 'error'); return; }

    const product = tempProducts[index] as ProductData & { id?: number };
    if (product.id) {
      const res = await authenticatedFetch(`/api/admin/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, code }),
      });
      if (!res || !res.ok) {
        const err = res ? await res.json().catch(() => ({})) as { error?: string } : {};
        showToast(err.error || 'Erro ao atualizar produto', 'error');
        return;
      }
    }
    product.name = name;
    product.code = code;
    editingProductIndex = null;
    renderProductsList();
    showToast('Produto atualizado', 'success');
  }

  async function deleteProductAt(index: number): Promise<void> {
    const product = tempProducts[index] as ProductData & { id?: number };
    if (!product.id) {
      tempProducts.splice(index, 1);
      renderProductsList();
      return;
    }
    const doDelete = async (force: boolean): Promise<boolean> => {
      const url = `/api/admin/products/${product.id}${force ? '?force=true' : ''}`;
      const res = await authenticatedFetch(url, { method: 'DELETE' });
      if (!res) return false;
      const data = await res.json().catch(() => ({})) as { success?: boolean; code?: string; stats?: { open: number; history: number }; error?: string };
      if (res.ok && data.success) return true;
      if (data.code === 'HAS_OPEN_INCIDENTS') {
        showToast(`Não é possível remover: ${data.stats?.open} incidente(s) em aberto.`, 'error');
        return false;
      }
      if (data.code === 'HAS_HISTORY_INCIDENTS') {
        const confirmMsg = `Este produto tem ${data.stats?.history} incidente(s) no histórico (resolvidos/fechados). Eles serão apagados junto. Confirmar?`;
        if (window.confirm(confirmMsg)) {
          return doDelete(true);
        }
        return false;
      }
      showToast(data.error || 'Erro ao remover produto', 'error');
      return false;
    };
    const ok = await doDelete(false);
    if (ok) {
      tempProducts.splice(index, 1);
      renderProductsList();
      showToast('Produto removido', 'success');
    }
  }

  function addProduct(): void {
    const nameInput = document.getElementById('new-product-name') as HTMLInputElement;
    const codeInput = document.getElementById('new-product-code') as HTMLInputElement;
    const colorInput = document.getElementById('new-product-color') as HTMLInputElement;

    const name = nameInput?.value.trim();
    const code = codeInput?.value.trim().toUpperCase();
    const color = colorInput?.value || '#3b82f6';

    if (!name) {
      showToast('Informe o nome do produto', 'error');
      nameInput?.focus();
      return;
    }

    if (!code || code.length < 2 || code.length > 5) {
      showToast('Código deve ter entre 2 e 5 caracteres', 'error');
      codeInput?.focus();
      return;
    }

    tempProducts.push({ name, code, color });
    renderProductsList();

    // Limpar campos
    if (nameInput) nameInput.value = '';
    if (codeInput) codeInput.value = '';
    if (colorInput) colorInput.value = '#3b82f6';
    nameInput?.focus();
  }

  function removeProduct(index: number): void {
    tempProducts.splice(index, 1);
    renderProductsList();
  }

  async function saveCompany(): Promise<void> {
    const saveBtn = document.getElementById('company-modal-save');
    if (saveBtn) saveBtn.setAttribute('disabled', 'true');

    try {
      const payload: CompanyData = {
        name: (document.getElementById('company-name') as HTMLInputElement).value,
        domain: (document.getElementById('company-domain') as HTMLInputElement).value || null,
        plan: 'basic',
        max_users: parseInt((document.getElementById('company-max-users') as HTMLInputElement).value) || null,
        allowed_features: {
          incidents: true,
          users: true,
          developers: (document.getElementById('role-developer') as HTMLInputElement).checked,
          trello_integration: false
        },
        allowed_roles: {
          user: (document.getElementById('role-user') as HTMLInputElement).checked,
          developer: (document.getElementById('role-developer') as HTMLInputElement).checked
        }
      };

      const url = editingCompanyId ? `/api/admin/companies/${editingCompanyId}` : '/api/admin/companies';
      const method = editingCompanyId ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response) return;

      const data = await response.json() as { success: boolean; error?: string; company?: CompanyData };

      if (!response.ok || !data.success) {
        showToast(data.error || 'Erro ao salvar empresa', 'error');
        return;
      }

      // Save new products (those without id) for both create and edit — em paralelo
      const companyId = editingCompanyId || data.company?.id;
      if (companyId) {
        const newProducts = tempProducts.filter(p => !(p as ProductData & { id?: number }).id);
        await Promise.all(newProducts.map(async product => {
          const productRes = await authenticatedFetch(`/api/admin/companies/${companyId}/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(product),
          });
          if (productRes && !productRes.ok) {
            const errData = await productRes.json().catch(() => ({})) as { error?: string };
            showToast(errData.error || `Erro ao salvar produto "${product.name}"`, 'error');
          }
        }));
      }

      showToast(editingCompanyId ? 'Empresa atualizada!' : 'Empresa criada!', 'success');
      closeCompanyModal();
      await loadCompaniesView();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      showToast('Erro ao salvar empresa', 'error');
    } finally {
      if (saveBtn) saveBtn.removeAttribute('disabled');
    }
  }

  function editCompany(companyId: number): void {
    openCompanyModal(companyId);
  }

  async function toggleCompanyStatus(companyId: number, activate: boolean): Promise<void> {
    try {
      const response = await authenticatedFetch(`/api/admin/companies/${companyId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: activate })
      });

      if (!response) return;

      const data = await response.json() as { success: boolean; error?: string };

      if (!response.ok || !data.success) {
        showToast(data.error || 'Erro ao alterar status', 'error');
        return;
      }

      showToast(activate ? 'Empresa ativada!' : 'Empresa desativada!', 'success');
      await loadCompaniesView();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showToast('Erro ao alterar status', 'error');
    }
  }

  function initCompanyModal(): void {
    const btnNew = document.getElementById('btn-new-company');
    const btnClose = document.getElementById('company-modal-close');
    const btnCancel = document.getElementById('company-modal-cancel');
    const btnAddProduct = document.getElementById('btn-add-product');
    const form = document.getElementById('company-form') as HTMLFormElement;

    if (btnNew) btnNew.addEventListener('click', () => openCompanyModal());
    if (btnClose) btnClose.addEventListener('click', closeCompanyModal);
    if (btnCancel) btnCancel.addEventListener('click', closeCompanyModal);
    if (btnAddProduct) btnAddProduct.addEventListener('click', addProduct);

    document.querySelectorAll<HTMLElement>('[data-expand-target]').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.expandTarget;
        if (target) toggleExpandableCard(target);
      });
    });

    const btnAddCompanyUser = document.getElementById('btn-add-company-user');
    if (btnAddCompanyUser) {
      btnAddCompanyUser.addEventListener('click', () => {
        initUserModal();
        openUserModal();
        const companySelect = document.getElementById('user-company') as HTMLSelectElement | null;
        const companyField = companySelect?.closest('.field') as HTMLElement | null;
        if (companySelect && editingCompanyId) {
          const idStr = String(editingCompanyId);
          if (!companySelect.querySelector(`option[value="${idStr}"]`)) {
            const opt = document.createElement('option');
            opt.value = idStr;
            opt.textContent = 'Empresa atual';
            companySelect.appendChild(opt);
          }
          companySelect.value = idStr;
          companySelect.required = false;
          if (companyField) companyField.hidden = true;
        }
      });
    }

    const companiesTable = document.getElementById('company-table-body');
    if (companiesTable && !companiesTable.dataset.delegated) {
      companiesTable.dataset.delegated = 'true';
      companiesTable.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-company-action]');
        if (!btn) return;
        const action = btn.dataset.companyAction;
        const id = Number(btn.dataset.companyId);
        if (!id) return;
        if (action === 'edit') {
          openCompanyModal(id);
        } else if (action === 'toggle') {
          const activate = btn.dataset.companyActivate === 'true';
          void toggleCompanyStatus(id, activate);
        }
      });
    }

    if (form) {
      form.onsubmit = async (e) => {
        e.preventDefault();
        await saveCompany();
      };
    }
  }

  // Expose company functions globally
  (window as Window).openCompanyModal = openCompanyModal;
  (window as Window).closeCompanyModal = closeCompanyModal;
  (window as Window).editCompany = editCompany;
  (window as Window).toggleCompanyStatus = toggleCompanyStatus;
  (window as Window).removeProduct = removeProduct;
  (window as Window).toggleExpandableCard = toggleExpandableCard;

  // ─── Dashboard SSO Integration ────────────────────────────────────────────
  async function openDashboard(): Promise<void> {
    const btn = document.getElementById('btn-external-dashboard') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.style.opacity = '0.6';
    }

    try {
      const response = await authenticatedFetch('/api/dashboard/sso', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response) {
        showToast('Erro de autenticação', 'error');
        return;
      }

      const data = await response.json() as { success: boolean; url?: string; error?: string };

      if (!response.ok || !data.success) {
        showToast(data.error || 'Não foi possível acessar o Dashboard', 'error');
        return;
      }

      // Open dashboard in new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('[Dashboard] Erro:', error);
      showToast('Erro ao conectar com o Dashboard', 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.style.opacity = '1';
      }
    }
  }

  (window as Window).openDashboard = openDashboard;

  // ─── Kanban Board Helpers ─────────────────────────────────────────────────
  interface KanbanCard {
    id: string;
    name: string;
    description?: string;
    labels?: Array<{ id: string; name: string; color: string }>;
    dueDate?: string | null;
    members?: Array<{ fullName: string; avatarUrl?: string; initials: string }>;
    url?: string;
    lastActivity?: string;
  }

  function renderKanbanCard(card: KanbanCard, listId: string): string {
    const hasLabels = card.labels && card.labels.length > 0;
    const hasMembers = card.members && card.members.length > 0;
    const hasDueDate = card.dueDate;
    const hasDescription = card.description && card.description.trim().length > 0;

    // Format due date
    let dueDateDisplay = '';
    let dueDateClass = '';
    if (hasDueDate) {
      const due = new Date(card.dueDate!);
      const now = new Date();
      const isOverdue = due < now;
      const isDueSoon = !isOverdue && (due.getTime() - now.getTime()) < 24 * 60 * 60 * 1000;
      dueDateClass = isOverdue ? 'due-soon' : (isDueSoon ? 'due-soon' : '');
      dueDateDisplay = due.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    }

    // Store card data as JSON in data attribute for safe handling
    const cardDataJson = JSON.stringify({
      id: card.id,
      name: card.name,
      description: card.description || '',
      url: card.url || ''
    }).replace(/"/g, '&quot;');

    return `
      <div class="kanban-card"
           data-card-id="${card.id}"
           data-list-id="${listId}"
           data-card-info="${cardDataJson}"
           draggable="true">
        ${hasLabels ? `
          <div class="kanban-card-labels">
            ${card.labels!.map(label => `<div class="kanban-card-label ${label.color}" title="${escapeHtml(label.name || '')}"></div>`).join('')}
          </div>
        ` : ''}
        <div class="kanban-card-title">${escapeHtml(card.name)}</div>
        ${(hasDueDate || hasDescription || hasMembers) ? `
          <div class="kanban-card-footer">
            <div class="kanban-card-badges">
              ${hasDueDate ? `
                <span class="kanban-card-badge ${dueDateClass}">
                  <i data-lucide="clock" aria-hidden="true"></i>
                  ${dueDateDisplay}
                </span>
              ` : ''}
              ${hasDescription ? `
                <span class="kanban-card-badge">
                  <i data-lucide="align-left" aria-hidden="true"></i>
                </span>
              ` : ''}
            </div>
            ${hasMembers ? `
              <div class="kanban-card-members">
                ${card.members!.slice(0, 3).map(member => `
                  <div class="kanban-card-member" title="${escapeHtml(member.fullName)}">
                    ${member.avatarUrl
                      ? `<img src="${member.avatarUrl}/30.png" alt="${escapeHtml(member.initials)}">`
                      : member.initials
                    }
                  </div>
                `).join('')}
                ${card.members!.length > 3 ? `<div class="kanban-card-member">+${card.members!.length - 3}</div>` : ''}
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  function initKanbanCardInteractions(): void {
    const cards = document.querySelectorAll('.kanban-card') as NodeListOf<HTMLElement>;
    const columns = document.querySelectorAll('.kanban-column-cards') as NodeListOf<HTMLElement>;

    cards.forEach(card => {
      // Click to open card details
      card.addEventListener('click', () => {
        const cardInfo = card.dataset.cardInfo;
        if (cardInfo) {
          try {
            const data = JSON.parse(cardInfo) as { id: string; name: string; description: string; url: string };
            openTrelloCard(data.id, data.name, data.description, data.url);
          } catch (e) {
            console.error('Error parsing card info:', e);
          }
        }
      });

      // Drag start
      card.addEventListener('dragstart', (e: DragEvent) => {
        card.classList.add('dragging');
        e.dataTransfer?.setData('text/plain', card.dataset.cardId || '');
        e.dataTransfer?.setData('source-list', card.dataset.listId || '');
      });

      card.addEventListener('dragend', () => {
        card.classList.remove('dragging');
      });
    });

    columns.forEach(column => {
      column.addEventListener('dragover', (e: DragEvent) => {
        e.preventDefault();
        const dragging = document.querySelector('.kanban-card.dragging') as HTMLElement | null;
        if (!dragging) return;

        const afterElement = getDragAfterElement(column, e.clientY);
        if (afterElement) {
          column.insertBefore(dragging, afterElement);
        } else {
          column.appendChild(dragging);
        }
      });

      column.addEventListener('drop', async (e: DragEvent) => {
        e.preventDefault();
        const cardId = e.dataTransfer?.getData('text/plain');
        const sourceListId = e.dataTransfer?.getData('source-list');
        const targetListId = column.closest('.kanban-column')?.getAttribute('data-list-id');

        if (cardId && sourceListId && targetListId && sourceListId !== targetListId) {
          await moveCardToList(cardId, targetListId);
        }
      });
    });
  }

  function getDragAfterElement(container: HTMLElement, y: number): HTMLElement | null {
    const draggableElements = [...container.querySelectorAll('.kanban-card:not(.dragging)')] as HTMLElement[];

    return draggableElements.reduce<{ offset: number; element: HTMLElement | null }>((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      if (offset < 0 && offset > closest.offset) {
        return { offset, element: child };
      }
      return closest;
    }, { offset: Number.NEGATIVE_INFINITY, element: null }).element;
  }

  async function moveCardToList(cardId: string, newListId: string): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/trello/card/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId, listId: newListId })
      });

      if (!response) return;
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        showToast('Card movido com sucesso!', 'success');
        // Update the card's data attribute
        const card = document.querySelector(`[data-card-id="${cardId}"]`) as HTMLElement | null;
        if (card) {
          card.dataset.listId = newListId;
        }
      } else {
        showToast(data.error || 'Erro ao mover card', 'error');
        await loadTrelloDashboard(); // Refresh to restore original position
      }
    } catch {
      showToast('Erro ao mover card no Trello', 'error');
      await loadTrelloDashboard();
    }
  }

  // Simple Markdown parser for Trello descriptions
  function renderMarkdown(text: string): string {
    if (!text) return '';

    let html = escapeHtml(text);

    // Headers (### Header)
    html = html.replace(/^### (.+)$/gm, '<h4 class="md-h4">$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3 class="md-h3">$1</h3>');
    html = html.replace(/^# (.+)$/gm, '<h2 class="md-h2">$1</h2>');

    // Bold (**text** or __text__)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic (*text* or _text_)
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Strikethrough (~~text~~)
    html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

    // Inline code (`code`)
    html = html.replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');

    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="md-link">$1</a>');

    // Images ![alt](url) - show as link since we can't verify the image
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="md-image-link">📷 $1 (imagem)</a>');

    // Checklists - [ ] and - [x]
    html = html.replace(/^- \[ \] (.+)$/gm, '<div class="md-checklist"><input type="checkbox" disabled> <span>$1</span></div>');
    html = html.replace(/^- \[x\] (.+)$/gm, '<div class="md-checklist"><input type="checkbox" checked disabled> <span>$1</span></div>');

    // Unordered lists (- item)
    html = html.replace(/^- (.+)$/gm, '<li class="md-li">$1</li>');

    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li class="md-li">.*<\/li>\n?)+/g, (match) => `<ul class="md-ul">${match}</ul>`);

    // Horizontal rule (---)
    html = html.replace(/^---$/gm, '<hr class="md-hr">');

    // Line breaks (preserve double newlines as paragraphs)
    html = html.replace(/\n\n/g, '</p><p class="md-p">');
    html = html.replace(/\n/g, '<br>');

    // Wrap in paragraph if not already wrapped
    if (!html.startsWith('<h') && !html.startsWith('<ul') && !html.startsWith('<div')) {
      html = `<p class="md-p">${html}</p>`;
    }

    return html;
  }

  function openTrelloCard(cardId: string, cardName: string, cardDescription: string, cardUrl: string): void {
    // Create modal to show card details
    const existingModal = document.getElementById('trello-card-modal');
    if (existingModal) existingModal.remove();

    const modalOverlay = document.createElement('div');
    modalOverlay.id = 'trello-card-modal';
    modalOverlay.className = 'modal-overlay';
    modalOverlay.setAttribute('role', 'dialog');
    modalOverlay.setAttribute('aria-modal', 'true');
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) closeTrelloCardModal();
    };

    const renderedDescription = renderMarkdown(cardDescription);

    modalOverlay.innerHTML = `
      <div class="modal-content" style="max-width: 600px;">
        <div class="modal-header">
          <h2 class="modal-title">${escapeHtml(cardName)}</h2>
          <button class="modal-close" onclick="closeTrelloCardModal()" aria-label="Fechar">
            <i data-lucide="x" aria-hidden="true"></i>
          </button>
        </div>
        <div class="modal-body">
          ${cardDescription ? `
            <div class="card-description markdown-content">${renderedDescription}</div>
          ` : '<p class="text-brand-copy-muted">Este card não possui descrição.</p>'}
          ${cardUrl ? `
            <div class="mt-4">
              <a href="${escapeHtml(cardUrl)}" target="_blank" rel="noopener noreferrer" class="trello-link">
                <i data-lucide="external-link" aria-hidden="true"></i>
                Abrir no Trello
              </a>
            </div>
          ` : ''}
        </div>
      </div>
    `;

    document.body.appendChild(modalOverlay);
    // Trigger reflow for animation
    void modalOverlay.offsetWidth;
    modalOverlay.classList.add('is-open');
    document.body.classList.add('modal-open');
    refreshIcons();
  }

  function closeTrelloCardModal(): void {
    const modal = document.getElementById('trello-card-modal');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.classList.remove('modal-open');
      setTimeout(() => modal.remove(), 200);
    }
  }

  // Expose to window for onclick handlers
  (window as Window & { openTrelloCard?: typeof openTrelloCard; closeTrelloCardModal?: typeof closeTrelloCardModal }).openTrelloCard = openTrelloCard;
  (window as Window & { closeTrelloCardModal?: typeof closeTrelloCardModal }).closeTrelloCardModal = closeTrelloCardModal;

  function renderKanbanSkeleton(columns: number = 4, cardsPerColumn: number = 4): string {
    return Array.from({ length: columns }, () => `
      <div class="kanban-column kanban-column-skeleton">
        <div class="kanban-column-header">
          <span class="skeleton-text" style="width: 80px; height: 14px;"></span>
          <span class="skeleton-text" style="width: 24px; height: 14px; border-radius: 10px;"></span>
        </div>
        <div class="kanban-column-cards">
          ${Array.from({ length: cardsPerColumn }, () => `
            <div class="kanban-card-skeleton">
              <div class="skeleton-text" style="width: 100%; height: 12px; margin-bottom: 6px;"></div>
              <div class="skeleton-text" style="width: 70%; height: 12px;"></div>
            </div>
          `).join('')}
        </div>
      </div>
    `).join('');
  }

  async function loadTrelloDashboard(): Promise<void> {
    const totalCards = document.getElementById('trello-total-cards');
    const activeLists = document.getElementById('trello-active-lists');
    const incidentsCreated = document.getElementById('trello-incidents-created');
    const lastSync = document.getElementById('trello-last-sync');
    const boardContent = document.getElementById('trello-board-content');
    const boardWrapper = document.getElementById('trello-board-wrapper');
    const notConfigured = document.getElementById('trello-not-configured');

    // Show skeleton loading state
    if (boardContent && boardWrapper) {
      notConfigured?.classList.add('hidden');
      boardWrapper.classList.remove('hidden');
      boardContent.innerHTML = renderKanbanSkeleton(4, 4);
    }

    try {
      const response = await authenticatedFetch('/api/trello/integration/dashboard');
      if (!response) return;

      const result = await response.json() as {
        success: boolean;
        error?: string;
        data?: {
          totalCards?: number;
          activeLists?: number;
          incidentsCreated?: number;
          lastSync?: string;
          lists?: Array<{
            id: string;
            name: string;
            cards: Array<KanbanCard>
          }>;
          boardName?: string;
        };
      };

      const notConfigured = document.getElementById('trello-not-configured');
      const boardWrapper = document.getElementById('trello-board-wrapper');

      if (!result.success) {
        if (totalCards) totalCards.textContent = '—';
        if (activeLists) activeLists.textContent = '—';
        if (incidentsCreated) incidentsCreated.textContent = '—';
        if (lastSync) lastSync.textContent = '—';
        // Show "not configured" state
        notConfigured?.classList.remove('hidden');
        boardWrapper?.classList.add('hidden');
        refreshIcons();
        return;
      }

      // Show the kanban board
      notConfigured?.classList.add('hidden');
      boardWrapper?.classList.remove('hidden');

      const data = result.data;
      if (!data) return;

      // Update stats
      if (totalCards) totalCards.textContent = String(data.totalCards ?? 0);
      if (activeLists) activeLists.textContent = String(data.activeLists ?? 0);
      if (incidentsCreated) incidentsCreated.textContent = String(data.incidentsCreated ?? 0);
      if (lastSync) {
        lastSync.textContent = data.lastSync
          ? new Date(data.lastSync).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
          : 'Nunca';
      }

      // Update board title
      const boardTitle = document.getElementById('trello-board-title');
      if (boardTitle && data.boardName) {
        boardTitle.textContent = `Board: ${data.boardName}`;
      }

      // Render Kanban board
      if (boardContent && data.lists && data.lists.length > 0) {
        boardContent.innerHTML = data.lists.map(list => `
          <div class="kanban-column" data-list-id="${list.id}">
            <div class="kanban-column-header">
              <span>${escapeHtml(list.name)}</span>
              <span class="count">${list.cards?.length || 0}</span>
            </div>
            <div class="kanban-column-cards">
              ${(list.cards || []).map(card => renderKanbanCard(card, list.id)).join('')}
            </div>
          </div>
        `).join('');

        // Initialize card interactions
        initKanbanCardInteractions();
        refreshIcons();
      } else if (boardContent) {
        boardContent.innerHTML = `
          <div class="kanban-empty">
            <i data-lucide="layout-grid" aria-hidden="true"></i>
            <p>Nenhuma lista encontrada no board</p>
            <a href="#trello-integration" class="btn btn-primary mt-4" style="color: white;">Configurar Integração</a>
          </div>
        `;
        refreshIcons();
      }

    } catch (error) {
      console.error('Erro ao carregar dashboard Trello:', error);
      if (boardContent) {
        boardContent.innerHTML = '<p class="text-brand-copy-muted text-center">Erro ao carregar dados do Trello</p>';
      }
    }
  }

  async function syncTrelloCards(): Promise<void> {
    const boardContent = document.getElementById('trello-board-content');
    if (boardContent) {
      boardContent.innerHTML = '<p class="text-brand-copy-muted text-center">Sincronizando...</p>';
    }

    try {
      const response = await authenticatedFetch('/api/trello/integration/sync', {
        method: 'POST'
      });
      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string; error?: string; synced?: number };

      if (data.success) {
        showToast(data.message || `Sincronização concluída!`, 'success');
        await loadTrelloDashboard();
      } else {
        showToast(data.error || 'Erro ao sincronizar', 'error');
        if (boardContent) {
          boardContent.innerHTML = `<p class="text-brand-copy-muted text-center">${data.error || 'Erro ao sincronizar'}</p>`;
        }
      }
    } catch {
      showToast('Erro ao sincronizar com Trello', 'error');
    }
  }

  async function loadTrelloIntegration(): Promise<void> {
    // Check current connection status
    await checkTrelloConnectionStatus();
    // Load saved settings
    await loadTrelloSettings();
  }

  async function checkTrelloConnectionStatus(): Promise<void> {
    const badge = document.getElementById('trello-connection-badge');
    const disconnectedState = document.getElementById('trello-status-disconnected');
    const connectedState = document.getElementById('trello-status-connected');
    const errorState = document.getElementById('trello-status-error');
    const boardName = document.getElementById('trello-board-name');
    const boardIdDisplay = document.getElementById('trello-board-id-display');
    const connectedSince = document.getElementById('trello-connected-since');

    // Hide all states initially (using is-active class)
    disconnectedState?.classList.remove('is-active');
    connectedState?.classList.remove('is-active');
    errorState?.classList.remove('is-active');

    if (badge) {
      badge.textContent = 'Verificando...';
      badge.className = 'badge badge-open';
    }

    try {
      const response = await authenticatedFetch('/api/trello/integration/status');
      if (!response) return;

      const data = await response.json() as {
        success: boolean;
        configured: boolean;
        incTrello?: { boardName?: string; boardId?: string; active?: boolean; connectedAt?: string };
        error?: string;
      };

      if (data.success && data.configured) {
        // Connected state
        if (badge) {
          badge.textContent = 'Conectado';
          badge.className = 'badge badge-resolved';
        }
        connectedState?.classList.add('is-active');
        if (boardName) boardName.textContent = data.incTrello?.boardName || 'Board conectado';
        if (boardIdDisplay) boardIdDisplay.textContent = data.incTrello?.boardId || '—';
        if (connectedSince) {
          const date = data.incTrello?.connectedAt ? new Date(data.incTrello.connectedAt).toLocaleString('pt-BR') : 'Esta sessão';
          connectedSince.textContent = `Conectado desde: ${date}`;
        }
        refreshIcons();
      } else {
        // Disconnected state
        if (badge) {
          badge.textContent = 'Não conectado';
          badge.className = 'badge badge-open';
        }
        disconnectedState?.classList.add('is-active');
        refreshIcons();
      }
    } catch {
      // Error state
      if (badge) {
        badge.textContent = 'Erro';
        badge.className = 'badge badge-open';
      }
      errorState?.classList.add('is-active');
      const errorMsg = document.getElementById('trello-error-message');
      if (errorMsg) errorMsg.textContent = 'Não foi possível verificar o status da conexão';
      if (typeof (window as Window).refreshIcons === 'function') {
        (window as Window).refreshIcons();
      }
    }
  }

  async function syncTrelloNow(): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/trello/integration/sync', {
        method: 'POST'
      });
      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string; error?: string; synced?: number };

      if (data.success) {
        showToast(data.message || `Sincronização concluída! ${data.synced || 0} cards sincronizados.`, 'success');
      } else {
        showToast(data.error || 'Erro ao sincronizar', 'error');
      }
    } catch {
      showToast('Erro ao sincronizar com Trello', 'error');
    }
  }

  async function disconnectTrello(): Promise<void> {
    if (!confirm('Tem certeza que deseja desconectar a integração com o Trello?')) {
      return;
    }

    try {
      const response = await authenticatedFetch('/api/trello/credentials', {
        method: 'DELETE'
      });
      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string };

      if (data.success) {
        showToast('Integração desconectada', 'success');
        await checkTrelloConnectionStatus();
      } else {
        showToast('Erro ao desconectar', 'error');
      }
    } catch {
      showToast('Erro ao desconectar integração', 'error');
    }
  }

  async function reconnectTrello(): Promise<void> {
    showToast('Tentando reconectar...', 'info');
    try {
      const response = await authenticatedFetch('/api/trello/test-connection', {
        method: 'POST',
        body: JSON.stringify({})
      });
      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string; error?: string };

      if (data.success) {
        showToast('Reconectado com sucesso!', 'success');
        await checkTrelloConnectionStatus();
      } else {
        showToast(data.error || 'Falha ao reconectar', 'error');
      }
    } catch {
      showToast('Erro ao reconectar', 'error');
    }
  }

  async function loadTrelloSettings(): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/trello/settings');
      if (!response) return;

      const data = await response.json() as {
        success: boolean;
        settings: { autoSync: boolean; createIncidents: boolean; syncComments: boolean };
      };

      if (data.success) {
        const autoSync = document.getElementById('trello-auto-sync') as HTMLInputElement;
        const createIncidents = document.getElementById('trello-create-incidents') as HTMLInputElement;
        const syncComments = document.getElementById('trello-sync-comments') as HTMLInputElement;

        if (autoSync) autoSync.checked = data.settings.autoSync;
        if (createIncidents) createIncidents.checked = data.settings.createIncidents;
        if (syncComments) syncComments.checked = data.settings.syncComments;
      }
    } catch {
      console.error('Erro ao carregar configurações do Trello');
    }
  }

  async function testTrelloConnection(): Promise<void> {
    const apiKey = (document.getElementById('trello-api-key') as HTMLInputElement)?.value.trim();
    const token = (document.getElementById('trello-token') as HTMLInputElement)?.value.trim();
    const boardId = (document.getElementById('trello-board-id') as HTMLInputElement)?.value.trim();

    const statusBadge = document.getElementById('trello-connection-status');
    const statusInfo = document.getElementById('trello-connection-info');

    if (statusBadge) {
      statusBadge.textContent = 'Testando...';
      statusBadge.className = 'badge badge-open';
    }
    if (statusInfo) {
      statusInfo.textContent = 'Verificando conexão...';
    }

    try {
      const response = await authenticatedFetch('/api/trello/test-connection', {
        method: 'POST',
        body: JSON.stringify({ apiKey: apiKey || undefined, token: token || undefined, boardId: boardId || undefined })
      });

      if (!response) return;

      const data = await response.json() as {
        success: boolean;
        message?: string;
        error?: string;
        board?: { name: string };
      };

      if (data.success) {
        if (statusBadge) {
          statusBadge.textContent = 'Conectado';
          statusBadge.className = 'badge badge-resolved';
        }
        if (statusInfo) {
          statusInfo.textContent = `Board: ${data.board?.name || 'Configurado'}`;
        }
        showToast(data.message || 'Conexão bem-sucedida!', 'success');
      } else {
        if (statusBadge) {
          statusBadge.textContent = 'Erro';
          statusBadge.className = 'badge badge-open';
        }
        if (statusInfo) {
          statusInfo.textContent = data.error || 'Falha na conexão';
        }
        showToast(data.error || 'Falha na conexão', 'error');
      }
    } catch {
      if (statusBadge) {
        statusBadge.textContent = 'Erro';
        statusBadge.className = 'badge badge-open';
      }
      if (statusInfo) {
        statusInfo.textContent = 'Erro ao testar conexão';
      }
      showToast('Erro ao testar conexão com o Trello', 'error');
    }
  }

  async function saveTrelloCredentials(): Promise<void> {
    const apiKeyInput = document.getElementById('trello-api-key') as HTMLInputElement | null;
    const tokenInput = document.getElementById('trello-token') as HTMLInputElement | null;
    const boardIdInput = document.getElementById('trello-board-id') as HTMLInputElement | null;

    const apiKey = apiKeyInput?.value?.trim() || '';
    const token = tokenInput?.value?.trim() || '';
    const boardId = boardIdInput?.value?.trim() || '';

    if (!apiKey || !token || !boardId) {
      showToast('Preencha todos os campos: API Key, Token e Board ID', 'warning');
      return;
    }

    try {
      const response = await authenticatedFetch('/api/trello/credentials', {
        method: 'POST',
        body: JSON.stringify({ apiKey, token, boardId })
      });

      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string; error?: string };

      if (data.success) {
        showToast(data.message || 'Credenciais salvas!', 'success');
        await checkTrelloConnectionStatus();
        // Clear form fields
        const apiKeyInput = document.getElementById('trello-api-key') as HTMLInputElement;
        const tokenInput = document.getElementById('trello-token') as HTMLInputElement;
        const boardIdInput = document.getElementById('trello-board-id') as HTMLInputElement;
        if (apiKeyInput) apiKeyInput.value = '';
        if (tokenInput) tokenInput.value = '';
        if (boardIdInput) boardIdInput.value = '';
      } else {
        showToast(data.error || 'Erro ao salvar credenciais', 'error');
      }
    } catch {
      showToast('Erro ao salvar credenciais', 'error');
    }
  }

  async function clearTrelloCredentials(): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/trello/credentials', {
        method: 'DELETE'
      });

      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string };

      if (data.success) {
        showToast(data.message || 'Credenciais removidas', 'success');
        await checkTrelloConnectionStatus();
      } else {
        showToast('Erro ao limpar credenciais', 'error');
      }
    } catch {
      showToast('Erro ao limpar credenciais', 'error');
    }
  }

  async function saveTrelloSettings(): Promise<void> {
    const autoSync = (document.getElementById('trello-auto-sync') as HTMLInputElement)?.checked;
    const createIncidents = (document.getElementById('trello-create-incidents') as HTMLInputElement)?.checked;
    const syncComments = (document.getElementById('trello-sync-comments') as HTMLInputElement)?.checked;

    try {
      const response = await authenticatedFetch('/api/trello/settings', {
        method: 'POST',
        body: JSON.stringify({ autoSync, createIncidents, syncComments })
      });

      if (!response) return;

      const data = await response.json() as { success: boolean; message?: string };

      if (data.success) {
        showToast(data.message || 'Configurações salvas!', 'success');
      } else {
        showToast('Erro ao salvar configurações', 'error');
      }
    } catch {
      showToast('Erro ao salvar configurações', 'error');
    }
  }

  async function loadGovernanceData(): Promise<void> {
    // TODO: Implement governance data loading
  }

  async function loadReportsData(): Promise<void> {
    // TODO: Implement reports loading
  }

  // ─── Incident Dashboard ─────────────────────────────────────────────────
  let dashboardFilter: 'active' | 'history' = 'active';
  let dashboardViewMode: 'list' | 'grid' = 'list';

  async function loadIncidentDashboard(): Promise<void> {
    await loadDashboardStats();
    await loadDashboardIncidents();
    initDashboardFilters();

    // New incident button
    const newIncidentBtn = document.getElementById('dash-new-incident-btn');
    if (newIncidentBtn) {
      newIncidentBtn.onclick = () => { window.location.hash = 'new'; };
    }
  }

  async function loadDashboardStats(): Promise<void> {
    try {
      const response = await fetch('/api/stats');
      if (!response.ok) return;

      const stats = await response.json() as IncidentStats;

      const total = Number(stats.total) || 0;
      const aguardando = Number(stats.abertos) || 0;
      const progresso = Number(stats.em_andamento) || 0;
      const resolvidos = Number(stats.resolvidos) || 0;
      const fechados = Number(stats.fechados) || 0;
      const altaPrioridade = Number(stats.criticos_ativos) || 0;

      const elTotal = document.getElementById('dash-total');
      const elAguardando = document.getElementById('dash-aguardando');
      const elProgresso = document.getElementById('dash-progresso');
      const elResolvidos = document.getElementById('dash-resolvidos');
      const elAltaPrioridade = document.getElementById('dash-alta-prioridade');
      const elActiveCount = document.getElementById('dash-active-count');
      const elHistoryCount = document.getElementById('dash-history-count');
      const elTabActiveCount = document.getElementById('dash-tab-active-count');
      const elTabHistoryCount = document.getElementById('dash-tab-history-count');

      if (elTotal) elTotal.textContent = String(total);
      if (elAguardando) elAguardando.textContent = String(aguardando);
      if (elProgresso) elProgresso.textContent = String(progresso);
      if (elResolvidos) elResolvidos.textContent = String(resolvidos);
      if (elAltaPrioridade) elAltaPrioridade.textContent = String(altaPrioridade);
      // Ativos = tudo que não é histórico (cobre em_analise/aguardando_validacao, que não têm card próprio).
      const ativos = total - resolvidos - fechados;
      if (elActiveCount) elActiveCount.textContent = String(ativos);
      if (elHistoryCount) elHistoryCount.textContent = String(resolvidos + fechados);
      if (elTabActiveCount) elTabActiveCount.textContent = String(ativos);
      if (elTabHistoryCount) elTabHistoryCount.textContent = String(resolvidos + fechados);
    } catch {
      console.error('Failed to load dashboard stats');
    }
  }

  async function loadDashboardIncidents(): Promise<void> {
    const listEl = document.getElementById('dashboard-incident-list');
    if (!listEl) return;

    try {
      const statusGroup = dashboardFilter === 'active' ? 'active' : 'history';
      const searchInput = document.getElementById('dash-filter-search') as HTMLInputElement;
      const portalSelect = document.getElementById('dash-filter-portal') as HTMLSelectElement;
      const critSelect = document.getElementById('dash-filter-criticality') as HTMLSelectElement;
      const periodSelect = document.getElementById('dash-filter-period') as HTMLSelectElement;

      const params = new URLSearchParams({
        status_group: statusGroup,
        limit: '50'
      });

      if (searchInput?.value) params.append('search', searchInput.value);
      if (portalSelect?.value) params.append('portal', portalSelect.value);
      if (critSelect?.value) params.append('criticality', critSelect.value);
      if (periodSelect?.value) params.append('period', periodSelect.value);

      // Tab counts are updated by loadDashboardStats (same /api/stats data)

      const response = await authenticatedFetch(`/api/incidents?${params}`);

      if (!response || !response.ok) {
        listEl.innerHTML = `
          <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div class="empty-title">Erro ao carregar</div>
            <div class="empty-desc">Não foi possível carregar os incidentes.</div>
          </div>
        `;
        return;
      }

      const result = await response.json() as { data: Incident[]; total: number };
      const incidents = result.data || [];

      if (!incidents.length) {
        listEl.innerHTML = `
          <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div class="empty-title">Nenhum incidente</div>
            <div class="empty-desc">${dashboardFilter === 'active' ? 'Não há incidentes ativos no momento.' : 'Nenhum incidente no histórico.'}</div>
          </div>
        `;
        return;
      }

      // Use same card pattern as incident list, with manage mode for click handler
      listEl.innerHTML = incidents.map(inc => renderIncidentCard(inc, { mode: 'manage' })).join('');
      refreshIcons();

    } catch {
      listEl.innerHTML = `
        <div class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <div class="empty-title">Erro ao carregar</div>
          <div class="empty-desc">Não foi possível carregar os incidentes.</div>
        </div>
      `;
    }
  }

  // Helpers a11y-safe pra mostrar/fechar modais .modal-overlay.show
  // (sempre setar aria-hidden=false ao mostrar e marcar inert nos modais abaixo)
  function showModalSafely(modal: HTMLElement): void {
    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => {
      if (m !== modal) m.setAttribute('inert', '');
    });
  }

  // Tira o foco de dentro do modal antes de aplicar aria-hidden=true.
  // Sem isso o browser bloqueia e loga "Blocked aria-hidden ... descendant retained focus".
  function blurWithin(modal: HTMLElement): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && modal.contains(active)) active.blur();
  }

  function hideModalSafely(modal: HTMLElement): void {
    blurWithin(modal);
    modal.classList.remove('show');
    modal.setAttribute('aria-hidden', 'true');
    document.querySelectorAll<HTMLElement>('.modal-overlay.show').forEach(m => m.removeAttribute('inert'));
  }

  function getCriticalityClass(criticality: string): string {
    const map: Record<string, string> = {
      'critica': 'critical',
      'alta': 'alta',
      'media': 'media',
      'baixa': 'baixa'
    };
    return map[criticality?.toLowerCase()] || 'baixa';
  }

  function getPriorityLabel(criticality: string): string {
    const map: Record<string, string> = {
      'critica': 'P1',
      'alta': 'P2',
      'media': 'P3',
      'baixa': 'P4'
    };
    return map[criticality?.toLowerCase()] || 'P4';
  }

  function getStatusBadge(status: string): string {
    const map: Record<string, { class: string; label: string }> = {
      'aberto': { class: 'badge-open', label: 'ABERTO' },
      'em_andamento': { class: 'badge-progress', label: 'EM ANDAMENTO' },
      'resolvido': { class: 'badge-resolved', label: 'RESOLVIDO' },
      'fechado': { class: 'badge-closed', label: 'FECHADO' }
    };
    const info = map[status?.toLowerCase()] || { class: 'badge-open', label: status?.toUpperCase() || 'ABERTO' };
    return `<span class="badge ${info.class}">${info.label}</span>`;
  }

  function formatDateShort(dateStr: string): string {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ', ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  }

  function initDashboardFilters(): void {
    // Tab filters - using dashboard-tabs pattern
    const toolbarTabs = document.querySelectorAll<HTMLElement>('.dashboard-toolbar .dashboard-tab');
    toolbarTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const filter = tab.dataset.filter as 'active' | 'history';
        if (filter) {
          dashboardFilter = filter;
          toolbarTabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');
          loadDashboardIncidents();
        }
      });
    });

    // Search filter
    const searchInput = document.getElementById('dash-filter-search') as HTMLInputElement;
    if (searchInput) {
      let searchTimeout: ReturnType<typeof setTimeout>;
      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => loadDashboardIncidents(), 300);
      });
    }

    // Portal filter
    const portalSelect = document.getElementById('dash-filter-portal') as HTMLSelectElement;
    if (portalSelect) {
      portalSelect.addEventListener('change', () => loadDashboardIncidents());
    }

    // Criticality filter
    const critSelect = document.getElementById('dash-filter-criticality') as HTMLSelectElement;
    if (critSelect) {
      critSelect.addEventListener('change', () => loadDashboardIncidents());
    }

    // Period filter
    const periodSelect = document.getElementById('dash-filter-period') as HTMLSelectElement;
    if (periodSelect) {
      periodSelect.addEventListener('change', () => loadDashboardIncidents());
    }

    // Clear filters button
    const clearBtn = document.getElementById('dash-filter-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        if (portalSelect) portalSelect.value = '';
        if (critSelect) critSelect.value = '';
        if (periodSelect) periodSelect.value = '';
        loadDashboardIncidents();
      });
    }
  }

  function startDashboardPolling(): void {
    stopDashboardPolling();
    dashboardPollingInterval = setInterval(() => {
      if (currentView === 'incident-dashboard') {
        loadDashboardStats();
        loadDashboardIncidents();
      }
    }, 60000);
  }

  function stopDashboardPolling(): void {
    if (dashboardPollingInterval) {
      clearInterval(dashboardPollingInterval);
      dashboardPollingInterval = null;
    }
  }

  // Modal utilities
  function getOpenModals(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>('.modal-overlay'))
      .filter(modal => modal.classList.contains('show') || modal.classList.contains('active'));
  }

  function closeTopModal(): void {
    const openModals = getOpenModals();
    const topModal = openModals[openModals.length - 1];
    if (!topModal) return;
    closeDialogModal(topModal);
  }

  function closeAllModals(): void {
    const modals = document.querySelectorAll<HTMLElement>('.modal-overlay');
    modals.forEach(modal => {
      closeDialogModal(modal);
    });
  }

  function updateModalScrollState(): void {
    const hasOpenModal = Array.from(document.querySelectorAll('.modal-overlay'))
      .some(modal => modal.classList.contains('show') || modal.classList.contains('active'));
    document.body.classList.toggle('modal-open', hasOpenModal);
  }

  // Initialize DOM
  function initializeDOM(): void {
    closeAllModals();
    updateModalScrollState();

    document.addEventListener('keydown', function(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        closeTopModal();
        return;
      }
      trapFocusInModal(e);
    });

    document.querySelectorAll<HTMLElement>('.modal-overlay').forEach(modal => {
      let mouseDownOnBackdrop = false;
      modal.addEventListener('mousedown', function(e: MouseEvent): void {
        mouseDownOnBackdrop = e.target === modal;
      });
      modal.addEventListener('click', function(e: MouseEvent): void {
        if (mouseDownOnBackdrop && e.target === modal) {
          closeTopModal();
        }
        mouseDownOnBackdrop = false;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDOM);
  } else {
    initializeDOM();
  }

  // Expose global functions for onclick handlers
  (window as Window).changeManagePage = (page: number): void => { manageCurrentPage = page; loadManageIncidents(); };
  (window as Window).openManageModal = async (id: number, sourceElement?: HTMLElement): Promise<void> => {
    const modal = document.getElementById('manage-modal');
    const modalBody = document.getElementById('manage-modal-body');
    const modalTitle = document.getElementById('manage-modal-title');
    if (!modal || !modalBody) return;

    showModalSafely(modal);
    modalBody.innerHTML = '<div class="modal-loading"><div class="spinner"></div><p>Carregando incidente...</p></div>';

    if (cachedResolutionTypes.length === 0) void loadResolutionTypes();
    if (cachedDevelopers.length === 0) await loadDevelopers();

    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Fetch incident and comments in parallel
      const [incidentRes, commentsRes] = await Promise.all([
        fetch(`/api/incidents/${id}`, { headers }),
        fetch(`/api/dev/incidents/${id}`, { headers }).catch(() => null)
      ]);

      if (!incidentRes.ok) throw new Error('Incidente não encontrado');

      const rawIncident = await incidentRes.json() as { success?: boolean; incident?: Incident } | Incident;
      const incident = unwrapIncident(rawIncident);
      if (!incident) throw new Error('Resposta vazia');
      const commentsData = commentsRes?.ok ? await commentsRes.json() : { comments: [] };
      const comments = (commentsData.comments || []) as IncidentComment[];

      if (modalTitle) modalTitle.textContent = `${incident.protocol} - ${incident.title}`;

      const critInfo = CRITICALITIES[incident.criticality] || { label: '?', tag: '?' };
      const portalInfo = PORTALS[incident.portal] || { label: incident.portal };
      const statusInfo = STATUSES[incident.status] || { label: incident.status };

      const reporterInfo = incident.reporter_email
        ? `${escapeHtml(incident.reporter_name)} (${escapeHtml(incident.reporter_email)})`
        : escapeHtml(incident.reporter_name);

      const currentUserName = currentUser?.name || '';
      const isAssignedToMe = incident.assigned_dev === currentUserName;
      const isUnassigned = !incident.assigned_dev;
      const isOwner = currentUser?.role === 'owner';

      // Build timeline entries from comments
      const timelineEntries = (comments || []).map(c => {
        const iconMap: Record<string, string> = {
          comment: 'message-circle',
          status_change: 'arrow-right-circle',
          assignment: 'user-check',
          resolution: 'check-circle-2'
        };
        const labelMap: Record<string, string> = {
          comment: 'comentou',
          status_change: 'alterou status',
          assignment: 'atribuiu',
          resolution: 'resolveu'
        };
        return `
          <div class="tl-entry tl-${c.comment_type}">
            <div class="tl-marker"><i data-lucide="${iconMap[c.comment_type] || 'circle'}"></i></div>
            <div class="tl-body">
              <div class="tl-meta">
                <strong>${escapeHtml(c.user_name || 'Sistema')}</strong>
                <span class="tl-action">${labelMap[c.comment_type] || c.comment_type}</span>
                <time class="tl-time">${formatDate(c.created_at)}</time>
              </div>
              ${c.comment ? `<p class="tl-text">${escapeHtml(c.comment)}</p>` : ''}
            </div>
          </div>
        `;
      });

      // Build attachments section
      const productName = (incident as Incident & { product_name?: string }).product_name;

      const attachmentsHtml = incident.image_path ? `
        <div class="manage-section">
          <h3 class="manage-section-title">Evidências do reportante</h3>
          ${renderAttachmentsGallery(incident.image_path)}
        </div>
      ` : '';

      const resolutionHtml = (incident.resolution_type || incident.resolution_notes) ? `
        <div class="manage-section">
          <h3 class="manage-section-title">Resolução</h3>
          ${incident.resolution_type ? `<div class="manage-row"><span class="manage-row-label">Tipo</span><span>${escapeHtml(incident.resolution_type.replace(/_/g, ' '))}</span></div>` : ''}
          ${incident.resolution_notes ? `<p class="manage-text">${escapeHtml(incident.resolution_notes)}</p>` : ''}
        </div>
      ` : '';

      const validationHtml = (incident.status === 'aguardando_validacao' && incident.validation_note) ? `
        <div class="manage-section manage-section-validation">
          <h3 class="manage-section-title">Aguardando validação do cliente</h3>
          <p class="manage-text">${escapeHtml(incident.validation_note)}</p>
          ${(() => {
            const files = parseImagePaths(incident.validation_files);
            return files.length > 0 ? `<div class="attachments-gallery" style="margin-top:10px">${files.map(p => renderAttachment(p)).join('')}</div>` : '';
          })()}
        </div>
      ` : '';

      const closeReasonHtml = incident.close_reason ? `<div class="manage-section"><h3 class="manage-section-title">Motivo do encerramento</h3><p class="manage-text">${escapeHtml(incident.close_reason)}</p></div>` : '';
      const archiveReasonHtml = incident.archive_reason ? `<div class="manage-section"><h3 class="manage-section-title">Motivo do arquivamento</h3><p class="manage-text">${escapeHtml(incident.archive_reason)}</p></div>` : '';
      const reopenReasonHtml = incident.reopen_reason ? `<div class="manage-section manage-section-validation"><h3 class="manage-section-title">Reaberto pelo cliente</h3><p class="manage-text">${escapeHtml(incident.reopen_reason)}</p></div>` : '';

      modalBody.innerHTML = `
        <div class="manage-stack">
          <input type="hidden" id="manage-incident-id" value="${incident.id}">

          <div class="manage-head">
            <div class="manage-head-main">
              <span class="manage-head-protocol">${escapeHtml(incident.protocol)}</span>
              <h2 class="manage-head-title">${escapeHtml(incident.title)}</h2>
            </div>
            <div class="manage-head-side">
              <div class="manage-head-badges">
                <span class="status-badge status-${incident.status}"><span class="status-dot"></span>${statusInfo.label}</span>
                <span class="incident-criticality incident-crit-${incident.criticality}">${critInfo.tag} ${critInfo.label}</span>
              </div>
              <div class="manage-assign">
                <button type="button" class="btn btn-primary manage-assign-toggle" aria-haspopup="true" aria-expanded="false">
                  <i data-lucide="${isAssignedToMe ? 'user-check' : 'user-plus'}" style="width:14px;height:14px"></i>
                  ${isAssignedToMe ? 'Atribuído a mim' : 'Atribuir'}
                  <i data-lucide="chevron-down" style="width:12px;height:12px"></i>
                </button>
                <div class="manage-assign-menu" hidden role="menu">
                  <button type="button" class="manage-assign-item" data-assign-self ${isAssignedToMe ? 'disabled' : ''}>
                    <i data-lucide="user" style="width:14px;height:14px"></i> Atribuir a mim
                  </button>
                  <div class="manage-assign-label">${isOwner ? 'Atribuir a outro' : 'Sugerir atribuição'}</div>
                  ${cachedDevelopers.filter(d => d.name !== currentUserName).map(d => `
                    <button type="button" class="manage-assign-item" data-assign-other="${escapeHtml(d.name)}" ${incident.assigned_dev === d.name ? 'disabled' : ''}>
                      ${escapeHtml(d.name)}${incident.assigned_dev === d.name ? ' (atual)' : ''}
                    </button>
                  `).join('') || `<div class="manage-assign-empty">Nenhum outro desenvolvedor</div>`}
                </div>
              </div>
            </div>
          </div>

          <dl class="manage-meta-grid">
            ${productName ? `<div><dt>Produto</dt><dd>${escapeHtml(productName)}</dd></div>` : ''}
            <div><dt>Reportado por</dt><dd>${reporterInfo}</dd></div>
            <div><dt>Responsável</dt><dd>${incident.assigned_dev ? escapeHtml(incident.assigned_dev) : '<em class="manage-muted">Não atribuído</em>'}</dd></div>
            <div><dt>Criado em</dt><dd>${formatDate(incident.created_at)}</dd></div>
            <div><dt>Entrega prevista</dt><dd>${incident.estimated_delivery ? formatDate(incident.estimated_delivery) : '<em class="manage-muted">Não definida</em>'}</dd></div>
            <div><dt>Tempo de execução</dt><dd class="execution-time ${incident.resolved_at || incident.closed_at ? 'completed' : 'in-progress'}">${formatExecutionTime(incident.created_at, incident.resolved_at || incident.closed_at)}${!incident.resolved_at && !incident.closed_at ? ' <em class="manage-muted">(em andamento)</em>' : ''}</dd></div>
          </dl>

          <div class="manage-section">
            <h3 class="manage-section-title">Descrição</h3>
            <p class="manage-text">${escapeHtml(incident.description || '')}</p>
          </div>

          ${attachmentsHtml}
          ${validationHtml}
          ${reopenReasonHtml}
          ${resolutionHtml}
          ${closeReasonHtml}
          ${archiveReasonHtml}

          <div class="manage-actions-bar">
            <button type="button" class="action-bar-btn action-bar-step" data-manage-action="step">
              <i data-lucide="plus-circle"></i> Adicionar Etapa
            </button>
            <button type="button" class="action-bar-btn action-bar-delivery" data-manage-action="delivery">
              <i data-lucide="calendar"></i> Data Prevista
            </button>
          </div>
          <div id="manage-panel-area"></div>

          <div class="manage-section">
            <h3 class="manage-section-title">Histórico</h3>
            <div class="tl-container">
              ${timelineEntries.length === 0
                ? '<p class="tl-empty">Nenhuma atividade registrada.</p>'
                : timelineEntries.slice().reverse().join('')}
            </div>
            <form id="quick-comment-form" class="quick-comment">
              <textarea id="quick-comment-input" class="quick-comment-input" rows="2" placeholder="Comentar rápido..."></textarea>
              <button type="submit" class="btn btn-primary btn-sm">Publicar</button>
            </form>
          </div>
        </div>
      `;

      // Wire action-bar buttons (debounce: desabilita o botão durante a ação)
      activeManagePanel = null;
      modalBody.querySelectorAll('[data-manage-action]').forEach(btn => {
        btn.addEventListener('click', async (): Promise<void> => {
          const target = btn as HTMLButtonElement;
          if (target.disabled) return;
          target.disabled = true;
          await handleManageAction(target.dataset.manageAction || '', incident).catch(() => undefined);
          // Pequeno delay extra evita click duplo logo após resposta muito rápida
          setTimeout(() => { target.disabled = false; }, 400);
        });
      });

      // Dropdown de atribuição
      const assignToggle = modalBody.querySelector<HTMLButtonElement>('.manage-assign-toggle');
      const assignMenu = modalBody.querySelector<HTMLElement>('.manage-assign-menu');
      if (assignToggle && assignMenu) {
        assignToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          const willOpen = assignMenu.hidden;
          assignMenu.hidden = !willOpen;
          assignToggle.setAttribute('aria-expanded', String(willOpen));
        });
        assignMenu.addEventListener('click', async (e) => {
          const item = (e.target as HTMLElement).closest<HTMLButtonElement>('.manage-assign-item');
          if (!item || item.disabled) return;
          assignMenu.hidden = true;
          assignToggle.setAttribute('aria-expanded', 'false');
          const me = currentUser?.name || '';
          if (item.dataset.assignSelf !== undefined) {
            await submitQuickAction(incident.id, { assigned_dev: me, comment: `${me} assumiu o incidente` });
          } else if (item.dataset.assignOther) {
            const targetDev = item.dataset.assignOther;
            if (isOwner) {
              await submitQuickAction(incident.id, { assigned_dev: targetDev, comment: `${me} atribuiu o incidente a ${targetDev}` });
            } else {
              await submitQuickAction(incident.id, { comment: `${me} sugeriu atribuir o incidente a ${targetDev}` });
            }
          }
        });
      }

      refreshIcons();
    } catch (error) {
      modalBody.innerHTML = `
        <div class="empty-state">
          <div class="empty-title">Erro ao carregar incidente</div>
          <div class="empty-desc">${(error as Error).message}</div>
        </div>
      `;
    }
  };

  async function handleManageAction(action: string, incident: Incident): Promise<void> {
    const panelArea = document.getElementById('manage-panel-area');
    if (!panelArea) return;

    // Toggle: close if same panel
    if (activeManagePanel === action) {
      panelArea.innerHTML = '';
      activeManagePanel = null;
      updateActionBarActive(null);
      return;
    }

    activeManagePanel = action;
    updateActionBarActive(action);

    const userName = currentUser?.name || '';

    if (action === 'assign') {
      panelArea.innerHTML = '';
      activeManagePanel = null;
      updateActionBarActive(null);
      await submitQuickAction(incident.id, { assigned_dev: userName, comment: `${userName} assumiu o incidente` });
      return;
    }

    const panels: Record<string, () => string> = {
      step: () => `
        <div class="inline-panel">
          <label>Comentário / Etapa <span style="color:var(--crit-critica)">*</span></label>
          <textarea id="panel-step-text" rows="3" placeholder="O que aconteceu — progresso, análise, decisão tomada..."></textarea>

          <label style="margin-top:12px">Mudar status (opcional)</label>
          <select id="panel-step-status">
            <option value="">— Manter (${STATUSES[incident.status]?.label || incident.status}) —</option>
            <option value="em_analise" ${incident.status === 'em_analise' ? 'disabled' : ''}>Em Análise</option>
            <option value="em_andamento" ${incident.status === 'em_andamento' ? 'disabled' : ''}>Em Andamento</option>
            <option value="aguardando_validacao" ${incident.status === 'aguardando_validacao' ? 'disabled' : ''}>Enviar para análise (cliente valida)</option>
          </select>

          <div id="panel-step-extra"></div>

          <div class="inline-panel-actions" style="margin-top:14px">
            <button type="button" class="btn btn-primary btn-sm" onclick="window.submitManageStep()">Publicar etapa</button>
            <button type="button" class="btn btn-ghost btn-sm" onclick="window.closeManagePanel()">Cancelar</button>
          </div>
        </div>
      `,
      delivery: () => `
        <div class="inline-panel">
          <label>Data Prevista de Entrega</label>
          <input type="date" id="panel-delivery-date" value="${incident.estimated_delivery || ''}">
          <div class="inline-panel-actions">
            <button type="button" class="btn btn-primary btn-sm" onclick="window.submitManageDelivery()">Definir Data</button>
            <button type="button" class="btn btn-ghost btn-sm" onclick="window.closeManagePanel()">Cancelar</button>
          </div>
        </div>
      `,
    };

    const renderer = panels[action];
    if (renderer) {
      panelArea.innerHTML = renderer();
      refreshIcons();

      // Lógica condicional do painel de etapa: campos extras conforme status escolhido
      if (action === 'step') {
        const statusSelect = document.getElementById('panel-step-status') as HTMLSelectElement | null;
        const extra = document.getElementById('panel-step-extra');
        const updateExtra = (): void => {
          if (!extra || !statusSelect) return;
          const v = statusSelect.value;
          if (v === 'aguardando_validacao') {
            const rtOptions = cachedResolutionTypes.length > 0
              ? cachedResolutionTypes.map(t => `<option value="${escapeHtml(t.code)}">${escapeHtml(t.label)}</option>`).join('')
              : '<option value="outro">Outro</option>';
            extra.innerHTML = `
              <div style="margin-top:12px;padding:12px;background:color-mix(in srgb, var(--crit-media) 8%, transparent);border:1px solid color-mix(in srgb, var(--crit-media) 30%, transparent);border-radius:var(--r-md)">
                <p style="margin:0 0 10px;font-size:0.8125rem;color:var(--text-muted)">O incidente fica marcado como resolvido parcialmente até o cliente confirmar.</p>
                <label style="display:block;font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-muted);margin-bottom:6px">Evidências <span style="color:var(--crit-critica)">*</span> (imagens ou vídeos)</label>
                <input type="file" id="panel-step-files" accept="image/*,video/mp4,video/webm,video/quicktime" multiple style="width:100%;padding:8px;border:1px dashed var(--border);border-radius:var(--r-sm);background:var(--bg-input);color:var(--text-primary);font-size:0.8125rem" />
                <div id="panel-step-files-list" style="margin-top:6px;font-size:0.75rem;color:var(--text-muted)"></div>
                <label style="display:block;font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-muted);margin:12px 0 6px">Tipo de Resolução (opcional)</label>
                <select id="panel-step-resolution-type" style="width:100%">
                  <option value="">Selecionar tipo...</option>
                  ${rtOptions}
                </select>
              </div>
            `;
            const fi = document.getElementById('panel-step-files') as HTMLInputElement | null;
            const lst = document.getElementById('panel-step-files-list');
            fi?.addEventListener('change', () => {
              if (!lst) return;
              const files = Array.from(fi.files || []);
              lst.innerHTML = files.length ? files.map(f => `• ${escapeHtml(f.name)} (${formatFileSize(f.size)})`).join('<br>') : '';
            });
          } else {
            extra.innerHTML = '';
          }
        };
        statusSelect?.addEventListener('change', updateExtra);
      }

      const firstInput = panelArea.querySelector('textarea, select, input') as HTMLElement;
      if (firstInput) firstInput.focus();
    }
  }

  function updateActionBarActive(action: string | null): void {
    document.querySelectorAll('.action-bar-btn[data-manage-action]').forEach(btn => {
      btn.classList.toggle('action-bar-btn--active', (btn as HTMLElement).dataset.manageAction === action);
    });
  }

  let manageActionInFlight = false;

  async function submitQuickAction(incidentId: number, updates: Record<string, unknown>): Promise<void> {
    if (manageActionInFlight) return;
    manageActionInFlight = true;
    // Desabilita todos os botões de submit dos painéis inline durante a request
    const inlineButtons = document.querySelectorAll<HTMLButtonElement>('#manage-panel-area button, #quick-comment-form button');
    inlineButtons.forEach(b => { b.disabled = true; });
    try {
      const token = getAuthToken();
      if (currentUser) updates.user_id = currentUser.id;

      const response = await fetch(`/api/dev/incidents/${incidentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(updates)
      });

      if (response.status === 401) { showToast('Sessão expirou. Recarregue a página.', 'error'); return; }
      if (response.status === 403) { showToast('Você não tem permissão para essa ação.', 'error'); return; }
      if (response.status === 429) { showToast('Muitas requisições. Aguarde alguns segundos.', 'error'); return; }

      if (!response.ok) {
        const err = await response.json().catch(() => ({} as { error?: string }));
        showToast(err.error || `Erro ao atualizar (HTTP ${response.status})`, 'error');
        return;
      }

      showToast('Incidente atualizado!', 'success');
      await (window as Window).openManageModal(incidentId);
    } catch (err) {
      const msg = err instanceof TypeError
        ? 'Sem conexão com o servidor.'
        : 'Erro ao atualizar incidente.';
      showToast(msg, 'error');
    } finally {
      manageActionInFlight = false;
      inlineButtons.forEach(b => { b.disabled = false; });
    }
  }

  (window as Window).closeManagePanel = (): void => {
    const panelArea = document.getElementById('manage-panel-area');
    if (panelArea) panelArea.innerHTML = '';
    activeManagePanel = null;
    updateActionBarActive(null);
  };

  (window as Window).submitManageStep = async (): Promise<void> => {
    const text = (document.getElementById('panel-step-text') as HTMLTextAreaElement)?.value?.trim();
    const status = (document.getElementById('panel-step-status') as HTMLSelectElement)?.value || '';
    const incidentId = parseInt((document.getElementById('manage-incident-id') as HTMLInputElement)?.value || '0');
    if (!text) { showToast('Descreva a etapa', 'warning'); return; }

    // Branch especial: aguardando_validacao usa endpoint próprio (multipart) + tipo de resolução opcional
    if (status === 'aguardando_validacao') {
      const fi = document.getElementById('panel-step-files') as HTMLInputElement | null;
      const files = Array.from(fi?.files || []);
      if (files.length === 0) { showToast('Anexe ao menos uma evidência', 'error'); return; }
      const rType = (document.getElementById('panel-step-resolution-type') as HTMLSelectElement | null)?.value || '';
      const formData = new FormData();
      formData.append('note', text);
      if (rType) formData.append('resolution_type', rType);
      files.forEach(f => formData.append('files', f));

      if (manageActionInFlight) return;
      manageActionInFlight = true;
      const inlineButtons = document.querySelectorAll<HTMLButtonElement>('#manage-panel-area button, #quick-comment-form button');
      inlineButtons.forEach(b => { b.disabled = true; });
      try {
        const token = getAuthToken();
        const res = await fetch(`/api/dev/incidents/${incidentId}/await-validation`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({})) as { error?: string };
          showToast(err.error || 'Erro ao enviar para análise', 'error');
          return;
        }
        showToast('Enviado para análise do cliente', 'success');
        await (window as Window).openManageModal(incidentId);
      } finally {
        manageActionInFlight = false;
        inlineButtons.forEach(b => { b.disabled = false; });
      }
      return;
    }

    // Branch genérico: comentário + opcional mudança de status (atribuição agora é pelo dropdown do header)
    const payload: Record<string, unknown> = { comment: text };
    if (status) payload.status = status;
    await submitQuickAction(incidentId, payload);
  };

  (window as Window).submitManageDelivery = async (): Promise<void> => {
    const date = (document.getElementById('panel-delivery-date') as HTMLInputElement)?.value;
    const incidentId = parseInt((document.getElementById('manage-incident-id') as HTMLInputElement)?.value || '0');
    if (!date) { showToast('Selecione uma data', 'warning'); return; }
    await submitQuickAction(incidentId, { estimated_delivery: date, comment: `Data prevista definida: ${date}` });
  };

  // Quick comment embaixo da timeline
  document.addEventListener('submit', (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#quick-comment-form');
    if (!form) return;
    e.preventDefault();
    const input = document.getElementById('quick-comment-input') as HTMLTextAreaElement | null;
    const text = input?.value.trim();
    const incidentId = parseInt((document.getElementById('manage-incident-id') as HTMLInputElement)?.value || '0');
    if (!text || !incidentId) return;
    void submitQuickAction(incidentId, { comment: text });
    if (input) input.value = '';
  });

  (window as Window).closeManageModal = (): void => {
    const modal = document.getElementById('manage-modal');
    if (modal) hideModalSafely(modal);
  };

  (window as Window).closeAllModals = closeAllModals;
  (window as Window).loadTrelloDashboard = loadTrelloDashboard;
  (window as Window).syncTrelloCards = syncTrelloCards;
  (window as Window).loadGovernanceData = loadGovernanceData;
  // Trello integration functions
  (window as Window).testTrelloConnection = testTrelloConnection;
  (window as Window).saveTrelloCredentials = saveTrelloCredentials;
  (window as Window).clearTrelloCredentials = clearTrelloCredentials;
  (window as Window).saveTrelloSettings = saveTrelloSettings;
  (window as Window).syncTrelloNow = syncTrelloNow;
  (window as Window).disconnectTrello = disconnectTrello;
  (window as Window).reconnectTrello = reconnectTrello;

})();
